import { runUpgradeComparison } from './logic/upgradeComparison';
import TrackerHeightGuide from './components/TrackerHeightGuide';
import { PROJECT_UNITS_PER_METRE } from './logic/projectMetrics';
import './components/project-workspace.css';
import ProjectPanel, { projectMode } from './components/ProjectPanel';
import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import SunCalc from 'suncalc';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { setupScene } from './logic/setupScene';
import { moonPositionFromAzEl, setMoonSunDirection } from './logic/Moon';
import { EnergyTracker } from './logic/tracker/EnergyTracker';
import { SunTracker } from './logic/SunTracker';
import { PanelInfo } from './logic/PanelInfo';
import { useTrackerApi, getTiltAngle } from './hooks/useTrackerApi';
import type { TrackerTelemetry } from './hooks/useTrackerApi';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend);

type NESW = 'N'|'E'|'S'|'W';
type DirKey = 'nord'|'ost'|'süd'|'west';
type TypeKey = 'fixed'|'single'|'limited'|'unlimited';
type NominatimResult = { display_name?: string; lat?: string | number; lon?: string | number };

/* ======================= DEBUG ======================= */
const DEBUG = true;
const log  = (...args: any[]) => { if (DEBUG) console.log('[SolarTrackerScene]', ...args); };
const warn = (...args: any[]) => { if (DEBUG) console.warn('[SolarTrackerScene]', ...args); };
/* ===================================================== */

export default function SolarTrackerScene() {
// irgendwo beim Start (einmalig)




// oben bei den States:
const distanceLabel=(value:number)=>projectMode?`${(value/PROJECT_UNITS_PER_METRE).toFixed(2)} m`:`${value} Einh.`;
const [projectToolsOpen,setProjectToolsOpen]=useState(()=>window.innerWidth>800);
const [selectedLog, setSelectedLog] = useState<any|null>(null);
const [copyMsg, setCopyMsg] = useState<string>('');

// kleine Helfer
const short = (s?: string, n = 120) => (s ? (s.length > n ? s.slice(0, n) + '…' : s) : '');
const parseMeta = (m: any) => {
  if (!m) return null;
  try { return typeof m === 'string' ? JSON.parse(m) : m; }
  catch { return { raw: m }; }
};
const copy = async (text: string) => {
  try { await navigator.clipboard.writeText(text); setCopyMsg('Copied'); setTimeout(()=>setCopyMsg(''), 1200); } catch {}
};
useEffect(() => {
  const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setSelectedLog(null); };
  window.addEventListener('keydown', onEsc);
  return () => window.removeEventListener('keydown', onEsc);
}, []);



  const trackerMapRef = useRef<Map<string, any>>(new Map());
  const panelInfoMap  = useRef(new Map<string, PanelInfo>()).current;
  const renderPausedRef = useRef(false);
  const wallMeshRef     = useRef<THREE.Mesh | null>(null);
const [adminLogs, setAdminLogs] = useState<any[]>([]);
const [adminOpen, setAdminOpen] = useState(false);
const [adminTab, setAdminTab] = useState<'logs' | 'users' | 'logins'>('logs');
const [logFilterUser, setLogFilterUser] = useState('');
const [logFilterIp, setLogFilterIp] = useState('');
const [logFilterPath, setLogFilterPath] = useState('');

const [loginAttempts, setLoginAttempts] = useState<any[]>([]);
const [loginFilterUser, setLoginFilterUser] = useState('');
const [loginFilterIp, setLoginFilterIp] = useState('');
const [loginFilterSuccess, setLoginFilterSuccess] = useState<''|'true'|'false'>('');

type AdminUserRow = {
  id: number; username: string; role: 'admin' | 'user'; status: 'active' | 'blocked';
  mustResetPassword: boolean; createdAt: string; updatedAt: string; lastLoginAt?: string | null;
};
const [adminUsers, setAdminUsers] = useState<AdminUserRow[]>([]);
const [adminUsersError, setAdminUsersError] = useState<string | null>(null);
const [newUserName, setNewUserName] = useState('');
const [newUserPass, setNewUserPass] = useState('');
const [newUserRole, setNewUserRole] = useState<'user' | 'admin'>('user');

async function refreshAdminUsers() {
  try {
    const res = await fetch(apiUrl('/api/admin/users'), { credentials: 'include' });
    if (!res.ok) { setAdminUsersError('Benutzerliste konnte nicht geladen werden.'); return; }
    const data = await res.json().catch(() => null);
    setAdminUsers(Array.isArray(data?.users) ? data.users : []);
    setAdminUsersError(null);
  } catch {
    setAdminUsersError('Server nicht erreichbar.');
  }
}

async function createAdminUser(e?: React.FormEvent) {
  e?.preventDefault();
  const username = newUserName.trim();
  if (!username || newUserPass.length < 8) {
    setAdminUsersError('Benutzername + Passwort (mind. 8 Zeichen) erforderlich.');
    return;
  }
  try {
    const res = await fetch(apiUrl('/api/admin/users'), {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password: newUserPass, role: newUserRole }),
    });
    const j = await res.json().catch(() => null);
    if (!res.ok) { setAdminUsersError(j?.error || 'Anlegen fehlgeschlagen.'); return; }
    setNewUserName(''); setNewUserPass(''); setNewUserRole('user');
    setAdminUsersError(null);
    await refreshAdminUsers();
  } catch {
    setAdminUsersError('Server nicht erreichbar.');
  }
}

async function patchAdminUser(id: number, patch: { role?: string; status?: string; password?: string }) {
  try {
    const res = await fetch(apiUrl(`/api/admin/users/${id}`), {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    const j = await res.json().catch(() => null);
    if (!res.ok) { setAdminUsersError(j?.error || 'Aktion fehlgeschlagen.'); return; }
    setAdminUsersError(null);
    await refreshAdminUsers();
  } catch {
    setAdminUsersError('Server nicht erreichbar.');
  }
}

function resetAdminUserPassword(id: number, username: string) {
  const pw = window.prompt(`Neues Passwort für "${username}" (mind. 8 Zeichen):`);
  if (!pw) return;
  if (pw.length < 8) { setAdminUsersError('Passwort muss mindestens 8 Zeichen haben.'); return; }
  patchAdminUser(id, { password: pw });
}


  const [serverData, setServerData] = useState<string | null>(null);  // Hier speichern wir die abgerufenen Daten
const [devLogs, setDevLogs] = useState<string[]>([]);
const [devLogOpen, setDevLogOpen] = useState(false);
function pushLog(msg: string) {
  const line = `${new Date().toISOString()}  ${msg}`;
  setDevLogs((prev) => [line, ...prev].slice(0, 1000));
  if (DEBUG) console.log('[DEVLOG]', line);
}
// Zentrale Fetch-Funktion für die Logs-Tabelle — nimmt optionale Filter-
// Overrides entgegen (nötig, weil State-Updates in React nicht synchron
// sind: ein setLogFilterUser() direkt vor dem Aufruf wäre im selben Tick
// noch nicht im State sichtbar).
async function fetchAdminLogsFiltered(overrides?: { username?: string; ip?: string; path?: string }) {
  const username = overrides?.username ?? logFilterUser;
  const ip       = overrides?.ip       ?? logFilterIp;
  const path     = overrides?.path     ?? logFilterPath;
  try {
    const params = new URLSearchParams();
    if (username.trim()) params.set('username', username.trim());
    if (ip.trim())       params.set('ip', ip.trim());
    if (path.trim())     params.set('path', path.trim());
    const qs = params.toString();

    // Same RBAC session cookie as everything else now — no more Basic-Auth realm.
    const res = await fetch(apiUrl(`/api/admin/visits${qs ? '?' + qs : ''}`), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });

    if (!res.ok) throw new Error(`HTTP ${res.status} – ${await res.text()}`);
    const data = await res.json();
    setAdminLogs(data.logs || []);
    setSelectedLog(null);
    pushLog(`Admin: ${data.logs?.length ?? 0} Einträge geladen${qs ? ` (gefiltert: ${qs})` : ''}`);
  } catch (e: any) {
    pushLog('Admin fetch failed: ' + (e?.message || e));
    alert('Fehler beim Holen der Logs – Details in der Konsole.');
  }
}

async function handleFetchAllLogs() {
  if (authRole !== 'admin') { alert('Nur Admin darf Logs sehen'); return; }

  // ⬇️ HIER IP & Meta erfassen, bevor wir den Admin-Fetch ausführen
  await captureIpAndLog('admin-logs');

  setAdminOpen(true);
  setAdminTab('logs');
  setAdminLogs([]);
  setLogFilterUser(''); setLogFilterIp(''); setLogFilterPath('');
  await fetchAdminLogsFiltered({ username: '', ip: '', path: '' });
}

// Sprung von der Benutzer- in die Logs-Ansicht, direkt gefiltert auf einen
// bestimmten Account — der zentrale "was macht dieser Nutzer"-Einstieg.
async function showVisitsForUser(username: string) {
  setLogFilterUser(username);
  setLogFilterIp('');
  setLogFilterPath('');
  setAdminTab('logs');
  await fetchAdminLogsFiltered({ username, ip: '', path: '' });
}

// Login-Versuche (erfolgreich + fehlgeschlagen) — separat von den Visits,
// da hier auch Versuche mit unbekanntem/falschem Benutzernamen auftauchen.
async function fetchLoginAttemptsFiltered(overrides?: { username?: string; ip?: string; success?: ''|'true'|'false' }) {
  const username = overrides?.username ?? loginFilterUser;
  const ip       = overrides?.ip       ?? loginFilterIp;
  const success  = overrides?.success  ?? loginFilterSuccess;
  try {
    const params = new URLSearchParams();
    if (username.trim()) params.set('username', username.trim());
    if (ip.trim())       params.set('ip', ip.trim());
    if (success)          params.set('success', success);
    const qs = params.toString();

    const res = await fetch(apiUrl(`/api/admin/login-attempts${qs ? '?' + qs : ''}`), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status} – ${await res.text()}`);
    const data = await res.json();
    setLoginAttempts(data.attempts || []);
    pushLog(`Admin: ${data.attempts?.length ?? 0} Login-Versuche geladen${qs ? ` (gefiltert: ${qs})` : ''}`);
  } catch (e: any) {
    pushLog('Login-attempts fetch failed: ' + (e?.message || e));
    alert('Fehler beim Holen der Login-Versuche – Details in der Konsole.');
  }
}

async function showLoginAttemptsForUser(username: string) {
  setLoginFilterUser(username);
  setLoginFilterIp('');
  setLoginFilterSuccess('');
  setAdminTab('logins');
  await fetchLoginAttemptsFiltered({ username, ip: '', success: '' });
}



async function handleFetchServerData() {
  try {
    const r = await fetch(apiUrl('/api/serverdata'));
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    setServerData(JSON.stringify(d, null, 2));
    pushLog('Serverdaten erfolgreich abgerufen');
  } catch (e: any) {
    console.error(e);
    pushLog(`Fehler beim Abrufen der Serverdaten: ${e?.message || e}`);
  }
}


  // Demo users — in production: vom Server laden & niemals Passwörter im Frontend speichern!



const ENV = process.env as Record<string, string | undefined>;

const API_BASE: string =
  ENV.VITE_API_BASE ||
  ENV.REACT_APP_API_BASE ||
  (window.location.port === '3000' ? 'http://localhost:8080' : '');  // <-- 8080

function apiUrl(p: string) {
  try { return new URL(p, API_BASE || window.location.origin).toString(); }
  catch { return p; }
}

/** Kamera-Pfade für das Dorf-Intro, berechnet aus den ECHTEN Objektpositionen des
 * gespeicherten Modells (kein Hardcoding — verschiebt der Admin Tor oder Brunnen,
 * passen sich die Flüge automatisch an):
 *  - intro: Sim-Start-Flug von außen durchs Efeu-Torhaus, die Hauptstraße entlang,
 *    endet am Marktplatz mit Blick über den Brunnen auf die Marktstand-Zeile.
 *  - ambient: Preview-Rundflug (vor dem Login) knapp über den Dächern entlang der
 *    Straßenachsen (aus den Pflasterplatten-Positionen), statt hohem Rand-Orbit.
 * Gibt null zurück, wenn die Landmarken fehlen — dann bleiben die Defaults aktiv. */
/* Eine Tabellenzeile im EIGENEN Scroll-Container sichtbar machen — und nur dort.
 * el.scrollIntoView() scrollt jeden scrollbaren Vorfahren bis hinauf zum
 * Dokument. Der HUD-Block mit dieser Tabelle sitzt bei top:78% und ragt mit
 * maxHeight:80vh unten aus dem Viewport hinaus (gemessen: 165 px), das Dokument
 * ist dadurch hoeher als das Fenster. Beim Markieren eines Trackers schob der
 * Browser deshalb die ganze Seite nach oben und unter der 3D-Szene klaffte der
 * body-Hintergrund — gemessene 164 px. Im Vollbild faellt das nicht auf, weil
 * dort das Fullscreen-Element die Scroll-Wurzel ist und das Dokument dahinter
 * unberuehrt bleibt; genau deshalb trat der Fehler NUR ausserhalb des Vollbilds
 * auf. Diese Variante hat dieselbe block:'nearest'-Semantik, fasst die Seite
 * aber nicht an. */
function scrollRowIntoContainerView(el: HTMLElement) {
  let sc: HTMLElement | null = el.parentElement;
  while (sc && sc.scrollHeight <= sc.clientHeight) sc = sc.parentElement;
  if (!sc) return;
  const er = el.getBoundingClientRect();
  const cr = sc.getBoundingClientRect();
  if (er.top < cr.top) sc.scrollTop -= cr.top - er.top;
  else if (er.bottom > cr.bottom) sc.scrollTop += er.bottom - cr.bottom;
}

function computeVillageCameraPaths(sceneData: any[]) {
  type P = { x: number; y: number; z: number };
  const houses = sceneData.filter((o: any) => o?.kind === 'house' && o?.position && o?.cfg?.modelFile);
  const find = (re: RegExp) => houses.filter((o: any) => re.test(o.cfg.modelFile));
  const gate = find(/Ivy_Gatehouse/)[0];
  const fountains = find(/Heritage_Stone_Founta/);
  const stalls = find(/Spice_Market|Harbor_Market|Olive_Oil_Market/);
  if (!gate || fountains.length === 0 || stalls.length === 0) return null;

  const centroid = (arr: any[]): P => ({
    x: arr.reduce((s, o) => s + o.position.x, 0) / arr.length,
    y: 0,
    z: arr.reduce((s, o) => s + o.position.z, 0) / arr.length,
  });
  const sc = centroid(stalls);
  const d2 = (a: P, b: P) => (a.x - b.x) ** 2 + (a.z - b.z) ** 2;
  // Der Marktplatz-Brunnen = der den Ständen nächste (es gibt einen zweiten am Ortsrand)
  const fountain = fountains.reduce((a: any, b: any) => (d2(a.position, sc) < d2(b.position, sc) ? a : b));

  const g = gate.position as P;
  const f = fountain.position as P;
  const len = Math.hypot(f.x - g.x, f.z - g.z) || 1;
  const dir = { x: (f.x - g.x) / len, z: (f.z - g.z) / len };
  const along = (t: number, y: number): [number, number, number] =>
    [g.x + dir.x * len * t, y, g.z + dir.z * len * t];

  // Endkamera: schräg hinter dem Brunnen (von den Ständen aus gesehen), Blick
  // über den Brunnen auf die Marktstand-Zeile. Gegen Ende steigt der Flug an,
  // damit das Schlussbild eine leicht erhöhte Marktplatz-Ansicht ist.
  const introEnd: [number, number, number] = [f.x + (f.x - sc.x) * 0.55, 86, f.z + (f.z - sc.z) * 0.55];
  const introPts: Array<[number, number, number]> = [
    [g.x - dir.x * 280, 34, g.z - dir.z * 280], // Anflug von außerhalb
    [g.x - dir.x * 80, 18, g.z - dir.z * 80],
    [g.x, 15, g.z],                              // durch den Torbogen (tief — der coole Teil)
    along(0.4, 14),
    along(0.75, 18),                             // ab hier sanft steigen
    introEnd,
  ];
  const introEndLook: [number, number, number] = [(f.x + sc.x) / 2, 3, (f.z + sc.z) / 2];

  // Preview-Rundflug: über die Extrempunkte des Pflaster-Straßennetzes + Marktplatz
  const pavers = find(/Cobblestone_Paver/).map((o: any) => o.position as P);
  let ambientPts: Array<[number, number, number]> | null = null;
  if (pavers.length >= 4) {
    const c = centroid(pavers.map((p) => ({ position: p })));
    const AH = 85; // knapp über den Dächern — Straßen und Häuser füllen das Bild
    const pick = (sel: (a: P, b: P) => P) => pavers.reduce((a, b) => sel(a, b));
    const pull = (p: P, fac = 0.8): [number, number, number] =>
      [c.x + (p.x - c.x) * fac, AH, c.z + (p.z - c.z) * fac];
    ambientPts = [
      pull(pick((a, b) => (a.z > b.z ? a : b))),        // Nordarm (Richtung Tor)
      pull(pick((a, b) => (a.x > b.x ? a : b)), 0.7),   // Ostarm
      pull(pick((a, b) => (a.z < b.z ? a : b)), 0.75),  // Südarm
      [f.x + 70, AH, f.z + 70],                          // über den Marktplatz
      pull(pick((a, b) => (a.x < b.x ? a : b)), 0.7),   // Westarm
    ];
  }
  return { introPts, introEndLook, ambientPts };
}

// Self-Build Größe-Slider: ein linearer <input type="range"> über einen großen
// Multiplikator-Bereich (z.B. 0.1x–4x) fühlt sich "empfindlich" an, weil die meisten
// sinnvollen Feinkorrekturen (0.8x–1.2x) nur einen winzigen Bruchteil der Slider-Breite
// belegen. Log-Mapping gibt jeder Verdopplung/Halbierung denselben Slider-Weg, egal wo
// man gerade steht — fühlt sich über den ganzen Bereich gleich fein an.
const scaleToSlider = (v: number) => Math.log2(Math.max(0.001, v));
const sliderToScale = (v: number) => Math.pow(2, v);










type AuthRole = 'admin' | 'user' | null;
const [authOpen, setAuthOpen] = useState(false);
const [authed,   setAuthed]   = useState(false);
const [authUser, setAuthUser] = useState('');
const [authPass, setAuthPass] = useState('');
const [authErr,  setAuthErr]  = useState('');
const [authRole, setAuthRole] = useState<AuthRole>(null);

// Login wird serverseitig geprüft (POST /api/login); beim Laden Session-Status abfragen.
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  (async () => {
    try {
      const r = await fetch(apiUrl('/api/session'), { credentials: 'include' });
      const j = await r.json();
      if (j?.ok) {
        setAuthed(true);
        setAuthRole(j.role as AuthRole);
      }
    } catch {}
  })();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);








const [clientIps, setClientIps] = useState<{v4?: string; v6?: string}>({});
const visitLoggedRef = useRef(false);
// One-shot visit logging; guarded with a ref for React StrictMode dev effects.
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  if (visitLoggedRef.current) return;
  visitLoggedRef.current = true;
  let alive = true;
  (async () => {
    try {
      const [info, ips] = await Promise.all([
        collectClientInfo(),
        getPublicIPs()
      ]);
      if (!alive) return;

      setClientIps(ips);
      pushLog('Client info:\n' + JSON.stringify({ ...info, publicIP_v4: ips.v4, publicIP_v6: ips.v6 }, null, 2));

      // Einmalig an Backend loggen
      await fetch(apiUrl('/api/visit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          path: window.location.pathname,
          // Top-Level Felder (falls dein Backend sie bevorzugt)
          clientIpV4: ips.v4 || null,
          clientIpV6: ips.v6 || null,
          // Zusätzlich in meta (praktisch für Anzeige):
          meta: { ...info, clientIp: ips.v4 ?? ips.v6 ?? null }
        })
      }).catch(() => {});
    } catch (e: any) {
      pushLog('Visit log failed: ' + (e?.message || e));
    }
  })();
  return () => { alive = false; };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

async function captureClientMetaDevStyle(
  sendToServer = false,
  serverPath = 'manual',
  extraMeta: Record<string, any> = {}
) {
  const ua   = navigator.userAgent;
  const lang = (navigator as any).language || (navigator as any).userLanguage || '';
  const plat = (navigator as any).platform || '';

  let ip = 'unknown';
  try {
    const r = await fetch(apiUrl('/api/myip'));
    const j = await r.json();
    ip = j?.v4 || j?.v6 || 'unknown';
  } catch {
    ip = 'fetch-ip-failed';
  }

  // exakt die gleiche Logzeile wie in deinem dev-logs useEffect:
  pushLog(`Client connected · IP=${ip} · UA="${ua}" · Lang=${lang} · Platform=${plat}`);

  if (sendToServer) {
    try {
      await fetch(apiUrl('/api/visit'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          path: serverPath,          // z.B. 'login'
          clientIp: ip,              // eine IP wie im dev-log
          meta: { ua, lang, plat, ...extraMeta },
        }),
      });
    } catch {}
  }

  return { ip, ua, lang, plat };
}

async function handleSubmitAuth(e?: React.FormEvent) {
  if (e) e.preventDefault();
  const user = authUser.trim();
  const pw   = authPass ?? '';

  let role: AuthRole = null;
  try {
    const res = await fetch(apiUrl('/api/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ username: user, password: pw }),
    });
    const j = await res.json();
    if (!res.ok || !j?.ok) {
      playBeep(220, 160, 0.05, 'square');
      setAuthErr(j?.error || 'Benutzername oder Passwort falsch');
      return;
    }
    role = j.role as AuthRole;
  } catch {
    playBeep(220, 160, 0.05, 'square');
    setAuthErr('Login fehlgeschlagen (Server nicht erreichbar)');
    return;
  }

  playBeep(1046, 160, 0.06);
  setAuthed(true);
  setAuthRole(role);
  setAuthOpen(false);
  setAuthErr('');

  // Erfolgreicher Login geht direkt weiter — kein zweites Enter mehr nötig.
  if (!fsDoneRef.current && !document.fullscreenElement) {
    fsDoneRef.current = true;
    setFsDone(true);
    await tryFullscreenNow();
  }
  reallyStartSim();

  await captureClientMetaDevStyle(true, 'login', { user });
  try {
    await fetch(apiUrl('/api/visit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        path: 'login',
        clientIpV4: clientIps.v4 || null,
        clientIpV6: clientIps.v6 || null,
        meta: { user }
      }),
    });
  } catch {}
}
  const mountRef = useRef<HTMLDivElement | null>(null);
  const sunLightRef = useRef<THREE.DirectionalLight | null>(null);
  const sunSphereRef = useRef<THREE.Mesh | null>(null);
  const moonSphereRef = useRef<THREE.Mesh | null>(null);
  const controlsRef = useRef<any>(null);
  const projectCalculation = useRef<any>(null);
  const sunTrackerRef = useRef<SunTracker | null>(null);
  const energyTracker = useRef<EnergyTracker | null>(null);

  const [date, setDate] = useState(new Date('2024-11-13T00:00:00'));
  const [intervalMs] = useState(10 * 60 * 1000);
  const [isRunning, setIsRunning] = useState(!projectMode);

  const [isFastSim, setIsFastSim] = useState(false);



const [rangeSimBusy, setRangeSimBusy] = useState(false);
const [showRangeSimDone, setShowRangeSimDone] = useState(false);
const [rangeSimResult, setRangeSimResult] = useState<{ totalKWh: number; days: number } | null>(null);

  const [, setAzimuth] = useState(0);
  const [, setAltitude] = useState(0);
  const [lastSimDay, setLastSimDay] = useState(date.getDate());

  const [location, setLocation] = useState('Wien');
  const DEFAULT_LAT = 48.2;
  const DEFAULT_LON = 16.25;
  const [lat, setLat] = useState(DEFAULT_LAT);
  const [lon, setLon] = useState(DEFAULT_LON);
  const projectTimezoneRef = useRef<string|null>(null);
  const projectLocationReady=useRef(!projectMode);
  const comparisonAbortRef = useRef(false);
  const comparisonRunningRef = useRef(false);
  const [comparisonPhase,setComparisonPhase] = useState('');
  const [freeTimeControls,setFreeTimeControls]=useState(false);
  const [locationTimezone, setLocationTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const suggestionAbortRef = useRef<AbortController | null>(null);
  const suggestionRequestIdRef = useRef(0);
  const skipNextSuggestionFetchRef = useRef(false);
  const [showMessage, setShowMessage] = useState(false);

  const [endDate, setEndDate] = useState(new Date('2024-11-14T23:59:59.999'));

  const [forwardActive, setForwardActive] = useState(false);
  const forwardTimerRef = useRef<number | null>(null);
  const forwardHeldTickedRef = useRef(false);

  const START_ISO = '2024-11-13T00:00:00';
  const initialDateRef = useRef<Date>(new Date(START_ISO));

  const isValidDate = (value: Date | null | undefined): value is Date =>
    value instanceof Date && Number.isFinite(value.getTime());

  // Returns (UTC ms) - (local ms in tz) — i.e. how far ahead local is from UTC
  const getTimezoneOffsetMs = (tz: string, date: Date): number => {
    const p = getZonedParts(date, tz);
    const zonedAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second, p.millisecond);
    return date.getTime() - zonedAsUtc;
  };

  type ZonedDateParts = {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
  };

  const pad2 = (n: number) => String(n).padStart(2, '0');

  const getZonedParts = (value: Date, tz: string): ZonedDateParts => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      hourCycle: 'h23',
    }).formatToParts(value);
    const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const hour = Number(map.hour);
    return {
      year: Number(map.year),
      month: Number(map.month),
      day: Number(map.day),
      hour: hour === 24 ? 0 : hour,
      minute: Number(map.minute),
      second: Number(map.second),
      millisecond: value.getUTCMilliseconds(),
    };
  };

  const makeDateTimeInTz = (parts: ZonedDateParts, tz: string): Date => {
    const wallClockUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      parts.millisecond
    );
    let ts = wallClockUtc;
    for (let i = 0; i < 3; i++) {
      ts = wallClockUtc + getTimezoneOffsetMs(tz, new Date(ts));
    }
    return new Date(ts);
  };

  // Returns a Date representing 00:00:00 on (year/month/day) in the given IANA timezone
  const makeMidnightInTz = (year: number, month: number, day: number, tz: string): Date => {
    return makeDateTimeInTz({ year, month, day, hour: 0, minute: 0, second: 0, millisecond: 0 }, tz);
  };

  const nextMidnightInTz = (value: Date, tz: string): Date => {
    const p = getZonedParts(value, tz);
    return makeMidnightInTz(p.year, p.month, p.day + 1, tz);
  };

  const formatDateInputInTz = (value: Date | null | undefined) => {
    if (!value) return '';
    const p = getZonedParts(value, locationTimezone);
    return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
  };

  const formatDateTimeInTz = (value: Date) => {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: locationTimezone,
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(value);
  };

  const guessTimezoneFromLongitude = (rawLon: number) => {
    const offsetHours = THREE.MathUtils.clamp(Math.round(rawLon / 15), -12, 14);
    if (offsetHours === 0) return 'UTC';
    // IANA Etc/GMT signs are inverted: Etc/GMT+5 means UTC-05:00.
    return `Etc/GMT${offsetHours > 0 ? '-' : '+'}${Math.abs(offsetHours)}`;
  };

  const parseBrowserLocalDateInput = (input: string, fallback: Date) => {
    if (!input) return new Date(fallback);
    const [year, month, day] = input.split('-').map(Number);
    if (!year || !month || !day) return new Date(fallback);
    const parsed = makeMidnightInTz(year, month, day, locationTimezone);
    if (!isValidDate(parsed)) return new Date(fallback);
    return parsed;
  };

  const readSafeCoords = () => {
    const safeLat = Number.isFinite(lat) ? THREE.MathUtils.clamp(lat, -90, 90) : DEFAULT_LAT;
    const safeLon = Number.isFinite(lon) ? THREE.MathUtils.clamp(lon, -180, 180) : DEFAULT_LON;
    return { lat: safeLat, lon: safeLon };
  };

  const applyCoordinates = (rawLat: unknown, rawLon: unknown) => {
    const nextLat = typeof rawLat === 'number' ? rawLat : parseFloat(String(rawLat));
    const nextLon = typeof rawLon === 'number' ? rawLon : parseFloat(String(rawLon));
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLon)) {
      warn('Invalid coordinates ignored:', rawLat, rawLon);
      return false;
    }
    projectTimezoneRef.current=null;projectLocationReady.current=true;
    setLat(THREE.MathUtils.clamp(nextLat, -90, 90));
    const safeLon = THREE.MathUtils.clamp(nextLon, -180, 180);
    setLon(safeLon);
    setLocationTimezone(guessTimezoneFromLongitude(safeLon));
    return true;
  };

  const dateRef = useRef(date);
  const endDateRef = useRef(endDate);
  const previousLocationTimezoneRef = useRef(locationTimezone);
  useEffect(() => { dateRef.current = date; }, [date]);
  useEffect(() => { endDateRef.current = endDate; }, [endDate]);
  useEffect(() => {
    const previousTz = previousLocationTimezoneRef.current;
    if (previousTz === locationTimezone) return;

    const currentWallTime = getZonedParts(dateRef.current, previousTz);
    const nextDate = makeDateTimeInTz(currentWallTime, locationTimezone);
    previousLocationTimezoneRef.current = locationTimezone;
    dateRef.current = nextDate;
    sunTrackerRef.current?.resetLastUpdateTime(nextDate);
    setDate(nextDate);
    setLastSimDay(getZonedParts(nextDate, locationTimezone).day);

    const currentEndDate = endDateRef.current;
    if (currentEndDate) {
      const endWallTime = getZonedParts(currentEndDate, previousTz);
      const nextEndDate = makeDateTimeInTz(endWallTime, locationTimezone);
      endDateRef.current = nextEndDate;
      setEndDate(nextEndDate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationTimezone]);

  /* ==================== DIGITAL TWIN (URL-Params) ==================== */
  const {
    state: trackerApiState,
    liveMode,
    setLiveMode,
    telemetryMap,
    energyHistory,
    sendCommand,
    sendBulkCommand,
  } = useTrackerApi((deviceId, t) => {
    // Only mirror angles in live mode
    if (liveMode !== 'live') return;
    trackerMapRef.current.forEach((panel: any) => {
      const angle = t.actual_tilt_x ?? t.actual_angle;
      panel.liveAngleDeg = angle != null ? angle : undefined;
    });
  });

  // Once tracker data is loaded: apply lat/lon of primary tracker + switch to Einzelhaus
  const trackerAppliedRef = useRef(false);
  useEffect(() => {
    if (trackerApiState.status !== 'ready' || trackerAppliedRef.current) return;
    trackerAppliedRef.current = true;
    const { primaryTracker } = trackerApiState;
    if (primaryTracker.latitude != null && primaryTracker.longitude != null) {
      applyCoordinates(primaryTracker.latitude, primaryTracker.longitude);
    }
    if (primaryTracker.location_name) setLocation(primaryTracker.location_name);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackerApiState.status]);

  const isDigitalTwin = trackerApiState.status === 'ready';

  /* Twin-Deep-Link an der URL erkennen. isDigitalTwin taugt für vieles nicht als
   * Weiche: es hängt an trackerApiState, das erst nach dem ersten API-Roundtrip
   * 'ready' wird. Die URL weiß es ab dem allerersten Render. */
  const isTwinDeepLink = (() => {
    const p = new URLSearchParams(window.location.search);
    return projectMode || (!!p.get('token') && !!p.get('api_base'));
  })();

  /* Solange ein Twin-Deep-Link auf seine erste API-Antwort wartet, darf keine
   * Anmeldemaske aufpoppen. Vorher genügte es, ENTER zu drücken, bevor
   * /api/trackers geantwortet hatte — oder einfach vier Sekunden zu warten, dann
   * kam sie von allein (siehe den Auto-Login-Effect weiter unten). Twin-Nutzer
   * haben für dieses Formular gar kein Konto; sie standen vor einer Hürde, die
   * für sie nicht gedacht ist. Erst wenn der Token wirklich abgelehnt wurde
   * (Status 'error'), ist die Maske die richtige Antwort. */
  const twinAuthPending = projectMode || (isTwinDeepLink && trackerApiState.status !== 'error');

  // When switching to simulation mode: clear live angles so sun-tracking resumes
  useEffect(() => {
    if (liveMode === 'simulation') {
      trackerMapRef.current.forEach((panel: any) => {
        panel.liveAngleDeg = undefined;
      });
    }
  }, [liveMode]);

  // Primary telemetry (first tracker in list)
  const primaryDeviceId = trackerApiState.status === 'ready'
    ? trackerApiState.primaryTracker.device_id : null;
  const primaryTelemetry = primaryDeviceId ? (telemetryMap[primaryDeviceId] ?? null) : null;
  const primaryEnergy    = primaryDeviceId ? (energyHistory[primaryDeviceId] ?? null) : null;

  // Aggregate live power across ALL user trackers
  const totalLivePowerW = Object.values(telemetryMap).reduce(
    (sum, t) => sum + (t.power_w ?? t.output_w ?? 0), 0
  );
  const totalDailyKwh = Object.values(energyHistory).reduce(
    (sum, h) => sum + (h.total_kwh ?? 0), 0
  );

  const [digitalTwinPanelPos, setDigitalTwinPanelPos] = useState<{ x: number; y: number } | null>(null);
  const digitalTwinDragRef = useRef<{
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
  } | null>(null);

  const startDigitalTwinPanelDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const panel = e.currentTarget.parentElement;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    digitalTwinDragRef.current = {
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    setDigitalTwinPanelPos({ x: rect.left, y: rect.top });
    e.currentTarget.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  useEffect(() => {
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), Math.max(min, max));
    const onPointerMove = (e: PointerEvent) => {
      const drag = digitalTwinDragRef.current;
      if (!drag) return;
      setDigitalTwinPanelPos({
        x: clamp(e.clientX - drag.offsetX, 8, window.innerWidth - drag.width - 8),
        y: clamp(e.clientY - drag.offsetY, 8, window.innerHeight - drag.height - 8),
      });
    };
    const onPointerUp = () => {
      digitalTwinDragRef.current = null;
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
  }, []);
  /* =================================================================== */

  useEffect(() => {
    const query = location.trim();
    suggestionAbortRef.current?.abort();

    if (skipNextSuggestionFetchRef.current) {
      skipNextSuggestionFetchRef.current = false;
      setSuggestions([]);
      return;
    }

    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    const requestId = ++suggestionRequestIdRef.current;
    const controller = new AbortController();
    suggestionAbortRef.current = controller;
    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch(`${apiUrl('/api/nominatim')}?q=${encodeURIComponent(query)}`, {
          signal: controller.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as NominatimResult[];
        if (controller.signal.aborted || requestId !== suggestionRequestIdRef.current) return;
        setSuggestions(data.map((item) => item.display_name).filter((name): name is string => !!name));
      } catch (err: any) {
        if (err?.name !== 'AbortError' && requestId === suggestionRequestIdRef.current) setSuggestions([]);
      }
    }, 400);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [location]);

  // Keep an explicit project timezone; do not overwrite it with a late lookup.
  useEffect(() => {
    if(projectMode && projectTimezoneRef.current)return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          apiUrl(`/api/timezone?lat=${lat}&lon=${lon}`)
        );
        if (!res.ok) return;
        const data = await res.json();
        const tz: unknown = data?.timeZone;
        if (!cancelled && typeof tz === 'string' && tz.length > 0) {
          setLocationTimezone(tz);
        }
      } catch { /* network error — keep current timezone */ }
    })();
    return () => { cancelled = true; };
  }, [lat, lon]);

  const [showIntroInfo, setShowIntroInfo] = useState(false);

  const introInfoTimerRef = useRef<number | null>(null);  ///TODO uncomment

type SkyMode = 'day' | 'night' | 'aurora' | 'galaxy' | null;
const [skyMode, setSkyMode] = useState<SkyMode>('day');

const lastManualPresetRef = useRef<SkyMode>('day');

const [rangeProgress, setRangeProgress] = useState(0);

  /** Szenen-Orientierung */
  const WORLD = { NORTH_OFFSET_DEG: 180, MIRROR_EW: true };

  const [resetActive, setResetActive] = useState(false);

  const [panelEnergyData, setPanelEnergyData] = useState<Map<string, number>>(new Map());


const fsDoneRef = useRef(false);          // merkt, ob die 1. Enter-Phase durch ist
const [fsDone, setFsDone] = useState(false); // nur für UI-Hinweis (optional)
const autoAuthShownRef = useRef(false);   // Login-Modal soll nur einmal automatisch aufpoppen
const [assetProgress, setAssetProgress] = useState({ loaded: 0, total: 5 }); // echter Ladefortschritt Nachbarschaft (Häuser/See/Yacht)
/* Fortschritt des Kundenhaus-Downloads (?site=). Diese GLBs sind zweistellig
 * MB groß (gemessen: 55,7 MB) und brauchen über den Prod-Pfad rund 60-85 s.
 * Ohne Anzeige steht der Nutzer die ganze Zeit vor einer leeren Baufläche —
 * enterBuilderMode() räumt die Szene nämlich sofort frei, während das Modell
 * noch unterwegs ist — und hält die Anwendung für kaputt. */
const [siteModelLoad, setSiteModelLoad] = useState<
  | { state: 'loading'; received: number; total: number }
  | { state: 'error'; message: string }
  | null
>(null);

// Dezenter Terminal-Beep, mutebar; AudioContext darf erst nach einer echten User-Geste entstehen.
const [soundOn, setSoundOn] = useState(() => {
  try { return localStorage.getItem('solarTracker.soundOn') !== '0'; } catch { return true; }
});
const soundOnRef = useRef(soundOn);
useEffect(() => { soundOnRef.current = soundOn; try { localStorage.setItem('solarTracker.soundOn', soundOn ? '1' : '0'); } catch {} }, [soundOn]);
const audioCtxRef = useRef<AudioContext | null>(null);
const playBeep = (freq = 880, durMs = 90, vol = 0.05, type: OscillatorType = 'sine') => {
  if (!soundOnRef.current) return;
  try {
    if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(vol, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + durMs / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durMs / 1000);
  } catch {}
};



// oben bei den States:
const [showSplash, setShowSplash] = useState(!projectMode);
const [splashFade, setSplashFade] = useState(false);
// markiert, ob das Kamera-Intro läuft
const [introPlaying, setIntroPlaying] = useState(false);




// --- Public-IP Helper (IPv4/IPv6 mit Timeouts & Fallback) ---
async function fetchWithTimeout(url: string, ms = 2500): Promise<Response> {
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), ms);
  try {
    const response = await fetch(url, { signal: ac.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response;
  } finally { clearTimeout(t); }
}

async function fetchJsonWithTimeout<T = any>(url: string, ms = 2500): Promise<T> {
  return fetchWithTimeout(url, ms).then(r => r.json());
}

async function fetchTextWithTimeout(url: string, ms = 2500): Promise<string> {
  return fetchWithTimeout(url, ms).then(r => r.text());
}

async function getPublicIPs(): Promise<{ v4?: string; v6?: string }> {
  try {
    const j = await fetchJsonWithTimeout<{ v4?: string; v6?: string }>(apiUrl('/api/myip'));
    return { v4: j?.v4 || undefined, v6: j?.v6 || undefined };
  } catch {}
  return {};
}

// --- IP-Erfassung bei Button-Aktionen ---
async function captureIpAndLog(tag: 'dev-logs' | 'admin-logs') {
  try {
    const ips = await getPublicIPs();              // { v4?: string, v6?: string }
    const ua   = navigator.userAgent;
    const lang = (navigator as any).language || (navigator as any).userLanguage || '';
    const plat = (navigator as any).platform || '';

    // hübsche Darstellung
    const ipText = ips.v4 && ips.v6
      ? `${ips.v4} (v6 ${ips.v6})`
      : (ips.v4 ?? ips.v6 ?? 'unknown');

    setClientIps(ips);

    // ⛳ in die Dev-Logs schreiben (sichtbar im Modal)
    pushLog(`[${tag}] Client connected · IP=${ipText} · UA="${ua}" · Lang=${lang} · Platform=${plat}`);

    // 🔌 optional auch ans Backend loggen
    fetch(apiUrl('/api/visit'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        path: tag,                 // "dev-logs" | "admin-logs"
        clientIpV4: ips.v4 || null,
        clientIpV6: ips.v6 || null,
        meta: { ua, lang, plat, clientIp: ips.v4 ?? ips.v6 ?? null }
      })
    }).catch(() => {});
  } catch (e: any) {
    pushLog(`[${tag}] IP capture failed: ${e?.message || e}`);
  }
}





// Minimaler Metadaten-Snapshot (nur harmlose Basics)
async function collectClientInfo(): Promise<Record<string, any>> {
  return {
    timeISO: new Date().toISOString(),
    lang: (navigator as any).language ?? null,
    userAgent: navigator.userAgent ?? null,
    platform: (navigator as any).platform ?? null,
    screen: {
      width: window.screen?.width ?? null,
      height: window.screen?.height ?? null,
      devicePixelRatio: window.devicePixelRatio ?? null,
    }
  };
}
// ALT: clientIp, clientIpV4, clientIpV6, clientIps mehrmals o.ä.  → entfernen
// NEU:










// ---------------------------------------------------------------------

// Simulation bis zum Klick pausiert starten:
useEffect(() => { setIsRunning(false); }, []);



/* Wiederholung des Dorf-Durchflugs. Ein Replay darf die laufende Simulation
 * NICHT umkonfigurieren: kein erneutes Info-Popup (das kennt man dann schon),
 * und ein pausierter Stand bleibt pausiert. Beides entscheidet onIntroFinished
 * anhand dieses Refs. */
const introReplayRef = useRef<{ wasRunning: boolean } | null>(null);

/* Den Einflug jederzeit erneut abspielen — auch mitten in einer laufenden
 * Simulation. startIntro() ist dafür gebaut: es merkt sich nur Startzeit und
 * ein Flag, schaltet die Controls ab, und finishIntro() setzt Kamera, Damping
 * und Controls sauber zurück. Ein Klick oder Mausrad bricht den Flug jederzeit
 * ab (userInteracted in setupScene).
 *
 * Bewusst OHNE Moduswechsel: die Dorf-Ansicht ist dieselbe builderGroup wie
 * Self Build, der Flugpfad hat also in beiden Modi etwas zu zeigen — und
 * activateSceneMode() würde clearEnergyTotalsOnly() auslösen und damit die
 * aufgelaufene Energie verwerfen, nur um ein Video anzusehen. */
const replayIntroFlight = () => {
  const ctrl = controlsRef.current as any;
  if (!ctrl?.startIntro) return;
  introReplayRef.current = { wasRunning: isRunning };
  setIntroPlaying(true);
  ctrl.stopAmbientLoop?.();
  ctrl.startIntro();
};

// Start-/Close-Handler
function startSimNow() {
  // NEU: Intro aus setupScene triggern
  // Falls controlsRef.startIntro erst im nächsten Frame hängt:
   if (!authed && !isDigitalTwin && !twinAuthPending) { setAuthOpen(true); return; }
    reallyStartSim();
  controlsRef.current?.stopAmbientLoop?.();
  controlsRef.current?.startIntro?.();
  requestAnimationFrame(() => controlsRef.current?.startIntro?.());

  setIntroPlaying(true);
  setSplashFade(true);
  setTimeout(() => {
    setShowSplash(false);

  }, 500);

}

function reallyStartSim() {
pushLog('Simulation begin');
  controlsRef.current?.stopAmbientLoop?.();
  controlsRef.current?.startIntro?.();
  requestAnimationFrame(() => controlsRef.current?.startIntro?.());
  setIntroPlaying(true);
  setSplashFade(true);
  setTimeout(() => setShowSplash(false), 500);
}

const closeSplashKeepPaused = () => {
  controlsRef.current?.stopAmbientLoop?.();
  setSplashFade(true);
  setTimeout(() => { setShowSplash(false); /* bleibt pausiert */ }, 280);
};
// ENTER/Escape
useEffect(() => {
  const onKey = (e: KeyboardEvent) => {
    if (!showSplash) return;
    //if (e.key === 'Enter') startSimNow();
    if (e.key === 'Escape') closeSplashKeepPaused();
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, [showSplash]);

// Keyboard shortcuts intentionally bind once and read the current sky state through refs/handlers.
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  const onKey = (e: KeyboardEvent) => {
    // avoid typing in inputs triggering hotkeys
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e as any).isComposing) return;

    if (e.key === '1') applyPreset('day');
    if (e.key === '2') toggleSky('aurora');
    if (e.key === '6') applyPreset('night');
    if (e.key === '7') toggleGalaxy();
    if (e.key.toLowerCase() === 'k') controlsRef.current?.cycleSkyPreset?.();
  };

  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


function applyPreset(mode: Exclude<SkyMode, null>) {
  // push to Three / controls and React state
  controlsRef.current?.setSkyPreset?.(mode);
  setSkyMode(mode);
  // remember last manual (non-galaxy) preset to return to
  if (mode !== 'galaxy') lastManualPresetRef.current = mode;
}

function toggleSky(mode: Exclude<SkyMode, null>) {
  // clicking the same mode toggles back to 'day'
  if (skyMode === mode) applyPreset('day');
  else applyPreset(mode);
}

function toggleGalaxy() {
  if (skyMode === 'galaxy') {
    // go back to the last non-galaxy preset (fallback 'night')
    applyPreset(lastManualPresetRef.current || 'night');
  } else {
    // enter galaxy, but remember where we came from
    lastManualPresetRef.current = skyMode || 'day';
    applyPreset('galaxy');
  }
}

// Splash enter flow intentionally keeps the original two-step interaction stable.
// eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => {
  const onKey = async (e: KeyboardEvent) => {
    if (!showSplash) return;
    if (e.key === 'Enter') {
      if (!fsDoneRef.current && !document.fullscreenElement) {
        fsDoneRef.current = true;
        setFsDone(true);                 // optional für Hinweistext
        await tryFullscreenNow();        // 1. Enter: nur Vollbild
      } else {
            if (!authed && !isDigitalTwin && !twinAuthPending) setAuthOpen(true);
            else startSimNow();                  // 2. Enter: Splash ausblenden + Start
      }
    }
    if (e.key === 'Escape') closeSplashKeepPaused();
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [showSplash]);

// Login-Modal poppt automatisch auf, nachdem der Besucher ein paar Sekunden den
// Flythrough im Hintergrund gesehen hat — kein Klick nötig, kein Warten auf 2x Enter.
useEffect(() => {
  if (autoAuthShownRef.current) return;
  if (authed || isDigitalTwin || authOpen || !showSplash) return;
  if (twinAuthPending) return; // Twin-Link wartet noch auf /api/trackers — nicht nach Login fragen
  const t = window.setTimeout(() => {
    autoAuthShownRef.current = true;
    setAuthOpen(true);
  }, 4000);
  return () => clearTimeout(t);
}, [authed, isDigitalTwin, authOpen, showSplash, twinAuthPending]);



  /* ========= EINZIGER Tabellen-Filter: Mounting-Degree ========= */
  const DEG_OPTIONS = [0,30, 35, 40, 45] as const;
  type MountFilter = 'all' | (typeof DEG_OPTIONS)[number];
  const [mountFilter, setMountFilter] = useState<MountFilter>(30);
  const mountFilterRef = useRef<MountFilter>(mountFilter);
  const matrixSignatureRef = useRef('');
  useEffect(() => { mountFilterRef.current = mountFilter; }, [mountFilter]);
  /* ============================================================ */

  /* ---- Richtung/Typ Normalisierung ---- */
  const normalizeDir = (v?: unknown, nameHint?: string): NESW | null => {
    if (v == null) return dirFromName(nameHint);
    if (typeof v === 'number' && isFinite(v)) {
      let deg = ((v % 360) + 360) % 360;
      if (deg >= 315 || deg < 45)  return 'N';
      if (deg >= 45  && deg < 135) return 'E';
      if (deg >= 135 && deg < 225) return 'S';
      if (deg >= 225 && deg < 315) return 'W';
      return null;
    }
    const s = String(v).toLowerCase().trim();
    if (['n','north','nord'].includes(s)) return 'N';
    if (['e','east','ost','öst','oe','osten'].includes(s)) return 'E';
    if (['s','south','sued','süd','sueden'].includes(s)) return 'S';
    if (['w','west','westen'].includes(s)) return 'W';
    return dirFromName(nameHint);
  };

  const dirFromName = (name?: string): NESW | null => {
    const s = (name ?? '').toLowerCase();
    if (/(\bnord\b|\bnorth\b|_nord\b|_north\b|^n_|-nord|-north)/.test(s)) return 'N';
    if (/(\bost\b|\beast\b|_ost\b|_east\b|^e_|-ost|-east)/.test(s))   return 'E';
    if (/(\bsüd\b|\bsued\b|\bsouth\b|_süd\b|_sued\b|_south\b|^s_|-süd|-sued|-south)/.test(s)) return 'S';
    if (/(\bwest\b|_west\b|^w_|-west)/.test(s)) return 'W';
    return null;
  };

  const normalizeType = (v?: string): TypeKey | null => {
    if (!v) return null;
    const s = v.toLowerCase().replace(/\s|-/g, '');
    if (s.includes('unlimited') || s.includes('dualaxis') || s.includes('2axis') || s.includes('dualtracker')) return 'unlimited';
    if (s.includes('limited')   || s.includes('dualaxislimited') || s.includes('2axisltd')) return 'limited';
    if (s.includes('single')    || s.includes('1axis') || s.includes('oneaxis')) return 'single';
    if (s.startsWith('fix')     || s.includes('static')) return 'fixed';
    return null;
  };

  const dirKeyFromNESW = (d: NESW): DirKey => {
    switch (d) { case 'N': return 'nord'; case 'E': return 'ost'; case 'S': return 'süd'; case 'W': return 'west'; }
  };

  /* ---- Tilt lesen (erst mountingDeg, sonst Name) ---- */
  const tiltFromName = (name?: string): number | undefined => {
    const m = (name ?? '').match(/(?:^|[_-])(0|30|35|40|45)(?:$|[_-])/);
    return m ? parseInt(m[1], 10) : undefined;
  };

  /** Aggregiert energyTotal mit aktuellem Mounting-Filter */
  const recomputeMatrixFromPanels = (filter: MountFilter = mountFilterRef.current, force = false) => {
    const m = new Map<string, number>();
    const signatureParts: string[] = [`filter:${filter}`];
    (['nord','ost','süd','west'] as DirKey[]).forEach(d =>
      (['fixed','single','limited','unlimited'] as TypeKey[]).forEach(t =>
        m.set(`${d}-${t}`, 0)
      )
    );

    trackerMapRef.current.forEach((panel: any, name: string) => {
      const rawTilt = panel?.mountingDeg;
      const absTilt = Number.isFinite(rawTilt) ? Math.abs(Number(rawTilt)) : tiltFromName(name);
      if (filter !== 'all' && absTilt !== filter) return;

      const dirRaw = panel?.compass ?? panel?.dir ?? panel?.direction ?? panel?.alignment;
      const typeRaw = panel?.type;
      const nd = normalizeDir(dirRaw, name);
      const nt = normalizeType(typeRaw);
      const total = Number(panel?.energyTotal ?? 0);
      signatureParts.push(`${name}:${absTilt}:${String(dirRaw)}:${String(typeRaw)}:${isFinite(total) ? total.toFixed(6) : 'nan'}`);
      if (!nd || !nt) return;

      const key = `${dirKeyFromNESW(nd)}-${nt}`;
      m.set(key, (m.get(key) ?? 0) + (isFinite(total) ? total : 0));
    });

    const signature = signatureParts.join('|');
    if (!force && signature === matrixSignatureRef.current) return;
    matrixSignatureRef.current = signature;
    setPanelEnergyData(m);
  };

  /* Kompass */
  const [cameraHeading, setCameraHeading] = useState<number>(0);
  const lastCameraHeadingRef = useRef<number | null>(null);
  const RAD2DEG = 180 / Math.PI;
  const computeCameraHeading = () => {
    if (!controlsRef.current || !controlsRef.current.object) return;
    const camera: THREE.Camera = controlsRef.current.object;
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    dir.y = 0; if (dir.lengthSq() === 0) return; dir.normalize();
    const s = WORLD.MIRROR_EW ? -1 : 1;
    let heading = Math.atan2(s * dir.x, dir.z) * RAD2DEG;
    heading = (heading - WORLD.NORTH_OFFSET_DEG + 360) % 360;
    const lastHeading = lastCameraHeadingRef.current;
    const diff = lastHeading == null ? Infinity : Math.abs(((heading - lastHeading + 540) % 360) - 180);
    if (diff < 0.5) return;
    lastCameraHeadingRef.current = heading;
    setCameraHeading(heading);
  };
  const orientNorth = () => {
    const controls = controlsRef.current; if (!controls) return;
    const cam: THREE.Camera & { position: THREE.Vector3 } = controls.object;
    const target: THREE.Vector3 = controls.target?.clone?.() ?? new THREE.Vector3(0, 0, 0);
    const offset = cam.position.clone().sub(target);
    const spherical = new THREE.Spherical().setFromVector3(offset);
    spherical.theta = THREE.MathUtils.degToRad(0 - WORLD.NORTH_OFFSET_DEG);
    cam.up.set(0,1,0); cam.rotation.z = 0;
    cam.position.setFromSpherical(spherical).add(target);
    controls.target.copy(target);
    controls.update();
    computeCameraHeading();
  };

  /* ==================== BUILDER TAB ==================== */
  type ActiveTab = 'sim' | 'builder';
  // 'village' = das gespeicherte Intro-Dorf (neuestes Admin-Modell) im reinen
  // Anzeige-Modus: gleiche Builder-Szene wie Self Build, aber ohne Editor-UI.
  type SceneMode = 'village' | 'multi' | 'single' | 'builder';
  const [activeTab, setActiveTab] = useState<ActiveTab>(projectMode ? 'builder' : 'sim');
  const [sceneMode, setSceneMode] = useState<SceneMode>(projectMode ? 'builder' : 'multi');
  // Erst true, wenn /api/public/intro-model erfolgreich geladen wurde — vorher
  // gibt es keinen "Dorf"-Tab und der Flythrough zeigt wie bisher die Siedlung.
  const [introModelAvailable, setIntroModelAvailable] = useState(false);
  const introModelLoadedRef = useRef(false);

  // Decode role from URL token (display-only, not security-critical)
  const isAdminToken = (() => {
    try {
      const t = new URLSearchParams(window.location.search).get('token') ?? '';
      const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g,'+').replace(/_/g,'/')));
      return payload.role === 'admin';
    } catch { return false; }
  })();

  type BuilderSubTab = 'house' | 'tracker' | 'obstacle';
  const [builderSubTab, setBuilderSubTab] = useState<BuilderSubTab>('house');
  const [builderPlacing, setBuilderPlacing] = useState(false);
  const [builderPlacedCount, setBuilderPlacedCount] = useState(0);
  const [builderGroundY, setBuilderGroundY] = useState(-3);
  type BuilderPlacedObject = {
    id: string;
    kind: 'house' | 'tracker' | 'obstacle';
    label: string;
  };
  const [builderObjects, setBuilderObjects] = useState<BuilderPlacedObject[]>([]);
  const [selectedBuilderObjectId, setSelectedBuilderObjectId] = useState<string | null>(null);
  const [selectedTransform, setSelectedTransform] = useState<{ rotDeg: number; scale: number; label: string; kind: string; scaleMin: number; scaleMax: number; roofHeight?: number; mountingDeg?: number } | null>(null);
  // Feste Slider-Grenzen relativ zur Größe *beim Auswählen* — bleiben stabil während des
  // Ziehens, statt bei jeder Änderung neu berechnet zu werden (sonst "wandert" der Regler).
  const withScaleRange = (t: { rotDeg: number; scale: number; label: string; kind: string; roofHeight?: number; mountingDeg?: number } | null) =>
    t ? { ...t, scaleMin: Math.max(0.01, t.scale * 0.1), scaleMax: t.scale * 4 } : null;
  const [builderMovingSelected, setBuilderMovingSelected] = useState(false);
  // Bidirektionale Kopplung Bestenliste <-> 3D-Szene: Klick auf Tracker highlightet
  // die Tabellenzeile; Klick auf die Zeile springt mit der Kamera zum Tracker.
  const [highlightedTrackerName, setHighlightedTrackerName] = useState<string | null>(null);
  const lastScrolledTrackerRef = useRef<string | null>(null);
  type HouseConfigStatus = 'idle' | 'saving' | 'saved' | 'error';
  const [houseConfigStatus, setHouseConfigStatus] = useState<HouseConfigStatus>('idle');

  // House config
  type BuilderHouseModel = {
    label: string;
    file: string;
    category: string;
    // scale ist bei handkalibrierten Modellen exakt vermessen. Fehlt sie, übernimmt
    // setupScene.getAutoScale() eine automatische Normierung anhand der echten
    // Bounding-Box auf fitSize (Default 70) — kein Rätselraten bei neu hinzugefügten GLBs nötig.
    scale?: number;
    fitSize?: number;
    ghostSize?: [number, number, number];
    preserveMaterials?: boolean;
    centerOnPlacement?: boolean;
  };

  const CAT_HAUS = '🏠 Häuser';
  const CAT_MEDITERRAN = '🏛️ Mediterran & Dörfer';
  const CAT_STADT = '🏙️ Städte & Sci-Fi';
  const CAT_SOLAR = '☀️ Solar-Tech';
  const CAT_CAFE = '☕ Cafés & Märkte';
  const CAT_NATUR = '🌳 Natur & Deko';
  const CAT_STRASSE = '🛣️ Straßen & Boden';
  const CAT_LEBEN = '🚶 Straßenleben';
  const CAT_WASSER = '⛵ Wasser';
  // Reihenfolge hier bestimmt auch die Reihenfolge der aufklappbaren Kategorien im Picker.
  const BUILDER_CATEGORY_ORDER = [CAT_HAUS, CAT_MEDITERRAN, CAT_STADT, CAT_SOLAR, CAT_CAFE, CAT_NATUR, CAT_STRASSE, CAT_LEBEN, CAT_WASSER];

  const BUILDER_HOUSE_MODELS: BuilderHouseModel[] = [
    { label: '25SOL Haus (flach)', file: '25SOL Umgebung 02.glb', category: CAT_HAUS, scale: 5 },
    { label: '25SOL Haus (30°)', file: '25SOL Umgebung 02 30 Grad.glb', category: CAT_HAUS, scale: 5 },
    { label: 'Farm House', file: 'farm_house.glb', category: CAT_HAUS, scale: 1 },
    // Dateiname korrigiert (2026-07-16): zeigte auf nicht existierendes 'assisi_city_scene.glb' — Modell lud still nie.
    { label: 'Italienisches Dorf', file: 'assisi_-_city_scene_assignment.glb', category: CAT_MEDITERRAN, scale: 0.075, ghostSize: [228, 153, 222] },
    { label: 'Italienisches Haus', file: 'house-italienisches-dorf.glb', category: CAT_MEDITERRAN, scale: 30, ghostSize: [57, 53, 35], preserveMaterials: true },
    { label: 'Futuristisches Haus', file: 'futuristisch.glb', category: CAT_STADT, scale: 100 },
    { label: 'American Colonial House', file: 'american_colonial_house.glb', category: CAT_HAUS, scale: 6.9, ghostSize: [150, 81, 90], preserveMaterials: true },
    { label: 'Apartment House', file: 'apartment_house.glb', category: CAT_HAUS, scale: 0.36, ghostSize: [105, 192, 99], preserveMaterials: true },
    { label: 'Brezhnevka Wohnblock', file: 'brezhnev_house_brezhnevka_russian_panel_house.glb', category: CAT_HAUS, scale: 0.034, ghostSize: [54, 66, 144], preserveMaterials: true },
    { label: 'Building 01', file: 'buildind_01.glb', category: CAT_HAUS, scale: 4.4, ghostSize: [94, 94, 36], preserveMaterials: true },
    { label: 'Country House', file: 'country_house_-_decimus_burton.glb', category: CAT_HAUS, scale: 1040, ghostSize: [244, 44, 240], preserveMaterials: true, centerOnPlacement: true },
    { label: 'London Kinnaird House', file: 'free_london_kinnaird_house.glb', category: CAT_HAUS, scale: 2.4, ghostSize: [124, 82, 94], preserveMaterials: true, centerOnPlacement: true },
    { label: 'Futuristische Stadt', file: 'futuristic_city.glb', category: CAT_STADT, scale: 0.00385, ghostSize: [651, 133, 588], preserveMaterials: true },
    { label: 'Luxusvilla mit Pool', file: 'modern_luxury_villa_house_building_with_pool.glb', category: CAT_HAUS, scale: 3, ghostSize: [82, 30, 104], preserveMaterials: true, centerOnPlacement: true },
    { label: 'Sci-Fi Building', file: 'sci-fi_building_1.glb', category: CAT_STADT, scale: 10, ghostSize: [60, 29, 60], preserveMaterials: true },
    { label: 'Small House', file: 'small_house.glb', category: CAT_HAUS, scale: 0.18, ghostSize: [55.5, 42, 90], preserveMaterials: true },
    // Neu hinzugefügt, unkalibriert -> automatische Größenanpassung (fitSize = Zieldurchmesser).
    { label: 'Modernes Hausdesign', file: 'a_modern_house_design.glb', category: CAT_HAUS, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Modernes Haus', file: 'modern_house.glb', category: CAT_HAUS, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Vorstadthaus', file: 'suburban_house.glb', category: CAT_HAUS, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Baltimore Reihenhäuser', file: 'rowhouses_on_29th_st._baltimore.glb', category: CAT_HAUS, fitSize: 130, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Tokio Stadtblock', file: 'city_scene_tokyo.glb', category: CAT_STADT, fitSize: 420, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Isparta Stadtblock', file: 'isparta_city_block_turkey.glb', category: CAT_STADT, fitSize: 420, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Schneelandschaft', file: 'snowy_mountain_landscape.glb', category: CAT_STADT, fitSize: 600, preserveMaterials: true, centerOnPlacement: true },
    { label: '🛣️ Straße (Asphalt)', file: 'road_asphalt.glb', category: CAT_STRASSE, fitSize: 150, preserveMaterials: true, centerOnPlacement: true },
    { label: '🛣️ Straße', file: 'road.glb', category: CAT_STRASSE, fitSize: 220, preserveMaterials: true, centerOnPlacement: true },
    { label: '🛣️ Straßennetz', file: 'roads.glb', category: CAT_STRASSE, fitSize: 420, preserveMaterials: true, centerOnPlacement: true },
    // Absichtlich KEINE Rasen-/Wiesen-Kacheln (flat_grass_surface.glb, gras.glb) hier mehr:
    // als einzeln platzierte Props auf dem heightmap-basierten Terrain unten (siehe "Terrain"-
    // Abschnitt) sitzen sie nie exakt auf der wechselnden Bodenhöhe und wirken wie schwebende,
    // andersfarbige Plattformen statt einer nahtlosen Fläche. Der Boden selbst ist jetzt 2500x2500
    // (statt 500x500) groß — der eigentliche Weg, den Self-Build-Platz zu erweitern.

    // MeshyAI-Generationen (2026-07-15) — mit gltf-transform optimiert (meshopt-Geometrie,
    // WebP-Texturen @1024px), ~89% kleiner als Rohexport. fitSize ist eine grobe Schätzung
    // nach Objektkategorie, keine Vermessung — bei Bedarf über den Größe-Slider live nachjustieren.
    { label: '🌴 Palme', file: 'Meshy_AI_Lone_Palm_0715203605_texture.glb', category: CAT_NATUR, fitSize: 45, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌳 Olivenbaum (V1)', file: 'Meshy_AI_Olive_Tree_in_a_Terra_0715203023_texture.glb', category: CAT_NATUR, fitSize: 35, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌳 Olivenbaum (V2)', file: 'Meshy_AI_Olive_Tree_in_a_Terra_0715203416_texture.glb', category: CAT_NATUR, fitSize: 35, preserveMaterials: true, centerOnPlacement: true },
    { label: '🍋 Zitronenbaum', file: 'Meshy_AI_Lemon_Tree_in_a_Terra_0715203056_texture.glb', category: CAT_NATUR, fitSize: 35, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌺 Bougainvillea-Kübel', file: 'Meshy_AI_Bougainvillea_in_a_Te_0715203527_texture.glb', category: CAT_NATUR, fitSize: 30, preserveMaterials: true, centerOnPlacement: true },
    { label: '⛲ Steinbrunnen', file: 'Meshy_AI_Heritage_Stone_Founta_0715203547_texture.glb', category: CAT_NATUR, fitSize: 40, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Efeu-Steinmauer', file: 'Meshy_AI_Ivy_Clad_Stone_Wall_0715202304_texture.glb', category: CAT_NATUR, fitSize: 60, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Kopfsteinpflaster-Fläche', file: 'Meshy_AI_Cobblestone_Paver_Pan_0715203338_texture.glb', category: CAT_STRASSE, fitSize: 150, preserveMaterials: true, centerOnPlacement: true },
    { label: '☕ Café-Tisch mit Schirm', file: 'Meshy_AI_Cream_Parasol_Cafe_0715203620_texture.glb', category: CAT_CAFE, fitSize: 25, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏪 Marktstand', file: 'Meshy_AI_Rustic_Regional_Marke_0715203503_texture.glb', category: CAT_CAFE, fitSize: 45, preserveMaterials: true, centerOnPlacement: true },
    { label: '☀️ Solar-Pergola', file: 'Meshy_AI_Solar_Pergola_Oasis_0715203118_texture.glb', category: CAT_SOLAR, fitSize: 70, preserveMaterials: true, centerOnPlacement: true },
    { label: '☀️ Solar-Gartenapartment', file: 'Meshy_AI_Solar_Garden_Apartmen_0715203147_texture.glb', category: CAT_SOLAR, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: '⛵ Angedockte Segelyacht', file: 'Meshy_AI_Docked_Sailboat_by_th_0715203449_texture.glb', category: CAT_WASSER, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: '🪑 Verwitterte Parkbank', file: 'Meshy_AI_Weathered_Park_Bench_0715203632_texture.glb', category: CAT_LEBEN, fitSize: 18, preserveMaterials: true, centerOnPlacement: true },
    { label: '🪑 Person auf Bank', file: 'Meshy_AI_Solitude_on_a_Bench_0715203645_texture.glb', category: CAT_LEBEN, fitSize: 20, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚶 Fußgänger (gehend)', file: 'Meshy_AI_Sunlit_Wanderer_0715203657_texture.glb', category: CAT_LEBEN, fitSize: 18, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚶 Junge mit Ball', file: 'Meshy_AI_Boy_Kicking_a_Vintage_0715202856_texture.glb', category: CAT_LEBEN, fitSize: 16, preserveMaterials: true, centerOnPlacement: true },
    { label: '🗑️ Solar-Mülleimer', file: 'Meshy_AI_Solar_Powered_Trash_B_0715202110_texture.glb', category: CAT_SOLAR, fitSize: 12, preserveMaterials: true, centerOnPlacement: true },
    { label: '💡 Solar-Straßenlaterne', file: 'Meshy_AI_Solar_Powered_Street__0715203807_texture.glb', category: CAT_SOLAR, fitSize: 25, preserveMaterials: true, centerOnPlacement: true },
    { label: '🔌 EV-Ladesäule', file: 'Meshy_AI_Solar_Pillar_EV_Charg_0715203725_texture.glb', category: CAT_SOLAR, fitSize: 15, preserveMaterials: true, centerOnPlacement: true },
    { label: '🔋 Solar-Wandspeicher', file: 'Meshy_AI_Solar_Wall_Vault_0715203706_texture.glb', category: CAT_SOLAR, fitSize: 12, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚲 E-Bike (V1)', file: 'Meshy_AI_Solar_Powered_E_Bike__0715202359_texture.glb', category: CAT_SOLAR, fitSize: 15, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚲 E-Bike (V2)', file: 'Meshy_AI_Solar_Powered_E_Bike__0715202946_texture.glb', category: CAT_SOLAR, fitSize: 15, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚲 E-Bike (V3)', file: 'Meshy_AI_Solar_Powered_E_Bike__0715203353_texture.glb', category: CAT_SOLAR, fitSize: 15, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚗 Elektro-Microcar', file: 'Meshy_AI_Solstice_Microcar_0715203819_texture.glb', category: CAT_SOLAR, fitSize: 35, preserveMaterials: true, centerOnPlacement: true },
    { label: '🐦 Möwen am Steg', file: 'Meshy_AI_Gull_Gathering_on_a_S_0715203039_texture.glb', category: CAT_WASSER, fitSize: 20, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Kai-Uferstück', file: 'Meshy_AI_Tilted_Shoreline_0715203431_texture.glb', category: CAT_WASSER, fitSize: 60, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚪 Solar-Torhaus (V1)', file: 'Meshy_AI_Solar_Gatehouse_0715201536_texture.glb', category: CAT_SOLAR, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚪 Solar-Torhaus (V2)', file: 'Meshy_AI_Solar_Gatehouse_0715203757_texture.glb', category: CAT_SOLAR, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: '☕ Café-Kiosk (V1)', file: 'Meshy_AI_Caffè_Kiosk_0715201615_texture.glb', category: CAT_CAFE, fitSize: 70, preserveMaterials: true, centerOnPlacement: true },
    { label: '☕ Café-Kiosk (V2)', file: 'Meshy_AI_Caffè_Kiosk_0715201737_texture.glb', category: CAT_CAFE, fitSize: 70, preserveMaterials: true, centerOnPlacement: true },
    { label: '☕ Café del Sole (V1)', file: 'Meshy_AI_Caffè_del_Sole_0715202423_texture.glb', category: CAT_CAFE, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '☕ Café del Sole (V2)', file: 'Meshy_AI_Caffè_Del_Sole_0715202925_texture.glb', category: CAT_CAFE, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '☀️ Solar-Café-Terrasse', file: 'Meshy_AI_Solar_Café_Terrace_0715202226_texture.glb', category: CAT_SOLAR, fitSize: 110, preserveMaterials: true, centerOnPlacement: true },
    { label: '☀️ Solarbetriebenes Café', file: 'Meshy_AI_Solar_Powered_Café_o_0715202150_texture.glb', category: CAT_SOLAR, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Sommer-Lounge-Ecke', file: 'Meshy_AI_Sunlit_Summer_Chat_0715202318_texture.glb', category: CAT_CAFE, fitSize: 60, preserveMaterials: true, centerOnPlacement: true },
    { label: '☀️ Efeu-Solarhaus', file: 'Meshy_AI_Ivy_Clad_Solar_House_0715202338_texture.glb', category: CAT_SOLAR, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterraner Torbogen', file: 'Meshy_AI_Mediterranean_Archway_0715202132_texture.glb', category: CAT_MEDITERRAN, fitSize: 80, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterrane Gasse', file: 'Meshy_AI_Mediterranean_Alleywa_0715202704_texture.glb', category: CAT_MEDITERRAN, fitSize: 180, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterraner Innenhof', file: 'Meshy_AI_Mediterranean_Courtya_0715202446_texture.glb', category: CAT_MEDITERRAN, fitSize: 180, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterrane Dachterrasse', file: 'Meshy_AI_Mediterranean_Rooftop_0715202620_texture.glb', category: CAT_MEDITERRAN, fitSize: 130, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterrane Villa', file: 'Meshy_AI_Mediterranean_Villa_w_0715202822_texture.glb', category: CAT_MEDITERRAN, fitSize: 150, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Küstenhäuschen', file: 'Meshy_AI_Seaside_Cottage_with__0715202758_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Solaris Gästehaus', file: 'Meshy_AI_Solaris_Gästehaus_0715202546_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Provenzalische Villa', file: 'Meshy_AI_Provencal_Villa_with__0715202843_texture.glb', category: CAT_MEDITERRAN, fitSize: 150, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚏 Piazza-del-Sole-Bushaltestelle', file: 'Meshy_AI_Piazza_del_Sole_Bus_S_0715202011_texture.glb', category: CAT_LEBEN, fitSize: 60, preserveMaterials: true, centerOnPlacement: true },
    { label: '☀️ Solar-Gartenanlage', file: 'Meshy_AI_Solar_Powered_Garden__0715201548_texture.glb', category: CAT_SOLAR, fitSize: 150, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mittelmeer-Straßenzug (V1)', file: 'Meshy_AI_Sunlit_Mediterranean__0715201741_texture.glb', category: CAT_MEDITERRAN, fitSize: 300, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mittelmeer-Straßenzug (V2)', file: 'Meshy_AI_Sunlit_Mediterranean__0715202739_texture.glb', category: CAT_MEDITERRAN, fitSize: 250, preserveMaterials: true, centerOnPlacement: true },

    // MeshyAI-Generationen (2026-07-16) — gleiche Pipeline, jetzt als Skript:
    // node scripts/optimize-models.mjs public/models/<datei>.glb (meshopt + WebP @1024px).
    // fitSize wie oben: grobe Schätzung nach Objektkategorie, per Größe-Slider nachjustierbar.
    { label: '⚓ Anchor House', file: 'Meshy_AI_Anchor_House_0716173436_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🐟 Fisch-Auktionshalle', file: 'Meshy_AI_Asta_del_Pesce_0716173327_texture.glb', category: CAT_CAFE, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: '🐟 Fischmarkt-Stand', file: 'Meshy_AI_Asta_del_Pesce_Market_0716173310_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: '🍝 Trattoria mit blauen Läden', file: 'Meshy_AI_Blue_Shuttered_Tratto_0716172735_texture.glb', category: CAT_CAFE, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '📚 Terrassen-Buchladen', file: 'Meshy_AI_Bookshop_on_the_Terra_0716173045_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌺 Bougainvillea-Cottage', file: 'Meshy_AI_Bougainvillea_Cottage_0716173430_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏺 Keramik-Marktstand', file: 'Meshy_AI_Ceramic_Market_Stall_0716172858_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: '⛵ Bootshaus-Werkstatt', file: 'Meshy_AI_Coastal_Boathouse_Wor_0716172850_texture.glb', category: CAT_WASSER, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '⚓ Hafen-Marktstand', file: 'Meshy_AI_Dockside_Market_Stall_0716173219_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏫 Gartenhof-Schule', file: 'Meshy_AI_Garden_Courtyard_Scho_0716173314_texture.glb', category: CAT_MEDITERRAN, fitSize: 140, preserveMaterials: true, centerOnPlacement: true },
    { label: '🦐 Meeresfrüchte-Markt', file: 'Meshy_AI_Harbor_Market_Seafood_0716173150_texture.glb', category: CAT_CAFE, fitSize: 60, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏨 Hotel del Fiore', file: 'Meshy_AI_Hotel_del_Fiore_0716173142_texture.glb', category: CAT_MEDITERRAN, fitSize: 140, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌿 Gartenschuppen mit Efeubogen', file: 'Meshy_AI_Ivy_Arch_Garden_Shed_0716172750_texture.glb', category: CAT_NATUR, fitSize: 60, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌿 Efeu-Haus (V1)', file: 'Meshy_AI_Ivy_Clad_Mediterranea_0716172742_texture.glb', category: CAT_MEDITERRAN, fitSize: 110, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌿 Efeu-Torhaus', file: 'Meshy_AI_Ivy_Gatehouse_0716173009_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌿 Efeu-Haus (V2)', file: 'Meshy_AI_Ivy_Wrapped_Mediterra_0716173029_texture.glb', category: CAT_MEDITERRAN, fitSize: 110, preserveMaterials: true, centerOnPlacement: true },
    { label: '🍕 Pizzeria del Borgo', file: 'Meshy_AI_La_Pizzeria_del_Borgo_0716173417_texture.glb', category: CAT_CAFE, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🍋 Zitronenhain-Terrasse', file: 'Meshy_AI_Lemon_Grove_Terrace_0716173303_texture.glb', category: CAT_NATUR, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: '⛪ Kleine Steinkapelle', file: 'Meshy_AI_Little_Stone_Chapel_0716173242_texture.glb', category: CAT_MEDITERRAN, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Balkonhaus', file: 'Meshy_AI_Mediterranean_Balcony_0716173423_texture.glb', category: CAT_MEDITERRAN, fitSize: 110, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏥 Mediterrane Klinik', file: 'Meshy_AI_Mediterranean_Clinic__0716172912_texture.glb', category: CAT_MEDITERRAN, fitSize: 120, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterranes Cottage', file: 'Meshy_AI_Mediterranean_Cottage_0716173130_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterraner Innenhof (V2)', file: 'Meshy_AI_Mediterranean_Courtya_0716172839_texture.glb', category: CAT_MEDITERRAN, fitSize: 180, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterraner Innenhof (V3)', file: 'Meshy_AI_Mediterranean_Courtya_0716173202_texture.glb', category: CAT_MEDITERRAN, fitSize: 180, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterraner Innenhof (V4)', file: 'Meshy_AI_Mediterranean_Courtya_0716173212_texture.glb', category: CAT_MEDITERRAN, fitSize: 180, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterraner Innenhof (V5)', file: 'Meshy_AI_Mediterranean_Courtya_0716173253_texture.glb', category: CAT_MEDITERRAN, fitSize: 180, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterranes Gartenhaus', file: 'Meshy_AI_Mediterranean_Garden__0716173352_texture.glb', category: CAT_MEDITERRAN, fitSize: 120, preserveMaterials: true, centerOnPlacement: true },
    { label: '🍨 Gelateria', file: 'Meshy_AI_Mediterranean_Gelato__0716173053_texture.glb', category: CAT_CAFE, fitSize: 90, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Küstenhaus', file: 'Meshy_AI_Mediterranean_Seaside_0716173403_texture.glb', category: CAT_MEDITERRAN, fitSize: 110, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mediterrane Villa (V2)', file: 'Meshy_AI_Mediterranean_Villa_w_0716172725_texture.glb', category: CAT_MEDITERRAN, fitSize: 150, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚲 Fahrradwerkstatt „Officina Cicli“', file: 'Meshy_AI_Officina_Cicli_0716173410_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🫒 Olivenöl-Marktstand', file: 'Meshy_AI_Olive_Oil_Market_Stal_0716173341_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: '📮 Post-Cottage', file: 'Meshy_AI_Poste_Cottage_0716172921_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Provenzalisches Cottage', file: 'Meshy_AI_Provencal_Cottage_Mar_0716173117_texture.glb', category: CAT_MEDITERRAN, fitSize: 110, preserveMaterials: true, centerOnPlacement: true },
    { label: '⚓ Kai-Lagerhaus', file: 'Meshy_AI_Quayside_Storehouse_0716173036_texture.glb', category: CAT_WASSER, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🥖 Bäckerei mit Dachgarten', file: 'Meshy_AI_Rooftop_Garden_Bakery_0716173235_texture.glb', category: CAT_CAFE, fitSize: 110, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏫 Schule mit Dachgarten', file: 'Meshy_AI_Rooftop_Garden_School_0716172821_texture.glb', category: CAT_MEDITERRAN, fitSize: 140, preserveMaterials: true, centerOnPlacement: true },
    { label: '🍞 Brot-Marktstand', file: 'Meshy_AI_Rustic_Bread_Market_S_0716173040_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏺 Keramik-Werkstatt', file: 'Meshy_AI_Rustic_Ceramic_Worksh_0716173334_texture.glb', category: CAT_MEDITERRAN, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '💐 Blumenstand', file: 'Meshy_AI_Rustic_Flower_Stall_0716173109_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏪 Marktstand (rustikal)', file: 'Meshy_AI_Rustic_Market_Stall_w_0716173321_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: '🎣 Fischerhäuschen', file: 'Meshy_AI_Seaside_Fisherman_s_C_0716172806_texture.glb', category: CAT_WASSER, fitSize: 100, preserveMaterials: true, centerOnPlacement: true },
    { label: '🌶️ Gewürz-Marktstand', file: 'Meshy_AI_Spice_Market_Stall_0716173016_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Mittelmeer-Straßenzug (V3)', file: 'Meshy_AI_Sunlit_Mediterranean__0716172929_texture.glb', category: CAT_MEDITERRAN, fitSize: 250, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Terrassengarten-Villa', file: 'Meshy_AI_Terraced_Garden_Villa_0716172935_texture.glb', category: CAT_MEDITERRAN, fitSize: 130, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Terrassenhaus', file: 'Meshy_AI_Terraced_Mediterranea_0716172830_texture.glb', category: CAT_MEDITERRAN, fitSize: 120, preserveMaterials: true, centerOnPlacement: true },
    { label: '🎣 Fischerhof', file: 'Meshy_AI_The_Fisherman_s_Court_0716173125_texture.glb', category: CAT_WASSER, fitSize: 120, preserveMaterials: true, centerOnPlacement: true },
    { label: '🕰️ Toskanisches Uhrenhaus', file: 'Meshy_AI_The_Tuscan_Clock_Hous_0716173021_texture.glb', category: CAT_MEDITERRAN, fitSize: 120, preserveMaterials: true, centerOnPlacement: true },
    { label: 'Innenhof mit drei Bögen', file: 'Meshy_AI_Three_Arched_Courtyar_0716173102_texture.glb', category: CAT_MEDITERRAN, fitSize: 150, preserveMaterials: true, centerOnPlacement: true },
    { label: '☕ Kleines Küsten-Café', file: 'Meshy_AI_Tiny_Coastal_Café_St_0716173259_texture.glb', category: CAT_CAFE, fitSize: 60, preserveMaterials: true, centerOnPlacement: true },
    { label: '🚒 Feuerwache „Vigili del Fuoco“', file: 'Meshy_AI_Vigili_del_Fuoco_Fire_0716173345_texture.glb', category: CAT_MEDITERRAN, fitSize: 130, preserveMaterials: true, centerOnPlacement: true },
    { label: '🎵 Villa delle Note', file: 'Meshy_AI_Villa_delle_Note_0716173226_texture.glb', category: CAT_MEDITERRAN, fitSize: 140, preserveMaterials: true, centerOnPlacement: true },
    { label: '🏪 Marktstand (gestreift)', file: 'Meshy_AI_Vintage_Striped_Marke_0716172815_texture.glb', category: CAT_CAFE, fitSize: 50, preserveMaterials: true, centerOnPlacement: true },
  ];
  const [selHouseIdx, setSelHouseIdx] = useState(0);
  const [builderOpenCategory, setBuilderOpenCategory] = useState<string | null>(null);
  const [builderRotation, setBuilderRotation] = useState(0);
  const [builderScale, setBuilderScale] = useState(1); // Multiplikator auf Preset-/Auto-Fit-Größe, damit Modelle zusammenpassen

  // Tracker config
  type TrackerType = 'fixed' | 'single' | 'limited' | 'unlimited';
  const [selTrackerType, setSelTrackerType] = useState<TrackerType>('limited');
  const [trackerMountDeg, setTrackerMountDeg] = useState(30);
  const [trackerCompass, setTrackerCompass] = useState<'süd'|'nord'|'ost'|'west'>('süd');
  const [trackerRoofHeight, setTrackerRoofHeight] = useState(5);

  // Panel-Bestückung & Farben. eff = relativer Wirkungsgrad der Modulfarbe —
  // farbige Module (z.B. Terracotta für mediterrane Dächer) liefern real
  // weniger Ertrag als Standard-Anthrazit; genau das macht den Farbvergleich
  // im Digital Twin interessant. Werte an übliche BIPV-Datenblätter angelehnt.
  const TRACKER_PANEL_COUNTS = [1, 2, 3, 4, 6, 8] as const;
  const TRACKER_STANDARD_KW = 1.44; // powerKW des EnergyTrackers = Standard-Rack (4 Module)
  const PANEL_COLORS = [
    { label: 'Anthrazit', hex: '#10151c', eff: 1.0 },
    { label: 'Blau', hex: '#1a2f5e', eff: 0.97 },
    { label: 'Terracotta', hex: '#9a4a32', eff: 0.85 },
    { label: 'Grün', hex: '#33523f', eff: 0.88 },
    { label: 'Bronze', hex: '#6e5432', eff: 0.90 },
  ] as const;
  const FRAME_COLORS = [
    { label: 'Alu', hex: null },
    { label: 'Anthrazit', hex: '#2b2d31' },
    { label: 'Weiß', hex: '#dfe3e6' },
    { label: 'Terracotta', hex: '#8a4a2f' },
  ] as const;
  const [trackerPanelCount, setTrackerPanelCount] = useState<number>(4);
  const [trackerPanelColorIdx, setTrackerPanelColorIdx] = useState(0);
  const [trackerFrameColorIdx, setTrackerFrameColorIdx] = useState(0);
  // Raster hochkant (2 breit x 4 tief) oder quer (4 breit x 2 tief) — gleiche Kipprichtung
  const [trackerPanelOrientation, setTrackerPanelOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const TRACKER_LAYOUTS: Record<number, [number, number]> = { 1: [1, 1], 2: [1, 2], 3: [1, 3], 4: [2, 2], 6: [2, 3], 8: [2, 4] };
  const trackerGridLabel = (count: number, orientation: 'portrait' | 'landscape') => {
    const [c, r] = TRACKER_LAYOUTS[count] ?? [2, 2];
    return orientation === 'landscape' ? `${r} breit × ${c} tief` : `${c} breit × ${r} tief`;
  };

  // Obstacle config
  type ObstacleType = 'tree' | 'wall';
  const [selObstacle, setSelObstacle] = useState<ObstacleType>('tree');
  const [obstacleW, setObstacleW] = useState(20);
  const [obstacleH, setObstacleH] = useState(10);
  const [obstacleD, setObstacleD] = useState(3);
  const [treeHeight, setTreeHeight] = useState(20);
  const [treeCrownRadius, setTreeCrownRadius] = useState(6);

  const switchTab = (tab: ActiveTab) => {
    if (tab === activeTab) return;
    if (tab === 'builder') {
      setIsRunning(false);
      controlsRef.current?.exitBuilderMode?.(); // ensure clean
      controlsRef.current?.enterBuilderMode?.();
      energyTracker.current?.setOccluders((controlsRef.current as any)?.getBuilderOccluderMeshes?.() ?? []);
    } else {
      controlsRef.current?.builderCancelPlacing?.();
      controlsRef.current?.exitBuilderMode?.();
      energyTracker.current?.setOccluders([]);
    }
    setBuilderPlacing(false);
    setActiveTab(tab);
  };

  /* Twin-Nutzer dürfen ihre eigene Umgebung modellieren: Bäume, Nachbargebäude,
   * Probe-Tracker. Verschattung durchzurechnen ist der Zweck dieser Simulation —
   * das darf nicht am Admin-Token hängen. Vorher sperrte der Lock unten JEDEN
   * Twin-Nutzer ohne Admin-Rolle in die Einzelansicht, und weil das Service-Token
   * gar kein role-Claim trug, traf das auch Admins: die Builder-Leiste war für
   * alle unerreichbar.
   *
   * Admin-Vorbehalt bleiben nur die kuratierten Schau-Ansichten (Dorf/Siedlung)
   * und das Speichern einer Haus-Config auf einen FREMDEN User (siehe das
   * isAdminToken-Gate am USER-ID-Feld weiter unten). */
  const TWIN_SCENE_MODES: SceneMode[] = ['single', 'builder'];
  const mayUseSceneMode = (mode: SceneMode) =>
    !isDigitalTwin || isAdminToken || TWIN_SCENE_MODES.includes(mode);

  useEffect(() => {
    if (!mayUseSceneMode(sceneMode)) setSceneMode('single');
  }, [isDigitalTwin, isAdminToken, sceneMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const activateSceneMode = (mode: SceneMode) => {
    if (!mayUseSceneMode(mode)) return;
    /* sceneMode allein reicht als "ist schon aktiv"-Test nicht: der Twin-Flow
     * setzt sceneMode='builder' beim Laden der House-Config, ohne switchTab zu
     * rufen — activeTab steht dann noch auf 'sim'. Ohne diese Ausnahme wäre der
     * erste Klick auf "Self Build" ein stiller No-op (der Button färbt sich
     * trotzdem aktiv), das Editor-Panel hängt an activeTab === 'builder' und
     * mountet nie. Bei Admin-Twin-Links bietet die Leiste nur diesen einen
     * Button — es gäbe also gar keinen Ausweg. */
    const tabForMode: ActiveTab = mode === 'builder' ? 'builder' : 'sim';
    if (mode === sceneMode && activeTab === tabForMode) return;
    setSceneMode(mode);
    // Reset energy when switching views so old data doesn't bleed across
    clearEnergyTotalsOnly();
    // Reset SunTracker time so the first tick after switch doesn't produce a huge deltaHours
    sunTrackerRef.current?.resetLastUpdateTime(date);

    if (mode === 'builder') {
      if (activeTab !== 'builder') switchTab('builder');
      else controlsRef.current?.enterBuilderMode?.();
      energyTracker.current?.setOccluders((controlsRef.current as any)?.getBuilderOccluderMeshes?.() ?? []);
      return;
    }

    if (mode === 'village') {
      // Dorf = Builder-Szene im Anzeige-Modus: Editor-Tab schließen (falls offen),
      // Builder-Mode aktivieren, aber Simulation weiterlaufen lassen.
      controlsRef.current?.builderCancelPlacing?.();
      if (activeTab !== 'sim') switchTab('sim');
      controlsRef.current?.enterBuilderMode?.();
      energyTracker.current?.setOccluders((controlsRef.current as any)?.getBuilderOccluderMeshes?.() ?? []);
      setIsRunning(true);
      return;
    }

    // Leaving builder/village: clear builder occluders, restart simulation.
    // exitBuilderMode explizit rufen — beim Wechsel Dorf→Siedlung bleibt
    // activeTab 'sim', switchTab (das sonst exitBuilderMode ruft) feuert also nicht.
    controlsRef.current?.builderCancelPlacing?.();
    controlsRef.current?.exitBuilderMode?.();
    energyTracker.current?.setOccluders([]);
    controlsRef.current?.setHouseMode?.(mode);
    if (activeTab !== 'sim') switchTab('sim');
    // Restart simulation ticks if coming from builder (which stops it)
    setIsRunning(true);
  };

  const builderBeginPlace = () => {
    const ctrl = controlsRef.current;
    if (!ctrl) return;
    if (builderSubTab === 'house') {
      const model = BUILDER_HOUSE_MODELS[selHouseIdx] ?? BUILDER_HOUSE_MODELS[0];
      if (!model) return;
      ctrl.builderSetRotation?.(builderRotation);
      ctrl.builderSetScaleMultiplier?.(builderScale);
      ctrl.builderStartPlacing?.({
        kind: 'house',
        label: model.label,
        modelFile: model.file,
        scale: model.scale,
        fitSize: model.fitSize,
        ghostSize: model.ghostSize,
        preserveMaterials: model.preserveMaterials,
        centerOnPlacement: model.centerOnPlacement,
      });
    } else if (builderSubTab === 'tracker') {
      const pc = PANEL_COLORS[trackerPanelColorIdx] ?? PANEL_COLORS[0];
      const fc = FRAME_COLORS[trackerFrameColorIdx] ?? FRAME_COLORS[0];
      ctrl.builderSetRotation?.(builderRotation);
      ctrl.builderStartPlacing?.({
        kind: 'tracker',
        label: `${selTrackerType} Tracker (${trackerCompass}, ${trackerMountDeg}°, ${trackerPanelCount}P ${pc.label})`,
        trackerType: selTrackerType,
        mountingDeg: trackerMountDeg,
        compass: trackerCompass,
        roofHeight: trackerRoofHeight,
        panelCount: trackerPanelCount,
        panelColor: pc.hex,
        panelEfficiency: pc.eff,
        frameColor: fc.hex,
        panelOrientation: trackerPanelOrientation,
      });
    } else {
      const obstacleLabels: Record<ObstacleType, string> = { tree: 'Baum', wall: 'Wand' };
      ctrl.builderSetRotation?.(builderRotation);
      ctrl.builderStartPlacing?.({
        kind: 'obstacle',
        label: obstacleLabels[selObstacle],
        obstacleType: selObstacle,
        width: obstacleW,
        height: obstacleH,
        depth: obstacleD,
        treeHeight,
        treeCrownRadius,
      });
    }
    setBuilderPlacing(true);
    ctrl.onBuilderPlaced = (info: any) => {
      // Trigger one sun-position update so new tracker shows correct orientation
      updateSunPosition();
      // Häuser/Modelle: nach dem Platzieren zurück in den Auswahlmodus, statt "scharf"
      // zu bleiben — sonst platziert jeder weitere Klick (auch auf ein schon stehendes
      // Objekt) einfach eine weitere Kopie, und man kann per Linksklick nichts mehr
      // auswählen/verschieben. Tracker/Hindernisse bleiben bewusst im Stempel-Modus
      // (siehe "Auto-restart" Effekt unten — praktisch, um mehrere am Stück zu setzen).
      if (info?.kind === 'house') {
        controlsRef.current?.builderCancelPlacing?.();
        setBuilderPlacing(false);
      }
    };
  };

  const builderCancel = () => {
    controlsRef.current?.builderCancelPlacing?.();
    setBuilderPlacing(false);
  };

  // Auto-restart placement when tracker/obstacle config changes while already placing
  useEffect(() => {
    if (!builderPlacing) return;
    if (builderSubTab === 'tracker' || builderSubTab === 'obstacle') {
      builderBeginPlace();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selTrackerType, trackerCompass, trackerMountDeg, trackerRoofHeight, trackerPanelCount, trackerPanelColorIdx, trackerFrameColorIdx, trackerPanelOrientation, selObstacle, obstacleW, obstacleH, obstacleD, treeHeight, treeCrownRadius]);

  const builderClearAll = () => {
    controlsRef.current?.clearBuilderScene?.();
    setBuilderPlacedCount(0);
    setBuilderObjects([]);
    setSelectedBuilderObjectId(null);
    setBuilderPlacing(false);
  };

  const builderSelectObject = (id: string | null) => {
    controlsRef.current?.builderCancelPlacing?.();
    controlsRef.current?.selectBuilderObject?.(id);
    setBuilderPlacing(false);
    setBuilderMovingSelected(false);
    setSelectedBuilderObjectId(id);
    setSelectedTransform(withScaleRange(controlsRef.current?.getSelectedBuilderTransform?.() ?? null));
  };

  const builderDeleteObject = (id: string) => {
    controlsRef.current?.builderCancelPlacing?.();
    controlsRef.current?.deleteBuilderObject?.(id);
    setBuilderPlacing(false);
    setBuilderMovingSelected(false);
  };

  const builderMoveSelected = () => {
    if (!selectedBuilderObjectId) return;
    controlsRef.current?.builderStartMoveSelected?.();
    setBuilderMovingSelected(true);
  };

  const builderDuplicateSelectedObj = () => {
    if (!selectedBuilderObjectId) return;
    controlsRef.current?.builderDuplicateSelected?.();
    setBuilderPlacing(true);
  };

  // Strg/Cmd+D dupliziert das ausgewählte Objekt (Platzierung startet sofort, Klick setzt ab).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (activeTab !== 'builder' || !selectedBuilderObjectId) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        builderDuplicateSelectedObj();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBuilderObjectId]);

  /* ==================== 🏠 HAUS-CONFIG SAVE / LOAD ==================== */

  const [adminTargetUserId, setAdminTargetUserId] = useState('');

  const saveHouseConfig = async (targetUserId?: string) => {
    const ctrl = controlsRef.current as any;
    if (!ctrl?.getBuilderSnapshot) return;
    const snapshot = ctrl.getBuilderSnapshot();
    const params = new URLSearchParams(window.location.search);
    const token   = params.get('token') ?? '';
    const apiBase = (params.get('api_base') ?? '').replace(/\/$/, '');
    if (!token || !apiBase) return;
    setHouseConfigStatus('saving');
    try {
      const path = targetUserId
        ? `/api/admin/house-config/${targetUserId}`
        : '/api/house-config';
      const proxyUrl = `/api/twin?api_base=${encodeURIComponent(apiBase)}&path=${encodeURIComponent(path)}`;
      const res = await fetch(proxyUrl, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ objects: snapshot }),
      });
      setHouseConfigStatus(res.ok ? 'saved' : 'error');
      setTimeout(() => setHouseConfigStatus('idle'), 3000);
    } catch {
      setHouseConfigStatus('error');
      setTimeout(() => setHouseConfigStatus('idle'), 3000);
    }
  };

  const loadHouseConfigRef = useRef(false);
  useEffect(() => {
    if (!['ready', 'site_ready'].includes(trackerApiState.status) || loadHouseConfigRef.current) return;
    loadHouseConfigRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const token   = params.get('token') ?? '';
    const apiBase = (params.get('api_base') ?? '').replace(/\/$/, '');
    if (!token || !apiBase) return;
    const siteId  = params.get('site') ?? '';
    let cancelled = false;
    (async () => {
      // Warten, bis die 3D-Szene initialisiert ist (max. ~15s) — wie beim Intro-Dorf.
      for (let i = 0; i < 60 && !(controlsRef.current as any)?.loadBuilderSnapshot; i++) {
        if (cancelled) return;
        await new Promise(r => setTimeout(r, 250));
      }
      const ctrl = controlsRef.current as any;
      if (cancelled || !ctrl?.loadBuilderSnapshot) return;

      // Provider für Kundenhaus-GLBs IMMER im Twin-Modus setzen (nicht nur bei
      // ?site=): auch gespeicherte Szenen mit source:'site-model'-Häusern müssen
      // ihre GLBs re-fetchen können. Der Fetch läuft same-origin über den
      // Binary-Proxy (/api/site-model, NICHT /api/twin — der zerstört Binärdaten)
      // und wird als blob:-URL an den GLTFLoader gegeben (CSP-konform).
      const blobUrlCache = new Map<string, Promise<string>>();
      ctrl.setSiteModelUrlProvider?.((sid: string) => {
        let cached = blobUrlCache.get(sid);
        if (!cached) {
          cached = (async () => {
            setSiteModelLoad({ state: 'loading', received: 0, total: 0 });
            let res: Response;
            try {
              res = await fetch(
                `/api/site-model?api_base=${encodeURIComponent(apiBase)}&site=${encodeURIComponent(sid)}`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
            } catch (e: any) {
              setSiteModelLoad({ state: 'error', message: `Verbindung abgebrochen (${e?.message ?? 'unbekannt'})` });
              throw e;
            }
            if (!res.ok) {
              setSiteModelLoad({ state: 'error', message: `Server antwortete mit HTTP ${res.status}` });
              throw new Error(`Site-Modell nicht ladbar (HTTP ${res.status})`);
            }
            const total = Number(res.headers.get('content-length')) || 0;
            const reader = res.body?.getReader();
            // Ohne Body-Reader (sehr alte Browser) bleibt nur der Blob am Stück —
            // dann eben ohne Prozentanzeige statt gar nicht.
            if (!reader) {
              const blob = await res.blob();
              setSiteModelLoad(null);
              return URL.createObjectURL(blob);
            }
            const chunks: Uint8Array[] = [];
            let received = 0;
            /* Drosseln, sonst wären das bei ~64-KB-Chunks über achthundert
             * State-Updates für einen einzigen Download. Bei bekannter Gesamtgröße
             * pro ganzem Prozent, sonst pro angefangenem halben Megabyte — ein
             * konstanter Sentinel für "Größe unbekannt" wäre nie verschieden vom
             * Startwert, der Zähler stünde dann für immer auf 0,0 MB. */
            let lastTick = -1;
            try {
              for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value);
                received += value.length;
                const tick = total
                  ? Math.floor((received / total) * 100)
                  : Math.floor(received / 524_288);
                if (tick !== lastTick) {
                  lastTick = tick;
                  setSiteModelLoad({ state: 'loading', received, total });
                }
              }
            } catch (e: any) {
              setSiteModelLoad({ state: 'error', message: `Übertragung abgebrochen (${e?.message ?? 'unbekannt'})` });
              throw e;
            }
            setSiteModelLoad(null);
            return URL.createObjectURL(new Blob(chunks as BlobPart[], { type: 'model/gltf-binary' }));
          })();
          cached.catch(() => blobUrlCache.delete(sid));
          blobUrlCache.set(sid, cached);
        }
        return cached;
      });

      // Gespeicherte House-Config laden (bisheriges Verhalten)
      let objects: any[] = [];
      try {
        const proxyUrl = `/api/twin?api_base=${encodeURIComponent(apiBase)}&path=${encodeURIComponent('/api/house-config')}`;
        const res = await fetch(proxyUrl, { headers: { Authorization: `Bearer ${token}` } });
        const data = res.ok ? await res.json().catch(() => null) : null;
        if (Array.isArray(data?.objects)) objects = data.objects;
      } catch { /* keine gespeicherte Config — leere Szene */ }

      // ?site= → generiertes Kundenhaus automatisch einfügen, falls das Modell
      // fertig ist und die Szene es nicht schon enthält.
      if (siteId && !objects.some(o => o?.cfg?.source === 'site-model' && o?.cfg?.siteId === siteId)) {
        try {
          const statusUrl = `/api/twin?api_base=${encodeURIComponent(apiBase)}&path=${encodeURIComponent(`/api/sites/${siteId}/model`)}`;
          const res = await fetch(statusUrl, { headers: { Authorization: `Bearer ${token}` } });
          const model = res.ok ? await res.json().catch(() => null) : null;
          if (model?.status === 'SUCCEEDED') {
            objects = [...objects, {
              kind: 'house',
              label: 'Kundenhaus',
              cfg: {
                kind: 'house',
                source: 'site-model',
                siteId,
                label: 'Kundenhaus',
                fitSize: 90,
                preserveMaterials: true,
                groundSnap: true,
              },
              rotDeg: 0,
              position: { x: 0, y: 0, z: 0 },
            }];
          } else {
            console.warn(`Site-Hausmodell noch nicht bereit (Status: ${model?.status ?? res.status})`);
          }
        } catch (err) {
          console.warn('Site-Hausmodell-Status nicht abrufbar:', err);
        }
      }

      if (cancelled) return;
      /* Auch OHNE Objekte in den Builder wechseln. Sonst bleibt three.js auf
       * houseMode 'multi' (Demo-Siedlung) stehen, während React für Twin-Nutzer
       * auf 'single' zurückfällt: die Moduswahl-Leiste markierte dann
       * "Einzelhaus" über einer fremden Demo-Siedlung, und der Klick darauf war
       * wirkungslos (mode === sceneMode). Eine leere Baufläche ist ehrlicher —
       * und sofort benutzbar, um Hindernisse und Tracker zu setzen. */
      ctrl.enterBuilderMode?.();
      setSceneMode('builder');
      if (objects.length === 0) return;
      ctrl.loadBuilderSnapshot?.(objects);
    })();
    return () => { cancelled = true; };
  }, [trackerApiState.status]);

  /* ==================== 💾 MEINE MODELLE (eigene, dauerhafte Speicherstände) ====================
   * Getrennt vom Haus-Config-Flow oben (der läuft über den Digital-Twin-JWT
   * und /api/twin gegen das andere Backend). Das hier ist die eigene
   * SQLite-Persistenz für jeden normal eingeloggten Web-UI-User.
   */
  type MyModelSummary = { id: number; name: string; createdAt: string; updatedAt: string };

  const [myModels, setMyModels] = useState<MyModelSummary[]>([]);
  const [myModelsOpen, setMyModelsOpen] = useState(false);
  const [myModelsStatus, setMyModelsStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [myModelsError, setMyModelsError] = useState<string | null>(null);
  // Datei-Import/Export einer Builder-Szene: erlaubt, eine in einer anderen
  // Umgebung (z.B. lokal) gebaute Szene in prod hochzuladen, ohne sie
  // nachzubauen oder direkt an der DB zu hantieren.
  const [importDragOver, setImportDragOver] = useState(false);
  const importInputRef = useRef<HTMLInputElement | null>(null);

  const refreshMyModels = async () => {
    try {
      const res = await fetch(apiUrl('/api/me/models'), { credentials: 'include' });
      if (!res.ok) { setMyModelsError('Modelle konnten nicht geladen werden.'); return; }
      const data = await res.json().catch(() => null);
      setMyModels(Array.isArray(data?.models) ? data.models : []);
      setMyModelsError(null);
    } catch {
      setMyModelsError('Server nicht erreichbar.');
    }
  };

  // Nur für normale Web-UI-Logins (nicht den Digital-Twin-JWT-Flow), sobald
  // die Session bestätigt ist — damit "beim nächsten Login wieder abrufen" greift.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (authed && !isDigitalTwin) refreshMyModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, isDigitalTwin]);

  // Lädt die Benutzerliste, sobald der Admin den "Benutzer"-Tab im Admin-Modal öffnet.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (adminOpen && adminTab === 'users') refreshAdminUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminOpen, adminTab]);

  // Lädt die Login-Versuche, sobald der Admin den "Logins"-Tab öffnet.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (adminOpen && adminTab === 'logins') fetchLoginAttemptsFiltered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminOpen, adminTab]);

  /* ==================== 🏛 INTRO-DORF (öffentliches Admin-Modell) ====================
   * Lädt beim App-Start (auch VOR dem Login) das zuletzt gespeicherte Modell
   * eines Admins über den öffentlichen Endpoint und zeigt es als eigene
   * "Dorf"-View — gleichzeitig die Kulisse für den Flythrough hinterm Splash.
   * Weiterbearbeiten geht ganz normal: Self Build nutzt dieselbe Szene; nach
   * erneutem Speichern ist der neue Stand beim nächsten Laden das Intro.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // isTwinDeepLink zuerst: isDigitalTwin ist beim Mount noch false (Status
    // 'idle'), der Guard griff also zu spät und das Dorf war schon unterwegs.
    if (isTwinDeepLink || isDigitalTwin) return; // Twin-Flow lädt stattdessen die House-Config
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/public/intro-model'));
        if (!res.ok) return;
        const data = await res.json().catch(() => null);
        const sceneData = data?.model?.sceneData;
        if (cancelled || !Array.isArray(sceneData) || sceneData.length === 0) return;
        // Warten, bis die 3D-Szene initialisiert ist (max. ~15s)
        for (let i = 0; i < 60 && !(controlsRef.current as any)?.loadBuilderSnapshot; i++) {
          if (cancelled) return;
          await new Promise(r => setTimeout(r, 250));
        }
        const ctrl = controlsRef.current as any;
        if (cancelled || !ctrl?.loadBuilderSnapshot || introModelLoadedRef.current) return;
        introModelLoadedRef.current = true;
        ctrl.enterBuilderMode?.();
        ctrl.loadBuilderSnapshot?.(sceneData);
        ctrl.focusAmbientOnBuilder?.();
        // Kamera-Regie aus den Dorf-Landmarken: Straßen-Preview + Tor-Marktplatz-Intro.
        // Fehlen die Landmarken (Tor/Brunnen/Stände), bleiben Bbox-Orbit und Standard-Intro.
        const paths = computeVillageCameraPaths(sceneData);
        if (paths) {
          if (paths.ambientPts) ctrl.setAmbientPath?.(paths.ambientPts, 40000);
          ctrl.setIntroFlightPath?.(paths.introPts, paths.introEndLook, 11000);
        }
        energyTracker.current?.setOccluders?.(ctrl.getBuilderOccluderMeshes?.() ?? []);
        setSceneMode('village');
        setIntroModelAvailable(true);
      } catch { /* Intro ist optional — ohne Modell bleibt alles wie bisher */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDigitalTwin]);

  const saveCurrentAsMyModel = async () => {
    const ctrl = controlsRef.current as any;
    if (!ctrl?.getBuilderSnapshot) return;
    const name = window.prompt('Name für dieses Modell:', `Modell ${new Date().toLocaleString('de-DE')}`);
    if (!name) return;
    const sceneData = ctrl.getBuilderSnapshot();
    setMyModelsStatus('saving');
    try {
      const res = await fetch(apiUrl('/api/me/models'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, sceneData }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        setMyModelsStatus('error');
        setMyModelsError(j?.error || 'Speichern fehlgeschlagen.');
      } else {
        setMyModelsStatus('saved');
        setMyModelsError(null);
        await refreshMyModels();
      }
    } catch {
      setMyModelsStatus('error');
      setMyModelsError('Server nicht erreichbar.');
    } finally {
      setTimeout(() => setMyModelsStatus('idle'), 3000);
    }
  };

  const loadMyModel = async (id: number) => {
    setMyModelsError(null);
    try {
      const res = await fetch(apiUrl(`/api/me/models/${id}`), { credentials: 'include' });
      if (!res.ok) { setMyModelsError('Modell konnte nicht geladen werden.'); return; }
      const data = await res.json().catch(() => null);
      const sceneData = data?.model?.sceneData;
      // Corrupt/unerwartete Antwort niemals an den 3D-Loader weiterreichen.
      if (!Array.isArray(sceneData)) {
        setMyModelsError('Dieses Modell ist beschädigt und kann nicht geladen werden.');
        return;
      }
      try {
        const ctrl = controlsRef.current as any;
        ctrl?.enterBuilderMode?.();
        ctrl?.loadBuilderSnapshot?.(sceneData);
        setMyModelsOpen(false);
      } catch {
        // z.B. fehlende/kaputte Felder in einzelnen Objekten -> nicht die App crashen lassen.
        setMyModelsError('Dieses Modell ist beschädigt und konnte nicht vollständig geladen werden.');
      }
    } catch {
      setMyModelsError('Server nicht erreichbar.');
    }
  };

  const deleteMyModel = async (id: number) => {
    if (!window.confirm('Dieses Modell wirklich löschen?')) return;
    try {
      const res = await fetch(apiUrl(`/api/me/models/${id}`), { method: 'DELETE', credentials: 'include' });
      if (!res.ok) { setMyModelsError('Löschen fehlgeschlagen.'); return; }
      await refreshMyModels();
    } catch {
      setMyModelsError('Server nicht erreichbar.');
    }
  };

  /* ---------- Szene als Datei exportieren / importieren ----------
   * Damit lässt sich eine fertig gebaute Szene aus einer Umgebung in eine
   * andere tragen (lokal gebautes Intro-Dorf -> prod), ohne SQL und ohne
   * Nachbauen: hier exportieren, drüben als Admin einloggen, reinziehen,
   * "Aktuelles Modell speichern". */

  // Muss zu MAX_SCENE_DATA_BYTES in src/routes/userModels.mjs passen — sonst
  // akzeptiert der Import eine Datei, die der Server beim Speichern mit 413
  // ablehnt, und der Nutzer verliert die Arbeit erst nach dem Platzieren.
  const MAX_SCENE_BYTES = 2 * 1024 * 1024;

  /** Gibt die Szene zurück, oder wirft mit einer anzeigbaren Meldung. Absichtlich
   *  streng: alles hier landet ungeprüft im 3D-Loader. */
  const parseSceneFileText = (text: string) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new Error('Keine gültige JSON-Datei.');
    }
    // Beide Formen akzeptieren: das nackte Array (so exportieren wir) und den
    // API-Payload { name, sceneData } — man hat sonst schnell das falsche File.
    const scene = Array.isArray(parsed)
      ? parsed
      : (parsed && typeof parsed === 'object' && Array.isArray((parsed as any).sceneData))
        ? (parsed as any).sceneData
        : null;
    if (!scene) throw new Error('Datei enthält keine Szene (erwartet: JSON-Array oder { sceneData: [...] }).');
    if (scene.length === 0) throw new Error('Die Szene in dieser Datei ist leer.');

    const ok = scene.every((o: any) =>
      o && typeof o === 'object' &&
      typeof o.kind === 'string' &&
      o.cfg && typeof o.cfg === 'object' &&
      o.position && ['x', 'y', 'z'].every((k) => Number.isFinite(o.position[k]))
    );
    if (!ok) throw new Error('Die Szene enthält unvollständige Objekte und wurde nicht geladen.');

    return scene;
  };

  const exportCurrentSceneFile = () => {
    const ctrl = controlsRef.current as any;
    if (!ctrl?.getBuilderSnapshot) return;
    const scene = ctrl.getBuilderSnapshot();
    if (!Array.isArray(scene) || scene.length === 0) {
      setMyModelsError('Nichts zu exportieren — die Szene ist leer.');
      return;
    }
    setMyModelsError(null);
    const blob = new Blob([JSON.stringify(scene)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `szene-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const importSceneFile = async (file: File | null | undefined) => {
    if (!file) return;
    setMyModelsError(null);
    if (file.size > MAX_SCENE_BYTES) {
      setMyModelsError(`Datei zu groß (${Math.round(file.size / 1024)} KB, max ${MAX_SCENE_BYTES / 1024 / 1024} MB).`);
      return;
    }
    let scene: any[];
    try {
      scene = parseSceneFileText(await file.text());
    } catch (err: any) {
      setMyModelsError(err?.message || 'Datei konnte nicht gelesen werden.');
      return;
    }
    try {
      const ctrl = controlsRef.current as any;
      ctrl?.enterBuilderMode?.();
      ctrl?.loadBuilderSnapshot?.(scene);
      // Panel offen lassen: der nächste Schritt ist fast immer "Aktuelles
      // Modell speichern", und der Button sitzt direkt darüber.
    } catch {
      setMyModelsError('Die Szene konnte nicht vollständig geladen werden.');
    }
  };

  /* ==================== 🔁 MODUS-TOGGLE (Ideal/Realistisch) ==================== */
  const [isRealistic, setIsRealistic] = useState(false);
  const [shadingEnabled, setShadingEnabled] = useState(true);
  const applyEnergyMode = (realistic: boolean, shading = shadingEnabled) => {
    const et = energyTracker.current; if (!et) return;
    if (realistic) {
      et.setFlags({ geometry:true, clearSky:true, clouds:true, iam:true, temperature:true, shading, soiling:true, pr:true, clipping:false, pushZeroAtNight:false });
      et.setParams({ debug:false, cloudiness01:0, iam_b0:0.05, gamma_Pmp_perK:-0.0045, NOCT:45, soilingLoss01:0.03, PR_misc:0.92, inverterLimitKW:null, ambientTempC:25 });
    } else {
      et.setFlags({ geometry:true, clearSky:false, clouds:false, iam:false, temperature:false, shading, soiling:false, pr:false, clipping:false, pushZeroAtNight:false });
      et.setParams({ debug:false, cloudiness01:0, iam_b0:0, gamma_Pmp_perK:0, NOCT:45, soilingLoss01:0, PR_misc:1, inverterLimitKW:null, ambientTempC:25 });
    }
  };
  const clearEnergyTotalsOnly = () => {
    trackerMapRef.current.forEach((p) => { if (!p) return; p.energyDay = 0; p.energyTotal = 0; p.energyHistory = []; });
    (energyTracker.current as any)?.resetAllTotals?.();
    recomputeMatrixFromPanels(mountFilterRef.current, true);
  };
  const refreshEnergyAfterSettingsChange = () => {
    clearEnergyTotalsOnly();
    const coords = readSafeCoords();
    sunTrackerRef.current?.update(date, coords.lat, coords.lon);
    updateMoonPosition(date, coords.lat, coords.lon);
    recomputeMatrixFromPanels(mountFilterRef.current);
  };
  const toggleEnergyMode = () => {
    const next = !isRealistic;
    setIsRealistic(next);
    applyEnergyMode(next, shadingEnabled);
    refreshEnergyAfterSettingsChange();
  };
  const toggleShading = () => {
    const next = !shadingEnabled;
    setShadingEnabled(next);
    applyEnergyMode(isRealistic, next);
    refreshEnergyAfterSettingsChange();
  };

  const resetSimulation = (resume = false) => {
    log('🔄 Reset simulation');
    setIsRunning(false);

    // Reset panel data and clear associated graphical elements
    trackerMapRef.current.forEach((panel) => {
      if (!panel) return;

      // Reset relevant panel data
      if ('energyTotal' in panel) panel.energyTotal = 0;
      if ('energyDay' in panel) panel.energyDay = 0;
      if ('history' in panel && Array.isArray(panel.history)) panel.history = [];
      if ('energyHistory' in panel && Array.isArray(panel.energyHistory)) panel.energyHistory = [];
      if ('lastUpdateTs' in panel) panel.lastUpdateTs = undefined;

    });

    // Reset energy tracker and clear all energy-related data
    energyTracker.current?.resetAllTotals?.();

    // Force-redraw all PanelInfo canvases so old graph lines disappear
    panelInfoMap.forEach((panelInfo, name) => {
      const panel = trackerMapRef.current.get(name);
      if (panel) panelInfo.update(panel, true);
    });

    // Clear energy data displayed in the panel
    setPanelEnergyData(new Map());

    // Recompute matrix to reflect the reset state
    recomputeMatrixFromPanels(mountFilterRef.current, true);

    // Reset simulation dates and end date
    const d = new Date(initialDateRef.current);
    sunTrackerRef.current?.resetLastUpdateTime(d);
    setDate(d);
    setLastSimDay(getZonedParts(d, locationTimezone).day);
    setEndDate(null);

    // Reset the reset button state
    setResetActive(true);
    setTimeout(() => setResetActive(false), 500);

    if (resume) setIsRunning(true);
  };

  const startFastForward = () => {
    if (forwardTimerRef.current != null) return;
    forwardHeldTickedRef.current = false;
    setForwardActive(true);
    forwardTimerRef.current = window.setInterval(() => {
      setDate((prev) => {
        // Use the same simulation step as the normal clock so energy totals
        // and PanelInfo curves stay deterministic while fast-forwarding.
        const step = intervalMs;
        const next = new Date(prev.getTime() + step);
        if (endDate && next > endDate) {
          setIsRunning(false);
          if (forwardTimerRef.current) { clearInterval(forwardTimerRef.current); forwardTimerRef.current = null; }
          setForwardActive(false);
          return prev;
        }
        forwardHeldTickedRef.current = true;
        return next;
      });
    }, 60);
  };
  const stopFastForward = () => {
    if (forwardTimerRef.current != null) { clearInterval(forwardTimerRef.current); forwardTimerRef.current = null; }
    setForwardActive(false);
  };
  useEffect(() => () => stopFastForward(), []);

  /* Sonne / Simulationstakt */
  // Eigenständig von der Sonnen-Logik (SunTracker.ts bleibt unangetastet): echte Mondposition
  // über SunCalc, dieselbe Nordausrichtung/Spiegelung wie die Sonne, damit beide im selben
  // Szenen-Koordinatensystem sitzen. Radius bewusst anders als die Sonne (3500 statt 4000),
  // rein damit sie bei zufälliger Konjunktion nicht exakt ineinander clippen.
  const updateMoonPosition = (d: Date, lat: number, lon: number) => {
    if (!moonSphereRef.current) return;
    const { azimuth, altitude } = SunCalc.getMoonPosition(d, lat, lon);
    const azFromNorthDeg = (azimuth * 180 / Math.PI + 180) % 360;
    const altDeg = altitude * 180 / Math.PI;
    const p = moonPositionFromAzEl(azFromNorthDeg, altDeg, 3500, WORLD.NORTH_OFFSET_DEG, WORLD.MIRROR_EW);
    moonSphereRef.current.position.copy(p);
    // Mondphase: Shader-Uniform auf die aktuelle Szenen-Sonnenrichtung setzen.
    // Beide Himmelskörper stehen astronomisch korrekt → der Winkel dazwischen
    // ist die echte Elongation, die Phase stimmt für jedes Simulationsdatum.
    if (sunSphereRef.current) setMoonSunDirection(moonSphereRef.current, sunSphereRef.current.position);
  };

  const updateSunPosition = () => {
    const currentDay = getZonedParts(date, locationTimezone).day;
    if (currentDay !== lastSimDay) {
      trackerMapRef.current.forEach((panel) => { if (typeof (panel as any).energyDay === 'number') (panel as any).energyDay = 0; });
      setLastSimDay(currentDay);
    }
    const coords = readSafeCoords();
    // `date` is the real UTC instant for the selected location's wall-clock time.
    const sunPos = SunCalc.getPosition(date, coords.lat, coords.lon);
    const altDeg = Number((sunPos.altitude * 180 / Math.PI).toFixed(2));
    const azFromNorth = (sunPos.azimuth * 180 / Math.PI + 180) % 360;
    setAltitude(altDeg); setAzimuth(Number(azFromNorth.toFixed(2)));
    sunTrackerRef.current?.update(date, coords.lat, coords.lon);
    updateMoonPosition(date, coords.lat, coords.lon);

// --- Auto-Day/Night Umschaltung mit Hysterese ---
const DAY_ON_DEG   = 8;   // >8° → sicher Tag
const NIGHT_ON_DEG = -3;  // <−3° → sicher Nacht (unter Horizont)
const lastAutoPresetRef = (window as any).__lastAutoPresetRef ??= { cur: 'day' as 'day'|'night'|'aurora'|'galaxy' };

if (lastAutoPresetRef.cur !== 'aurora' && lastAutoPresetRef.cur !== 'galaxy') {
  if (altDeg > DAY_ON_DEG && lastAutoPresetRef.cur !== 'day' ) {
    controlsRef.current?.setSkyPreset?.('day');
    lastAutoPresetRef.cur = 'day';
    applyHouseNightMode(false);
  } else if (altDeg < NIGHT_ON_DEG && lastAutoPresetRef.cur !== 'night') {
    controlsRef.current?.setSkyPreset?.('night');
    lastAutoPresetRef.cur = 'night';
    applyHouseNightMode(true);
  }
}

    recomputeMatrixFromPanels(mountFilterRef.current);
  };

// sammelt alle Haus-Meshes zum Umschalten
const houseMeshesRef = useRef<THREE.Mesh[]>([]);

// Umschalter
function applyHouseNightMode(night: boolean) {
  for (const mesh of houseMeshesRef.current) {
    mesh.material = night ? mesh.userData.__nightMat : mesh.userData.__dayMat;
  }
}


  /* Setup */
  useEffect(() => {
    const cleanup = setupScene({ mountRef, sunLightRef, sunSphereRef, moonSphereRef, controlsRef, updateSunPosition, trackerMap: trackerMapRef.current, panelInfoMap, renderPausedRef, wallMeshRef,
      onAssetProgress: (loaded, total) => setAssetProgress({ loaded, total }) })
    // 20 Sekunden NACH Intro ein Info-Popup anzeigen:
       if (typeof cleanup !== 'function') {
         console.warn('Expected cleanup function, but got:', typeof cleanup);
       }
    if (controlsRef.current) {
        const sceneControls = controlsRef.current as any;
        sceneControls.onBuilderObjectsChanged = (objects: BuilderPlacedObject[]) => {
          setBuilderObjects(objects);
          setBuilderPlacedCount(objects.length);
          if (projectMode) window.dispatchEvent(new Event('project-dimensions-changed'));
          // Keep EnergyTracker occluders in sync with builder-placed objects
          const builderMeshes: THREE.Mesh[] = sceneControls.getBuilderOccluderMeshes?.() ?? [];
          energyTracker.current?.setOccluders(builderMeshes);
        };
        sceneControls.onBuilderSelectionChanged = (id: string | null) => {
          setSelectedBuilderObjectId(id);
          if (projectMode) window.dispatchEvent(new Event('project-dimensions-changed'));
          setSelectedTransform(withScaleRange(sceneControls.getSelectedBuilderTransform?.() ?? null));
        };
        sceneControls.onBuilderMoveFinished = () => {
          setBuilderMovingSelected(false);
        };
        // 3D-Klick auf einen Tracker (Info-Karte auf) -> Zeile in der Bestenliste highlighten
        sceneControls.onPanelClicked = (name: string | null) => {
          setHighlightedTrackerName(name);
        };
        const initialBuilderObjects = sceneControls.getBuilderObjects?.() ?? [];
        setBuilderObjects(initialBuilderObjects);
        setBuilderPlacedCount(initialBuilderObjects.length);

        sceneControls.onIntroFinished = () => {
          // Beim Wiederholen des Flugs (Button in der Steuerleiste) ist dies
          // KEIN Erstkontakt: kein Info-Popup, und der Lauf-/Pausenzustand von
          // vorher wird wiederhergestellt statt pauschal gestartet.
          const replay = introReplayRef.current;
          introReplayRef.current = null;

          // optional: dein Info-Popup-Timer (bleibt wie gehabt)
          if (introInfoTimerRef.current) clearTimeout(introInfoTimerRef.current);
          if (!replay) {
            introInfoTimerRef.current = window.setTimeout(() => {
              setShowIntroInfo(true);
              introInfoTimerRef.current = null;
            }, 20000);
          }

          // JETZT erst die Simulation (Zeitfortschritt) starten
          setIntroPlaying(false);
          computeCameraHeading();
          setIsRunning(replay ? replay.wasRunning : true);
        };
      }


    energyTracker.current = new EnergyTracker(
      trackerMapRef.current, 1440, panelInfoMap,
      { geometry:true, clearSky:false, clouds:false, iam:false, temperature:false, shading:true, soiling:false, pr:false, clipping:false, pushZeroAtNight:false },
      { debug:false, cloudiness01:0, iam_b0:0, gamma_Pmp_perK:0, NOCT:45, soilingLoss01:0, PR_misc:1, inverterLimitKW:null, ambientTempC:25 }
    );
    energyTracker.current.setOccluders((controlsRef.current as any)?.getBuilderOccluderMeshes?.() ?? []);

    sunTrackerRef.current = new SunTracker(
      trackerMapRef.current, sunLightRef, sunSphereRef, energyTracker.current,
      { worldNorthOffsetDeg: WORLD.NORTH_OFFSET_DEG, radius: 4000, mirrorEastWest: WORLD.MIRROR_EW,
        limitsByType: { unlimited:{coneFrontDeg:90,coneBackDeg:0,coneLeftDeg:60,coneRightDeg:60}, limited:{coneFrontDeg:30,coneBackDeg:0,coneLeftDeg:30,coneRightDeg:30}, single:{singlePitchPosDeg:90,singlePitchNegDeg:30} } }
    );

    // Wall mesh is a manual obstacle — keep it hidden, no auto-occluder
    if (wallMeshRef.current) wallMeshRef.current.visible = false;

    applyEnergyMode(false);
    recomputeMatrixFromPanels(mountFilterRef.current);

    const controls = controlsRef.current;
    const onChange = () => computeCameraHeading();
    controls?.addEventListener?.('change', onChange);
    computeCameraHeading();
    // Ambienter Kamera-Flythrough startet selbstständig in setupScene, sobald die Häuser geladen sind
    // (statt sofort auf leerem Terrain loszufahren) und läuft in Endlosschleife bis der Sim-Start.

    return () => {
     controls?.removeEventListener?.('change', onChange);
     cleanup && cleanup();
     if (introInfoTimerRef.current) {
       clearTimeout(introInfoTimerRef.current);
       introInfoTimerRef.current = null;
     }
     if (controls) {
       (controls as any).onIntroFinished = undefined;
       (controls as any).onBuilderObjectsChanged = undefined;
       (controls as any).onBuilderSelectionChanged = undefined;
       (controls as any).onBuilderMoveFinished = undefined;
     }
      };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Zeitraffer-Clock
  useEffect(() => {
    if (!isRunning || forwardActive) return;
    const timer = setInterval(() => {
      setDate((prev) => {
        const next = new Date(prev.getTime() + intervalMs);
        if (endDate && next > endDate) { setIsRunning(false); return prev; }
        return next;
      });
    }, 500);
    return () => clearInterval(timer);
  }, [isRunning, intervalMs, endDate, forwardActive]);

  // Jeder Tick
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { updateSunPosition(); }, [date, lat, lon, locationTimezone]);

  // Bei Filterwechsel neu berechnen
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { recomputeMatrixFromPanels(mountFilter); }, [mountFilter]);

  /* ======= UI/UX FIXES: Vollbild & Pointer-Events ======= */

  // HUD sichtbar/unsichtbar — bewusst true: sauberer erster Eindruck der 3D-Szene ohne Panels
  const [hudHidden, setHudHidden] = useState(true);

  // HUD-Wrapper: nimmt NIE Pointer-Events an (damit OrbitControls immer funktionieren)
  const hudWrapperStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0, left: 0, width: '100%', height: '100%',
    zIndex: 10,
    opacity: hudHidden ? 0 : 1,
    pointerEvents: 'none',
    transition: 'opacity 180ms ease',
  };

  // Interaktive HUD-Blöcke bekommen Pointer-Events lokal zurück
  const hudBlockPE: React.CSSProperties = { pointerEvents: hudHidden ? 'none' : 'auto' };

  const toggleFullscreen = async () => {
    const el = projectMode ? mountRef.current?.closest('.project-workspace') : mountRef.current?.parentElement ?? mountRef.current;
    if (!document.fullscreenElement) await (el as any)?.requestFullscreen?.();
    else await document.exitFullscreen?.();
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  };
  // 1) Helper ergänzen (z. B. neben toggleFullscreen)
  const tryFullscreenNow = async () => {
    const el = projectMode ? mountRef.current?.closest('.project-workspace') : mountRef.current?.parentElement ?? mountRef.current ?? document.documentElement;
    if (!el || document.fullscreenElement) return;
    try {
      if ((el as any).requestFullscreen) {
        await (el as any).requestFullscreen({ navigationUI: 'hide' });
      } else if ((el as any).webkitRequestFullscreen) {
        (el as any).webkitRequestFullscreen(); // Safari
      } else if ((el as any).msRequestFullscreen) {
        (el as any).msRequestFullscreen();     // älter
      }
    } catch (err) {
      warn('Fullscreen denied:', err);
    } finally {
      setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
    }
  };



  // Vollbildwechsel & Resize → Renderer neu siz(en)
  useEffect(() => {
    const onFs = () => setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Reagiere auf Container-Größe (auch im Vollbild)
  useEffect(() => {
    if (!mountRef.current) return;
    const ro = new ResizeObserver(() => {
      window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    });
    ro.observe(mountRef.current);
    return () => ro.disconnect();
  }, []);

// GENAU & SCHNELL: deterministische Bereichs-Simulation
           // Normaler Timer




const captureProjectInputs = () => ({
  latitude: lat, longitude: lon, timezone: locationTimezone, location_label: location,
  engine: 'RayAlign EnergyTracker browser v1',
  scene_metrics: controlsRef.current?.getProjectMetrics?.(),
  power_kw_per_standard_tracker: energyTracker.current?.powerKW,
  flags: { ...energyTracker.current?.flags }, parameters: { ...energyTracker.current?.params },
  note: 'Standort- und zeitbezogene Ertragsschätzung aus der RayAlign-Simulation; Wetter- und Verlustannahmen gemäß Einstellungen',
});
const restoreProjectInputs = (inputs: any) => {
  projectLocationReady.current=Number.isFinite(inputs.latitude)&&Number.isFinite(inputs.longitude);
  controlsRef.current?.setProjectMetrics?.(inputs.scene_metrics||{});
  if (Number.isFinite(inputs.latitude)) setLat(inputs.latitude);
  if (Number.isFinite(inputs.longitude)) setLon(inputs.longitude);
  if (inputs.timezone) {projectTimezoneRef.current=inputs.timezone;setLocationTimezone(inputs.timezone);}
  if(inputs.location_label){skipNextSuggestionFetchRef.current=true;setLocation(inputs.location_label);setSuggestions([]);}
  if (inputs.flags && energyTracker.current) energyTracker.current.flags = { ...energyTracker.current.flags, ...inputs.flags };
  if (inputs.parameters) energyTracker.current?.setParams(inputs.parameters);
  if (Number.isFinite(inputs.power_kw_per_standard_tracker)) energyTracker.current?.setPowerKW(inputs.power_kw_per_standard_tracker);
  if(inputs.flags)setIsRealistic(!!inputs.flags.clearSky);
  if(energyTracker.current)energyTracker.current.flags.shading=true;setShadingEnabled(true);
};

// Funktion für den Start der exakten Simulation (läuft auch ohne Fast-Sim)
async function runRangeSimExact(opts: {
  from: Date;
  to: Date;
  dtMinute?: number;
  twilightMinute?: number;
  includeCivilTwilight?: boolean;
  quiet?: boolean;
}) {
  let st = sunTrackerRef.current;
  let et = energyTracker.current;
  if (!st || !et) return;
  if(projectMode){
    const panels=new Map([...trackerMapRef.current].filter(([name])=>name.startsWith('b_tracker_')));
    et=new EnergyTracker(panels,et.powerKW,new Map(),{...et.flags},{...et.params});
    et.setOccluders(controlsRef.current?.getBuilderOccluderMeshes?.()||[]);
    st=st.forPanels(panels,et);
  }
  if (!isValidDate(opts.from) || !isValidDate(opts.to) || opts.to < opts.from) {
    warn('Invalid range simulation dates ignored:', opts.from, opts.to);
    return;
  }

  if (projectMode && controlsRef.current?.isBuilderLoading?.()) { alert('Das Kundenmodell wird noch geladen.'); return; }
  const projectScene = projectMode ? JSON.stringify(controlsRef.current?.getBuilderSnapshot?.() || []) : '';
  const projectInputs = captureProjectInputs();
  if (projectMode) { et.resetAllTotals(); projectCalculation.current = null; }
  const projectDaily: {date:string;energy_kwh:number}[] = [];
  let previousProjectEnergy = 0;
  const recordProjectDay = (at: Date) => {
    if (!projectMode) return;
    const parts = getZonedParts(at, locationTimezone);
    const day = `${parts.year}-${String(parts.month).padStart(2,'0')}-${String(parts.day).padStart(2,'0')}`;
    const total = (controlsRef.current?.getProjectTrackerResults?.() || []).reduce((sum:number,r:any)=>sum+r.energy_kwh,0);
    projectDaily.push({date:day,energy_kwh:Math.max(0,total-previousProjectEnergy)});
    previousProjectEnergy=total;
  };
  // --- Parameter ---
  const dtMinute = Math.max(1, Math.round(opts.dtMinute ?? 1));
  const twilightMinute = Math.max(1, Math.round(opts.twilightMinute ?? 1));
  const useTwilight = !!opts.includeCivilTwilight;
  const RAD2DEG = 180 / Math.PI;
  const TWILIGHT_DEG = 2;

  // --- Helper: alle Timer genau auf ts setzen ---
  const setAllClocksTo = (ts: number) => {
    trackerMapRef.current.forEach((p: any) => { p.lastUpdateTs = ts; });
    st.resetLastUpdateTime(ts);
    (et as any)?.resetLastTs?.(ts);
  };

  // --- grobe Fortschritts-Schätzung (für Balken) ---
  const daysTotal = Math.max(1, Math.ceil((+opts.to - +opts.from) / (24 * 3600e3)));
  let daysDone = 0;

  setIsRunning(false);
  stopFastForward();
  setRangeSimBusy(true);
  setRangeProgress(0);
  renderPausedRef.current = true;
  let chunkStartedAt = performance.now();
  let stepsSinceYield = 0;
  const yieldToBrowser = async (progress?: number) => {
    if (typeof progress === 'number') {
      setRangeProgress(Math.max(0, Math.min(1, progress)));
    }
    renderPausedRef.current = false;
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    renderPausedRef.current = true;
    chunkStartedAt = performance.now();
    stepsSinceYield = 0;
  };

  // Startzustand exakt setzen
  setAllClocksTo(+opts.from);

  let cur = new Date(opts.from);
  let lastDay = getZonedParts(cur, locationTimezone).day;

  while (cur < opts.to) {
    if(comparisonRunningRef.current && comparisonAbortRef.current)throw new Error('Vergleich abgebrochen. Kein unvollständiger Bericht gespeichert.');
    // Tageswechsel → Tageszähler (nur Anzeige) zurücksetzen
    const currentLocalDay = getZonedParts(cur, locationTimezone).day;
    if (currentLocalDay !== lastDay) {
      trackerMapRef.current.forEach((p: any) => { if (typeof p.energyDay === 'number') p.energyDay = 0; });
      lastDay = currentLocalDay;
    }

    // Tageszeiten holen
    const coords = readSafeCoords();
    const localDay = getZonedParts(cur, locationTimezone);
    const localNoon = makeDateTimeInTz({ ...localDay, hour: 12, minute: 0, second: 0, millisecond: 0 }, locationTimezone);
    const times = SunCalc.getTimes(localNoon, coords.lat, coords.lon);
    const timesWithCivil = times as typeof times & { civilDawn?: Date; civilDusk?: Date };
    let dayStart = useTwilight ? (timesWithCivil.civilDawn ?? times.dawn ?? times.sunrise) : times.sunrise;
    let dayEnd = useTwilight ? (timesWithCivil.civilDusk ?? times.dusk ?? times.sunset) : times.sunset;

    // At polar sites sunrise/sunset can be absent. Integrate the full local day
    // when the noon sun is above the horizon; polar night remains zero.
    if (projectMode && (!isValidDate(dayStart) || !isValidDate(dayEnd)) && SunCalc.getPosition(localNoon,coords.lat,coords.lon).altitude > 0) {
      dayStart=makeDateTimeInTz({...localDay,hour:0,minute:0,second:0,millisecond:0},locationTimezone);
      dayEnd=nextMidnightInTz(dayStart,locationTimezone);
    }
    // Polar-Nacht/Tag oder fehlende Zeiten absichern
    if (!isValidDate(dayStart) || !isValidDate(dayEnd) || +dayEnd <= +dayStart) {
      recordProjectDay(cur);
      const nextMidnight = nextMidnightInTz(cur, locationTimezone);
      setAllClocksTo(+nextMidnight);
      cur = nextMidnight;
      daysDone++;
      if(performance.now()-chunkStartedAt>30)await yieldToBrowser(Math.min(1, daysDone / daysTotal));
      continue;
    }

    // Range auf gewünschten Zeitraum clippen
    if (dayEnd < opts.from) {
      cur = nextMidnightInTz(cur, locationTimezone);
      setAllClocksTo(+cur);
      continue;
    }
    if (dayStart < opts.from) dayStart = new Date(opts.from);
    if (dayStart > opts.to) break;
    if (dayEnd > opts.to) dayEnd = new Date(opts.to);

    // Nacht → zum Tagesbeginn springen (Timer mitnehmen)
    if (cur < dayStart) {
      setAllClocksTo(+dayStart);
      cur = new Date(dayStart);
    }

    // --- Tagesintegration ---
    let t = new Date(dayStart);
    const end = +dayEnd;

    while (+t <= end) {
      const altDeg = SunCalc.getPosition(t, coords.lat, coords.lon).altitude * RAD2DEG;
      const stepMs = (Math.abs(altDeg) < TWILIGHT_DEG ? twilightMinute : dtMinute) * 60 * 1000;
      st.update(t, coords.lat, coords.lon, { skipVisual: true });
      if(+t===end)break;
      t = new Date(Math.min(end,+t + stepMs));

      stepsSinceYield++;
      if (stepsSinceYield >= 24 && performance.now() - chunkStartedAt > 30) {
        const daySpan = Math.max(1, end - +dayStart);
        const dayProgress = Math.min(1, Math.max(0, (+t - +dayStart) / daySpan));
        await yieldToBrowser(Math.min(1, (daysDone + dayProgress) / daysTotal));
      }
    }

    recordProjectDay(dayStart);
    // Tagesende erreicht → auf Mitternacht springen (Timer mitnehmen)
    const nextMidnight = nextMidnightInTz(dayEnd, locationTimezone);
    setAllClocksTo(+nextMidnight);
    cur = nextMidnight;

    daysDone++;
    if(performance.now()-chunkStartedAt>30)await yieldToBrowser(Math.min(1, daysDone / daysTotal));
  }

  {
    const coords = readSafeCoords();
    st.update(opts.to, coords.lat, coords.lon, { skipVisual: true });
  }
  renderPausedRef.current = false;
  setDate(new Date(opts.to));
  recomputeMatrixFromPanels(mountFilterRef.current);
  setRangeSimBusy(false);
  setRangeProgress(1);

  // Ergebnis berechnen und Popup zeigen
  let totalKWh = 0;
  trackerMapRef.current.forEach((p: any) => { totalKWh += p?.energyTotal ?? 0; });
  const days = Math.max(1, Math.round((opts.to.getTime() - opts.from.getTime()) / 86_400_000));
  if (projectMode) {
    // Only placed project trackers belong to this estimate, never the demonstration rows.
    const trackers: any[] = controlsRef.current?.getProjectTrackerResults?.() || [];
    const months: Record<string,number> = {};
    projectDaily.forEach(day=>{const key=day.date.slice(0,7);months[key]=(months[key]||0)+day.energy_kwh;});
    totalKWh = trackers.reduce((sum, item) => sum + item.energy_kwh, 0);
    projectCalculation.current = {
      sceneSignature: projectScene, inputsSignature: JSON.stringify(projectInputs),
      result: { total_kwh: totalKWh, from: opts.from.toISOString(), to: opts.to.toISOString(), days,
        integration_minutes: dtMinute, twilight_minutes: twilightMinute, include_twilight: useTwilight,
        trackers, daily:projectDaily, monthly:Object.entries(months).map(([month,energy_kwh])=>({month,energy_kwh})), source: 'rayalign_simulation_estimate', assumptions: projectInputs.note }
    };
  }
  setRangeSimResult({ totalKWh, days });
  if(!opts.quiet)setShowRangeSimDone(true);

  pushLog('RangeSim finished');
  return projectCalculation.current?.result;
}


async function runProjectComparison() {
 if(comparisonRunningRef.current||rangeSimBusy)throw new Error('Eine Berechnung läuft bereits.');
 const trackers=[...trackerMapRef.current.values()].filter((p:any)=>p.name.startsWith('b_tracker_')) as any[];
 if(!trackers.length)throw new Error('Zuerst mindestens einen Tracker platzieren.');
 if(!projectLocationReady.current)throw new Error('Für dieses Projekt fehlt der Standort. Bitte zuerst die Adresse in der App bestätigen.');
 if(!energyTracker.current||!sunTrackerRef.current)throw new Error('Simulation noch nicht bereit.');
 energyTracker.current.flags.shading=true;setShadingEnabled(true);
 energyTracker.current.setOccluders(controlsRef.current?.getBuilderOccluderMeshes?.()||[]);
 const original=trackers.map(p=>({p,type:p.type,isParked:p.isParked,parkOnNoSun:p.parkOnNoSun,activeLift:p.activeLift,parkDrop:p.parkDrop,position:p.position.clone(),quaternion:p.quaternion.clone()}));
 const originalDate=new Date(date),sceneSignature=JSON.stringify(controlsRef.current.getBuilderSnapshot());
 const inputsSignature=JSON.stringify(captureProjectInputs());
 comparisonRunningRef.current=true;comparisonAbortRef.current=false;projectCalculation.current=null;setShowRangeSimDone(false);
 try {
  const result=await runUpgradeComparison(async(period,mode)=>{
   if(comparisonAbortRef.current)throw new Error('Vergleich abgebrochen.');
   setComparisonPhase(`${period.id==='year'?'1 Jahr':'6 Jahre'} · ${mode==='fixed'?'Feste Module':'Mit Tracker'}`);
   for(const item of original){
    const {p}=item;p.type=mode==='fixed'?'fixed':item.type==='fixed'?'limited':item.type;
    p.parkOnNoSun=mode==='fixed'?false:item.type==='fixed'?true:item.parkOnNoSun;
    p.isParked=false;p.activeLift=mode==='fixed'?0:item.activeLift;p.parkDrop=mode==='fixed'?0:item.parkDrop;
    p.position.copy(p.basePosition);p.quaternion.copy(item.quaternion);
   }
   const fromParts=period.from.split('-').map(Number),toParts=period.to.split('-').map(Number);
   const value=await runRangeSimExact({from:makeMidnightInTz(fromParts[0],fromParts[1],fromParts[2],locationTimezone),to:makeMidnightInTz(toParts[0],toParts[1],toParts[2],locationTimezone),dtMinute:15,twilightMinute:5,quiet:true});
   if(!value)throw new Error('Die Berechnung konnte nicht gestartet werden.');
   return value;
  });
  if(comparisonAbortRef.current)throw new Error('Vergleich abgebrochen.');
  projectCalculation.current={sceneSignature,inputsSignature,result};
  return result;
 }catch(error){projectCalculation.current=null;throw error;}
 finally {
  for(const item of original){Object.assign(item.p,{type:item.type,isParked:item.isParked,parkOnNoSun:item.parkOnNoSun,activeLift:item.activeLift,parkDrop:item.parkDrop});item.p.position.copy(item.position);item.p.quaternion.copy(item.quaternion);}
  energyTracker.current?.resetAllTotals();sunTrackerRef.current?.resetLastUpdateTime(originalDate);setDate(originalDate);
  comparisonRunningRef.current=false;renderPausedRef.current=false;setRangeSimBusy(false);setComparisonPhase('');
 }
}

const renderSimulationSettingsControls = (variant: 'panel' | 'compass') => {
  if (variant === 'compass') {
    return (
      <>
        <button
          title={isRealistic ? 'Realistische Wetter- und Energie-Berechnung aktiv' : 'Idealer verlustfreier Wettermodus aktiv'}
          onClick={toggleEnergyMode}
          style={{
            marginTop:'8px', padding:'8px 10px', width:'100%',
            borderRadius:10, cursor:'pointer',
            background: isRealistic
              ? 'linear-gradient(135deg, rgba(16,185,129,.28), rgba(59,130,246,.24))'
              : 'linear-gradient(135deg, rgba(250,204,21,.3), rgba(56,189,248,.22))',
            color:'#fff',
            border:'1px solid rgba(255,255,255,.18)'
          }}
        >
          {isRealistic ? '🌤️ Realistisch' : '☀️ Ideal'}
        </button>

        <button
          title={shadingEnabled ? 'Schattenberechnung aktiv' : 'Schattenberechnung aus'}
          onClick={toggleShading}
          style={{
            marginTop:'8px', padding:'8px 10px', width:'100%',
            borderRadius:10, cursor:'pointer',
            background: shadingEnabled
              ? 'linear-gradient(135deg, rgba(100,100,100,.45), rgba(60,60,80,.4))'
              : 'linear-gradient(135deg, rgba(56,189,248,.15), rgba(168,85,247,.12))',
            color:'#fff',
            border: shadingEnabled
              ? '1px solid rgba(180,180,180,.35)'
              : '1px solid rgba(255,255,255,.18)',
          }}
        >
          {shadingEnabled ? '🌑 Schatten AN' : '🌕 Schatten AUS'}
        </button>
      </>
    );
  }

  return (
    <div style={{
      background:'rgba(255,255,255,.05)',
      border:'1px solid rgba(255,255,255,.1)',
      borderRadius:10,
      padding:'10px',
      marginBottom:14,
      display:'grid',
      gap:8,
    }}>
      <div style={{ display:'grid', gridTemplateColumns:'64px 1fr 1fr', gap:6, alignItems:'center' }}>
        <span style={{ fontSize:11, opacity:.62 }}>Wetter</span>
        <button
          title="Idealer verlustfreier Wettermodus"
          onClick={() => {
            if (isRealistic) toggleEnergyMode();
          }}
          style={{
            minHeight:30, borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700,
            border: isRealistic ? '1px solid rgba(255,255,255,.12)' : '1px solid rgba(250,204,21,.45)',
            background: isRealistic ? 'rgba(255,255,255,.06)' : 'linear-gradient(135deg, rgba(250,204,21,.28), rgba(56,189,248,.18))',
            color: isRealistic ? 'rgba(255,255,255,.55)' : '#fff',
          }}
        >
          ☀️ Ideal
        </button>
        <button
          title="Realistischer Wettermodus mit Verlusten"
          onClick={() => {
            if (!isRealistic) toggleEnergyMode();
          }}
          style={{
            minHeight:30, borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700,
            border: isRealistic ? '1px solid rgba(16,185,129,.42)' : '1px solid rgba(255,255,255,.12)',
            background: isRealistic ? 'linear-gradient(135deg, rgba(16,185,129,.28), rgba(59,130,246,.22))' : 'rgba(255,255,255,.06)',
            color: isRealistic ? '#fff' : 'rgba(255,255,255,.55)',
          }}
        >
          🌤️ Real
        </button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'64px 1fr 1fr', gap:6, alignItems:'center' }}>
        <span style={{ fontSize:11, opacity:.62 }}>Schatten</span>
        <button
          title="Schattenberechnung ausschalten"
          onClick={() => {
            if (shadingEnabled) toggleShading();
          }}
          style={{
            minHeight:30, borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700,
            border: shadingEnabled ? '1px solid rgba(255,255,255,.12)' : '1px solid rgba(56,189,248,.38)',
            background: shadingEnabled ? 'rgba(255,255,255,.06)' : 'linear-gradient(135deg, rgba(56,189,248,.18), rgba(168,85,247,.14))',
            color: shadingEnabled ? 'rgba(255,255,255,.55)' : '#fff',
          }}
        >
          AUS
        </button>
        <button
          title="Schattenberechnung einschalten"
          onClick={() => {
            if (!shadingEnabled) toggleShading();
          }}
          style={{
            minHeight:30, borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:700,
            border: shadingEnabled ? '1px solid rgba(180,180,180,.38)' : '1px solid rgba(255,255,255,.12)',
            background: shadingEnabled ? 'linear-gradient(135deg, rgba(100,100,100,.45), rgba(60,60,80,.38))' : 'rgba(255,255,255,.06)',
            color: shadingEnabled ? '#fff' : 'rgba(255,255,255,.55)',
          }}
        >
          AN
        </button>
      </div>
    </div>
  );
};


//simulations zeit anzeigen




return (
  <div className={projectMode ? 'project-workspace' : undefined} style={projectMode ? undefined : { position: 'relative', width: '100vw', height: '100dvh' }}>
    <ProjectPanel controls={controlsRef} capture={captureProjectInputs} restore={restoreProjectInputs} calculation={projectCalculation} enter={() => { setSceneMode('builder'); setActiveTab('builder'); setIsRunning(false); }} runComparison={runProjectComparison} settings={renderSimulationSettingsControls('panel')} plan={()=>{setSceneMode('builder');setActiveTab('builder');setIsRunning(false);controlsRef.current?.enterBuilderMode?.();setProjectToolsOpen(true);setHudHidden(true);}} simulate={()=>{controlsRef.current?.builderCancelPlacing?.();setBuilderPlacing(false);setIsRunning(false);setProjectToolsOpen(false);setHudHidden(true);}} />
    <div className="project-viewport" style={{position:'relative',width:'100%',height:'100%'}}>
    {projectMode && rangeSimBusy && <div role="status" style={{position:'absolute',inset:0,zIndex:10000,display:'grid',placeItems:'center',background:'#071c2580',color:'#fff',fontSize:20}}><div style={{width:'min(420px,90%)',textAlign:'center',fontVariantNumeric:'tabular-nums'}}>{comparisonPhase||'Standortsimulation'}: {Math.round(rangeProgress*100)} %{comparisonRunningRef.current&&<p><button onClick={()=>{comparisonAbortRef.current=true;}}>Berechnung abbrechen</button></p>}</div></div>}
    {/* Mounting Area for 3D Rendering */}
    <div
      ref={mountRef}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        zIndex: 0,
        touchAction: 'none',
      }}
    />

    {/* Authentication Form */}
    <div>


      {/* Display Data Fetch Button After Auth */}
      {authRole === 'admin' && (
        <div>
          <button onClick={handleFetchServerData}>Daten vom Server holen</button>
          {serverData && (
            <div>
              <h3>Serverdaten:</h3>
              <pre>{serverData}</pre>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Sky Mode Button */}
    <button
      onClick={() => controlsRef.current?.cycleSkyPreset?.()}
      title="Cycle sky preset (Day → Aurora → Night)"
      style={{
        padding: '6px 10px',
        borderRadius: 8,
        background: 'rgba(56,189,248,.18)',
        border: '1px solid rgba(56,189,248,.45)',
        color: '#E6F7FF',
        cursor: 'pointer',
      }}
    >
      🌌 Sky
    </button>

    {/* Digital Twin: loading / error overlay */}
    {trackerApiState.status === 'loading' && (
      <div style={{ position:'absolute', top:70, left:'50%', transform:'translateX(-50%)',
        background:'rgba(2,6,23,0.85)', borderRadius:12, padding:'10px 18px',
        color:'#38bdf8', fontSize:13, border:'1px solid rgba(56,189,248,.3)', zIndex:80 }}>
        ⏳ Tracker wird geladen…
      </div>
    )}
    {trackerApiState.status === 'error' && (
      <div style={{ position:'absolute', top:70, left:'50%', transform:'translateX(-50%)',
        background:'rgba(40,0,0,0.9)', borderRadius:12, padding:'10px 18px',
        color:'#f87171', fontSize:13, border:'1px solid rgba(248,113,113,.3)', zIndex:80 }}>
        ⚠️ {trackerApiState.message}
      </div>
    )}

    {/* Splash Screen with Fullscreen / Start Simulation Logic */}
    {showSplash && (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 9999,
          display: 'grid',
          placeItems: 'center',
          // Transparent genug, damit der laufende Kamera-Flythrough im Hintergrund sichtbar bleibt;
          // Vignette oben/unten sorgt trotzdem für Lesbarkeit von Titel und Button.
          background: `
            linear-gradient(180deg, rgba(2,6,10,.6) 0%, rgba(2,6,10,.12) 26%, rgba(2,6,10,.12) 74%, rgba(2,6,10,.65) 100%),
            radial-gradient(60% 50% at 50% 40%, rgba(20,60,70,.18), rgba(0,0,0,0) 70%)
          `,
          opacity: splashFade ? 0 : 1,
          transition: 'opacity 280ms ease',
          pointerEvents: 'auto',
          overflow: 'hidden',
        }}
        onClick={async () => {
          if (!fsDoneRef.current && !document.fullscreenElement) {
            fsDoneRef.current = true;
            setFsDone(true);
            playBeep(660, 90, 0.05);
            await tryFullscreenNow();
          } else {
            if (!authed && !isDigitalTwin) {
              setAuthOpen(true);
              pushLog('Auth canceled');
            } else {
              playBeep(880, 140, 0.06);
              startSimNow();
            }
          }
        }}
      >
        {/* CRT scan sweep */}
        <div style={{
          position: 'absolute', left: 0, right: 0, height: '35%',
          background: 'linear-gradient(180deg, rgba(94,234,212,0) 0%, rgba(94,234,212,.05) 50%, rgba(94,234,212,0) 100%)',
          animation: 'splashScanSweep 6s linear infinite',
          pointerEvents: 'none',
        }} />

        <div style={{
          textAlign: 'center', userSelect: 'none', pointerEvents: 'none',
          width: 'min(720px, 92vw)',
          animation: 'splashFlicker 7s ease-in-out infinite',
        }}>
          <div style={{
            position: 'relative',
            textAlign: 'left',
            background: 'linear-gradient(180deg, rgba(6,14,20,.94), rgba(2,7,10,.97))',
            border: '1px solid rgba(56,189,248,.35)',
            borderRadius: 14,
            overflow: 'hidden',
            boxShadow: '0 30px 80px rgba(0,0,0,.6), 0 0 60px rgba(56,189,248,.12), inset 0 0 0 1px rgba(255,255,255,.03)',
            fontFamily: 'ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace',
          }}>
            {/* terminal title bar */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 14px',
              background: 'rgba(255,255,255,.03)',
              borderBottom: '1px solid rgba(56,189,248,.2)',
            }}>
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5f56', display: 'inline-block' }} />
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ffbd2e', display: 'inline-block' }} />
              <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#27c93f', display: 'inline-block' }} />
              <span style={{ marginLeft: 10, fontSize: 12, letterSpacing: 1, color: 'rgba(148,197,255,.6)' }}>
                solar_tracker@core — bash
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); setSoundOn((s) => !s); }}
                title={soundOn ? 'Sound aus' : 'Sound an'}
                style={{
                  marginLeft: 'auto', pointerEvents: 'auto', cursor: 'pointer',
                  background: 'transparent', border: 'none', color: 'rgba(148,197,255,.6)',
                  fontSize: 13, lineHeight: 1, padding: 4,
                }}
              >
                {soundOn ? '🔊' : '🔇'}
              </button>
            </div>

            {/* terminal body */}
            <div style={{ padding: '22px 26px 26px', fontSize: 14, lineHeight: 1.9, color: '#8ff0d4' }}>
              <div style={{
                fontSize: 'clamp(24px,4.5vw,38px)', fontWeight: 800, letterSpacing: 3,
                color: '#5eead4',
                textShadow: '0 0 18px rgba(94,234,212,.55), 0 0 44px rgba(56,189,248,.25)',
                marginBottom: 4,
              }}>
                SOLAR_TRACKER
              </div>
              <div style={{ color: 'rgba(148,197,255,.5)', fontSize: 12, marginBottom: 18 }}>
                {'// sometimes control is an illusion — sometimes you need illusion to gain control'}
              </div>

              {(() => {
                const assetsDone = assetProgress.loaded >= assetProgress.total;
                const assetPct = Math.round((assetProgress.loaded / Math.max(1, assetProgress.total)) * 100);
                const authDone = authed || isDigitalTwin;
                const lines: Array<{ label: string; status: string; tone: 'ok' | 'pending' | 'progress' }> = [
                  { label: 'booting kernel', status: 'OK', tone: 'ok' },
                  {
                    label: `streaming neighborhood (${assetProgress.loaded}/${assetProgress.total})`,
                    status: assetsDone ? 'OK' : `${assetPct}%`,
                    tone: assetsDone ? 'ok' : 'progress',
                  },
                  { label: 'calibrating sun-position array', status: 'OK', tone: 'ok' },
                  { label: 'establishing telemetry uplink', status: 'OK', tone: 'ok' },
                  { label: 'authenticating operator', status: authDone ? 'OK' : 'PENDING', tone: authDone ? 'ok' : 'pending' },
                ];
                const toneColor = { ok: '#4ade80', pending: '#facc15', progress: '#38bdf8' } as const;
                return lines.map((line, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', gap: 12,
                    opacity: 0, animation: 'splashBootLineIn .4s ease forwards',
                    animationDelay: `${i * 140}ms`,
                  }}>
                    <span><span style={{ color: '#38bdf8' }}>{'>'}</span> {line.label}...</span>
                    <span style={{ color: toneColor[line.tone] }}>[{line.status}]</span>
                  </div>
                ));
              })()}

              <div style={{
                marginTop: 18, color: (authed || isDigitalTwin) ? '#f472b6' : 'rgba(148,197,255,.5)',
                opacity: 0, animation: 'splashBootLineIn .4s ease forwards', animationDelay: '760ms',
              }}>
                {(authed || isDigitalTwin) ? 'welcome back, operator.' : 'awaiting operator authentication...'}
              </div>
              <div style={{
                marginTop: 4, display: 'flex', alignItems: 'center', gap: 6,
                opacity: 0, animation: 'splashBootLineIn .4s ease forwards', animationDelay: '900ms',
              }}>
                <span style={{ color: '#38bdf8' }}>{'>'}</span>
                <span>
                  {!fsDone ? 'press [ENTER] for fullscreen'
                    : (authed || isDigitalTwin) ? 'press [ENTER] to start'
                    : 'press [ENTER] to authenticate'}
                </span>
                <span style={{
                  display: 'inline-block', width: 8, height: 16, background: '#5eead4',
                  animation: 'splashBlinkCursor 1s steps(1) infinite',
                }} />
              </div>
            </div>

            {/* scanline texture */}
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: 'repeating-linear-gradient(0deg, rgba(0,0,0,.15) 0px, rgba(0,0,0,.15) 1px, transparent 1px, transparent 3px)',
              mixBlendMode: 'overlay', opacity: .35,
            }} />
          </div>

          <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'center', pointerEvents: 'auto' }}>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                if (!fsDoneRef.current) {
                  fsDoneRef.current = true;
                  setFsDone(true);
                  playBeep(660, 90, 0.05);
                  await tryFullscreenNow();
                } else {
                  playBeep(880, 140, 0.06);
                  startSimNow();
                }
              }}
              style={{
                padding: '12px 20px',
                borderRadius: 10,
                border: '1px solid rgba(94,234,212,.4)',
                background: 'linear-gradient(135deg, rgba(56,189,248,.16), rgba(94,234,212,.12))',
                color: '#8ff0d4',
                fontFamily: 'ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace',
                fontSize: 14,
                letterSpacing: 1,
                cursor: 'pointer',
                backdropFilter: 'blur(6px)',
                boxShadow: '0 10px 28px rgba(0,0,0,.45), inset 0 0 0 1px rgba(255,255,255,.06)',
                transition: 'transform .08s ease, box-shadow .2s ease',
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = 'translateY(1px)';
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {fsDone ? '[ ▶ START ]' : '[ ENTER ]'}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Authentication Modal */}
    {authOpen && (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 10000,
          display: 'grid',
          placeItems: 'center',
          background: 'rgba(2,8,23,0.55)',
          backdropFilter: 'blur(8px)',
          pointerEvents: 'auto',
        }}
        onClick={() => {}}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 360,
            borderRadius: 14,
            background: 'linear-gradient(180deg, rgba(6,14,20,.96), rgba(2,7,10,.98))',
            border: '1px solid rgba(56,189,248,.35)',
            boxShadow: '0 18px 60px rgba(0,0,0,.6), 0 0 60px rgba(56,189,248,.12)',
            color: '#8ff0d4',
            fontFamily: 'ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace',
            overflow: 'hidden',
          }}
        >
          {/* terminal title bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 14px',
            background: 'rgba(255,255,255,.03)',
            borderBottom: '1px solid rgba(56,189,248,.2)',
          }}>
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ff5f56', display: 'inline-block' }} />
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#ffbd2e', display: 'inline-block' }} />
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: '#27c93f', display: 'inline-block' }} />
            <span style={{ marginLeft: 10, fontSize: 12, letterSpacing: 1, color: 'rgba(148,197,255,.6)' }}>
              auth@core — login
            </span>
          </div>

          <div style={{ padding: 18 }}>
            <div style={{ fontSize: 13, color: '#38bdf8', marginBottom: 14 }}>
              <span style={{ color: '#5eead4' }}>{'>'}</span> authentication required_
            </div>

           <form onSubmit={handleSubmitAuth}>

              <label style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1 }}>USERNAME</label>
              <input
                autoFocus
                className="term-input"
                value={authUser}
                onChange={(e) => setAuthUser(e.target.value)}
                style={{
                  boxSizing: 'border-box',
                  width: '100%',
                  margin: '6px 0 12px',
                  padding: '11px 12px',
                  background: 'rgba(255,255,255,.05)',
                  color: '#EAF2FF',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  border: '1px solid rgba(94,234,212,.3)',
                  borderRadius: 8,
                  outline: 'none',
                }}
                placeholder="username"
              />

              <label style={{ fontSize: 11, opacity: 0.7, letterSpacing: 1 }}>PASSWORD</label>
              <input
                type="password"
                className="term-input"
                value={authPass}
                onChange={(e) => setAuthPass(e.target.value)}
                style={{
                  boxSizing: 'border-box',
                  width: '100%',
                  margin: '6px 0 14px',
                  padding: '11px 12px',
                  background: 'rgba(255,255,255,.05)',
                  color: '#EAF2FF',
                  fontFamily: 'inherit',
                  fontSize: 14,
                  border: '1px solid rgba(94,234,212,.3)',
                  borderRadius: 8,
                  outline: 'none',
                }}
                placeholder="••••••••"
              />

              {authErr && (
                <div style={{ color: '#fb7185', fontSize: 12, marginBottom: 10 }}>
                  <span style={{ color: '#fb7185' }}>{'>'}</span> error: {authErr}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setAuthOpen(false);
                    setAuthErr('');
                    pushLog('Auth canceled');
                  }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: 'transparent',
                    color: 'rgba(148,197,255,.75)',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    border: '1px solid rgba(255,255,255,.18)',
                  }}
                >
                  [ cancel ]
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 14px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, rgba(56,189,248,.18), rgba(94,234,212,.14))',
                    color: '#8ff0d4',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    border: '1px solid rgba(94,234,212,.4)',
                  }}
                >
                  [ login ]
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    )}










    {/* HUD-Wrapper (durchklickbar) */}
    <div className="simulation-hud" data-hidden={hudHidden} style={hudWrapperStyle}>
      {/* Simulation controls: regular Digital-Twin users may simulate time ranges, but remain locked to their own house view. */}
      {/* Datum/Uhrzeit + Mode-Badge */}
      <div style={{ ...hudBlockPE, position:'absolute', top:'1.5vh', left:'1.5vh',
        background:'rgba(2,6,23,0.6)', color:'#fff', padding:'10px 14px', borderRadius:12,
        fontFamily:'Inter, monospace', fontSize:14, zIndex:10, display:'flex', alignItems:'center', gap:10,
        border:'1px solid rgba(255,255,255,.12)', backdropFilter:'blur(6px)' }}>
        <span>{formatDateTimeInTz(date)}</span>
        <span style={{
          padding:'4px 8px',
          background:'linear-gradient(135deg, rgba(56,189,248,.18), rgba(168,85,247,.18))',
          border:'1px solid rgba(255,255,255,.18)', borderRadius:999
        }}>
          Mode: {isRealistic ? 'Realistisch' : 'Ideal'} · Schatten {shadingEnabled ? 'AN' : 'AUS'}
        </span>
      </div>

      {projectMode&&<button style={{...hudBlockPE,position:'absolute',top:65,left:12,zIndex:15,color:'#e2e8f0',background:'#102f46',border:'1px solid #38bdf850',borderRadius:8,padding:8}} onClick={()=>setFreeTimeControls(v=>!v)}>{freeTimeControls?'Freie Zeitsteuerung schließen':'Freie Zeitsteuerung öffnen'}</button>}
      {(!projectMode||freeTimeControls)&&<>
      {/* Startdatum */}
      <div style={{ ...hudBlockPE, position:'absolute', top:projectMode?'110px':'7vh', left:'1.5vh',
        background:'rgba(2,6,23,0.6)', color:'#fff', padding:'10px 14px', borderRadius:12,
        fontFamily:'Inter, monospace', fontSize:14, zIndex:10,
        border:'1px solid rgba(255,255,255,.12)', backdropFilter:'blur(6px)' }}>
        <label htmlFor="calendarPicker">📅 start date:&nbsp;</label>
        <input
          id="calendarPicker"
          type="date"
          value={formatDateInputInTz(date)}
          onChange={(e) => {
            const input = e.target.value;
            const parts = input.split('-').map(Number);
            const d = projectMode && parts.length === 3 && parts.every(Number.isFinite)
              ? makeMidnightInTz(parts[0], parts[1], parts[2], locationTimezone)
              : parseBrowserLocalDateInput(input, initialDateRef.current);
            if (isValidDate(d)) setDate(d);
          }}
          style={{
            background:'rgba(15,20,35,.7)', color:'#fff', border:'1px solid rgba(255,255,255,.18)',
            padding:'6px 8px', borderRadius:8
          }}
        />
      </div>

      {/* Controls – futuristische Buttons */}
      <div style={{ ...hudBlockPE, position:'absolute', top:projectMode?'220px':'19vh', left:'1.5vh',
        background:'rgba(2,6,23,0.6)', padding:'10px 12px', borderRadius:14, zIndex:10,
        display:'flex', flexDirection:'column', gap:6,
        border:'1px solid rgba(255,255,255,.12)', backdropFilter:'blur(6px)' }}>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
          {/* Stop */}
          <button
            onClick={() => setIsRunning(false)}
            title="Pause simulation"
            style={{
              padding:'10px 14px', borderRadius:12, cursor:'pointer',
              background: !isRunning
                ? 'linear-gradient(135deg, rgba(244,63,94,.35), rgba(239,68,68,.25))'
                : 'linear-gradient(135deg, rgba(244,63,94,.18), rgba(239,68,68,.12))',
              color:'#ffeef2',
              border:'1px solid rgba(244,63,94,.45)',
              boxShadow: !isRunning ? '0 10px 26px rgba(244,63,94,.35)' : 'none',
              transition:'transform .08s ease, box-shadow .2s ease'
            }}
            onMouseDown={(e)=>{ e.currentTarget.style.transform='translateY(1px)'; }}
            onMouseUp={(e)=>{ e.currentTarget.style.transform='translateY(0)'; }}
          >⏸️ Stop</button>

          {/* Start */}
          <button
            onClick={() => setIsRunning(true)}
            title="Start simulation"
            style={{
              padding:'10px 14px', borderRadius:12, cursor:'pointer',
              background: isRunning
                ? 'linear-gradient(135deg, rgba(16,185,129,.35), rgba(59,130,246,.28))'
                : 'linear-gradient(135deg, rgba(16,185,129,.2), rgba(59,130,246,.16))',
              color:'#eafff6',
              border:'1px solid rgba(16,185,129,.45)',
              boxShadow: isRunning ? '0 10px 26px rgba(16,185,129,.35)' : 'none',
              transition:'transform .08s ease, box-shadow .2s ease'
            }}
            onMouseDown={(e)=>{ e.currentTarget.style.transform='translateY(1px)'; }}
            onMouseUp={(e)=>{ e.currentTarget.style.transform='translateY(0)'; }}
          >▶️ Start</button>

          {/* Forward */}
          <button
            onClick={() => {
              if (forwardHeldTickedRef.current) {
                forwardHeldTickedRef.current = false;
                return;
              }
              setDate((p) => {
                const next = new Date(p.getTime() + intervalMs);
                const coords = readSafeCoords();
                sunTrackerRef.current?.update(next, coords.lat, coords.lon);
                updateMoonPosition(next, coords.lat, coords.lon);
                recomputeMatrixFromPanels(mountFilterRef.current);
                return next;
              });
            }}
            onMouseDown={startFastForward}
            onMouseUp={stopFastForward}
            onMouseLeave={stopFastForward}
            onTouchStart={(e) => { e.preventDefault(); startFastForward(); }}
            onTouchEnd={stopFastForward}
            title="Klick: +intervalMs · Halten: Schnellvorlauf"
            style={{
              padding:'10px 14px', borderRadius:12, cursor:'pointer',
              background: forwardActive
                ? 'linear-gradient(135deg, rgba(59,130,246,.35), rgba(168,85,247,.30))'
                : 'linear-gradient(135deg, rgba(59,130,246,.2), rgba(168,85,247,.18))',
              color:'#e7f1ff',
              border:'1px solid rgba(59,130,246,.45)',
              boxShadow: forwardActive ? '0 10px 26px rgba(59,130,246,.35)' : 'none',
              transition:'transform .08s ease, box-shadow .2s ease'
            }}
            onMouseDownCapture={(e)=>{ e.currentTarget.style.transform='translateY(1px)'; }}
            onMouseUpCapture={(e)=>{ e.currentTarget.style.transform='translateY(0)'; }}
          >⏭️ Forward</button>

          {/* Reset */}
          <button
            onClick={() => resetSimulation(false)}
            title="Setzt Datum, Energiesummen und Enddatum zurück"
            style={{
              padding:'10px 14px', borderRadius:12, cursor:'pointer',
              background: resetActive
                ? 'linear-gradient(135deg, rgba(250,204,21,.35), rgba(244,63,94,.28))'
                : 'linear-gradient(135deg, rgba(250,204,21,.18), rgba(244,63,94,.16))',
              color:'#fff9e6',
              border:'1px solid rgba(250,204,21,.45)',
              boxShadow: resetActive ? '0 10px 26px rgba(250,204,21,.35)' : 'none',
              transition:'transform .08s ease, box-shadow .2s ease'
            }}
            onMouseDown={(e)=>{ e.currentTarget.style.transform='translateY(1px)'; }}
            onMouseUp={(e)=>{ e.currentTarget.style.transform='translateY(0)'; }}
          >🔄 Reset</button>

          {/* Range Sim */}
          <button
            onClick={async () => {
                                   if (!endDate) { alert('Bitte Enddatum wählen.'); return; }
                                   await runRangeSimExact({
                                     from: date,
                                     to: endDate!,
                                     dtMinute: 1.5,
                                     twilightMinute: intervalMs/60000,
                                     includeCivilTwilight: false
                                   });
              setIsFastSim(false);
              setIsRunning(false);
            }}
            disabled={!endDate || rangeSimBusy}
            title="Schnelle deterministische Zeitraum-Simulation"
            style={{
              padding:'10px 14px', borderRadius:12, cursor: (!endDate || rangeSimBusy) ? 'not-allowed' : 'pointer',
              background:'linear-gradient(135deg, rgba(56,189,248,.25), rgba(168,85,247,.25))',
              color:'#E6F7FF',
              border:'1px solid rgba(56,189,248,.45)',
              opacity: (!endDate || rangeSimBusy) ? 0.55 : 1,
              boxShadow:'0 10px 26px rgba(56,189,248,.28)',
              transition:'transform .08s ease, box-shadow .2s ease'
            }}
          >
            ⚡ fast-sim
          </button>

          {/* Dorf-Durchflug wiederholen. Nur anbieten, wenn das Dorf-Modell
              wirklich geladen wurde — ohne setIntroFlightPath liefe sonst das
              generische Fix-Intro über eine Szene, für die es nicht gedacht ist. */}
          {introModelAvailable && (
            <button
              onClick={replayIntroFlight}
              disabled={introPlaying}
              title="Kamerafahrt durchs Dorf noch einmal abspielen (Klick oder Mausrad bricht ab)"
              style={{
                padding:'10px 14px', borderRadius:12,
                cursor: introPlaying ? 'not-allowed' : 'pointer',
                background:'linear-gradient(135deg, rgba(244,114,182,.25), rgba(168,85,247,.25))',
                color:'#FDE7F6',
                border:'1px solid rgba(244,114,182,.45)',
                opacity: introPlaying ? 0.55 : 1,
                boxShadow:'0 10px 26px rgba(244,114,182,.28)',
                transition:'transform .08s ease, box-shadow .2s ease'
              }}
              onMouseDown={(e)=>{ if(!introPlaying) e.currentTarget.style.transform='translateY(1px)'; }}
              onMouseUp={(e)=>{ e.currentTarget.style.transform='translateY(0)'; }}
            >
              {introPlaying ? '🎬 fliegt…' : '🎬 Dorf-Flug'}
            </button>
          )}

          {rangeSimBusy && (
            <div style={{
              width: 200, height: 10, background:'rgba(255,255,255,0.12)', borderRadius: 999,
              overflow:'hidden', alignSelf:'center', border:'1px solid rgba(255,255,255,.18)'
            }}>
              <div style={{
                width: `${(rangeProgress*100).toFixed(0)}%`, height:'100%',
                background:'linear-gradient(90deg, rgba(56,189,248,.9), rgba(168,85,247,.9))',
                transition:'width 90ms linear'
              }} />
            </div>
          )}
        </div>
      </div>

      {/* Enddatum */}
      <div style={{ ...hudBlockPE, position:'absolute', top:projectMode?'165px':'13vh', left:'1.5vh',
        background:'rgba(2,6,23,0.6)', color:'#fff', padding:'10px 14px', borderRadius:12,
        fontFamily:'Inter, monospace', fontSize:14, zIndex:10,
        border:'1px solid rgba(255,255,255,.12)', backdropFilter:'blur(6px)' }}>
        <label htmlFor="endDatePicker">⏳ end date:&nbsp;</label>
        <input
          id="endDatePicker"
          type="date"
          value={formatDateInputInTz(endDate)}
          onChange={(e) => {
            const input = e.target.value;
            if (!input) { setEndDate(null); return; }
            const [y, mo, dy] = input.split('-').map(Number);
            if (!y || !mo || !dy) return;
            // one tick before midnight of the next day = 23:59:59.999 in location tz
            const endD = new Date(makeMidnightInTz(y, mo, dy + 1, locationTimezone).getTime() - 1);
            if (isValidDate(endD)) setEndDate(endD);
          }}
          style={{
            background:'rgba(15,20,35,.7)', color:'#fff', border:'1px solid rgba(255,255,255,.18)',
            padding:'6px 8px', borderRadius:8
          }}
        />
      </div>




      </>}
      {/* Tabelle */}
      {(!isDigitalTwin || isAdminToken) && (!projectMode || (freeTimeControls&&builderObjects.some(o=>o.kind==='tracker'))) && (
      <div className="simulation-results" style={{
        ...hudBlockPE, position:'absolute', left:10, top:'78%', transform:'translateY(-50%)',
        background:'rgba(2,6,23,0.65)', color:'#fff', padding:18, borderRadius:14,
        fontFamily:'Inter, monospace', zIndex:12, width:'65vw', maxWidth:1200, maxHeight:'80vh',
        overflowY:'auto', boxShadow:'0 8px 22px rgba(0,0,0,0.45)', border:'1px solid rgba(255,255,255,.12)',
        backdropFilter:'blur(6px)'
      }}>
        {(sceneMode === 'village' || sceneMode === 'builder') ? (() => {
          /* 🏆 Dorf/Self-Build: Pro-Tracker-Bestenliste statt Richtungs-Matrix.
             Bei frei rotierten Trackern (Kompass + stufenlose Rotation) ist die
             N/O/S/W-Bucketierung nicht mehr aussagekräftig — die Matrix bleibt
             für die Siedlungs-/Einzelhaus-Vergleichsszenen erhalten. Kernmetrik
             hier: spezifischer Ertrag (kWh/kWp) — normiert auf installierte
             Leistung, macht 2er- und 8er-Tracker sowie Modulfarben fair
             vergleichbar. */
          const SECTORS = ['N', 'NO', 'O', 'SO', 'S', 'SW', 'W', 'NW'];
          type Row = { nr: number; name: string; azReal: number; sector: string; tilt: number; type: string; count: number; orientation?: string; color: string; colorLabel: string; eff: number; kwp: number; total: number; spec: number };
          const rows: Row[] = [];
          let idx = 0;
          trackerMapRef.current.forEach((p: any, name: string) => {
            if (!name.startsWith('b_tracker_')) return;
            idx++;
            const count = p.panelCount ?? 4;
            const eff = p.panelEfficiency ?? 1;
            const kwp = TRACKER_STANDARD_KW * (count / 4) * eff;
            const total = p.energyTotal ?? 0;
            // Interner Azimut (Süd=0) -> reale Kompass-Konvention (Nord=0, Ost=90)
            const azInternal = p.yawDeg != null ? p.yawDeg : (({ süd: 0, ost: 90, nord: 180, west: 270 } as Record<string, number>)[p.compass] ?? 0);
            const azReal = ((180 - azInternal) % 360 + 360) % 360;
            rows.push({
              nr: idx, name, azReal: Math.round(azReal), sector: SECTORS[Math.round(azReal / 45) % 8],
              tilt: Math.round(Math.abs(p.mountingDeg ?? 0)), type: p.type ?? '—',
              count, orientation: p.panelOrientation, color: p.panelColor ?? '#10151c',
              colorLabel: PANEL_COLORS.find(c => c.hex === p.panelColor)?.label ?? '',
              eff, kwp, total, spec: kwp > 0 ? total / kwp : 0,
            });
          });
          rows.sort((a, b) => b.spec - a.spec);
          const maxSpec = Math.max(0.01, ...rows.map(r => r.spec));
          const mono = 'ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace';
          const th = { textAlign: 'right' as const, padding: '9px 12px', borderBottom: '1px solid rgba(94,234,212,.25)', fontSize: 12, letterSpacing: 1, color: 'rgba(148,197,255,.6)', fontWeight: 600 };
          const td = { padding: '9px 12px', borderBottom: '1px solid rgba(255,255,255,.06)', textAlign: 'right' as const };
          return (
            <>
              <div style={{ fontWeight: 800, marginBottom: 12, fontSize: 18, color: '#5eead4', fontFamily: mono, textShadow: '0 0 12px rgba(94,234,212,.35)' }}>
                🏆 Tracker-Bestenliste (seit Sim-Start) — sortiert nach spezifischem Ertrag
              </div>
              {rows.length === 0 ? (
                <div style={{ opacity: .6, fontSize: 13 }}>Noch keine Tracker platziert — im Self-Build-Tab welche setzen.</div>
              ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14, fontVariantNumeric: 'tabular-nums', fontFamily: mono }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: 'left' }}>#</th>
                      <th style={{ ...th, textAlign: 'left' }}>ausrichtung</th>
                      <th style={th}>neigung</th>
                      <th style={{ ...th, textAlign: 'left' }}>typ</th>
                      <th style={{ ...th, textAlign: 'left' }}>module</th>
                      <th style={th}>kWp</th>
                      <th style={th}>gesamt</th>
                      <th style={{ ...th, width: '22%' }}>spez. ertrag (kWh/kWp)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const isHl = r.name === highlightedTrackerName;
                      return (
                      <tr key={r.nr}
                        title="Klick: Kamera springt zu diesem Tracker"
                        onClick={() => {
                          setHighlightedTrackerName(r.name);
                          (controlsRef.current as any)?.focusTracker?.(r.name);
                        }}
                        ref={isHl ? (el) => {
                          if (el && lastScrolledTrackerRef.current !== r.name) {
                            lastScrolledTrackerRef.current = r.name;
                            scrollRowIntoContainerView(el);
                          }
                        } : undefined}
                        style={{
                          cursor: 'pointer',
                          background: isHl ? 'rgba(250,204,21,.14)' : (i % 2 ? 'rgba(255,255,255,0.025)' : 'transparent'),
                          outline: isHl ? '1px solid rgba(250,204,21,.45)' : 'none',
                        }}>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: i === 0 ? '#facc15' : '#8ff0d4' }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : r.nr}</td>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#8ff0d4' }}>{r.sector} · {r.azReal}°</td>
                        <td style={td}>{r.tilt}°</td>
                        <td style={{ ...td, textAlign: 'left', opacity: .85 }}>{r.type}</td>
                        <td style={{ ...td, textAlign: 'left' }}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 3, background: r.color, border: '1px solid rgba(255,255,255,.3)', marginRight: 6, verticalAlign: 'middle' }} />
                          {r.count}{r.orientation === 'landscape' ? ' quer' : ''}{r.colorLabel ? ` · ${r.colorLabel}` : ''}{r.eff < 1 ? ` (${Math.round(r.eff * 100)}%)` : ''}
                        </td>
                        <td style={td}>{r.kwp.toFixed(2)}</td>
                        <td style={td}><AnimatedNumber value={r.total} decimals={2} durationMs={400} /> kWh</td>
                        <td style={td}>
                          <div><AnimatedNumber value={r.spec} decimals={1} durationMs={400} /></div>
                          <div style={{ marginTop: 4, height: 4, borderRadius: 999, background: 'rgba(255,255,255,.07)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(100, (r.spec / maxSpec) * 100)}%`, borderRadius: 999, background: 'linear-gradient(90deg, rgba(56,189,248,.9), rgba(94,234,212,.95))', transition: 'width 400ms ease' }} />
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              <div style={{ marginTop: 10, fontSize: 12, color: '#bbb' }}>
                kWh/kWp = Ertrag normiert auf installierte Leistung — macht unterschiedliche Bestückungen und Modulfarben fair vergleichbar. Ausrichtung in realer Kompass-Konvention (N=0°, O=90°). Klick auf eine Zeile springt zum Tracker.
              </div>
            </>
          );
        })() : (<>
        <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:12, flexWrap:'wrap' }}>
          <div style={{ opacity:0.9 }}>hausdach neigung:</div>
          <button
            onClick={() => setMountFilter('all')}
            style={{
              padding:'8px 12px', borderRadius:999, cursor:'pointer',
              background: mountFilter==='all'
                ? 'linear-gradient(135deg, rgba(56,189,248,.3), rgba(168,85,247,.3))'
                : 'transparent',
              color:'#fff', border:'1px solid rgba(255,255,255,.18)'
            }}
          >Alle</button>
          {DEG_OPTIONS.map(deg => (
            <button
              key={deg}
              onClick={() => setMountFilter(deg)}
              title={`Nur Panels mit ~${deg}° (Vorzeichen egal)`}
              style={{
                padding:'8px 12px', borderRadius:999, cursor:'pointer',
                background: mountFilter===deg
                  ? 'linear-gradient(135deg, rgba(56,189,248,.3), rgba(168,85,247,.3))'
                  : 'transparent',
                color:'#fff', border:'1px solid rgba(255,255,255,.18)'
              }}
            >{deg}°</button>
          ))}
          <div style={{ marginLeft:'auto', opacity:0.75, fontSize:12 }}>
            aktiv: {mountFilter==='all' ? 'Alle' : `${mountFilter}°`}
          </div>
        </div>

        <div style={{
          fontWeight:800, marginBottom:12, fontSize:18, color: '#5eead4',
          fontFamily: 'ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace',
          textShadow: '0 0 12px rgba(94,234,212,.35)',
        }}>
          ⚡ Gesamtenergie je Richtung &amp; Typ (seit Sim-Start)
        </div>

        {(() => {
          const DIRS = ['nord', 'ost', 'süd', 'west'] as const;
          const TYPES = ['fixed', 'single', 'limited', 'unlimited'] as const;
          // Sequenzielle Magnitude-Kodierung (eine Farbe, Balkenlänge ~ Wert) statt reiner
          // Zahlenwand — max über die GESAMTE Tabelle, damit Balkenlängen zellenübergreifend
          // vergleichbar bleiben (nicht nur innerhalb einer Zeile).
          const tableMaxKWh = Math.max(0.01, ...DIRS.flatMap((dir) => TYPES.map((t) => panelEnergyData.get(`${dir}-${t}`) ?? 0)));
          return (
            <table style={{
              borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', fontSize: 15,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: 'ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace',
            }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(94,234,212,.25)', fontSize: 12, letterSpacing: 1, color: 'rgba(148,197,255,.6)', fontWeight: 600 }}>richtung</th>
                  {TYPES.map((t) => (
                    <th key={t} style={{ textAlign: 'right', padding: '10px 14px', borderBottom: '1px solid rgba(94,234,212,.25)', fontSize: 12, letterSpacing: 1, color: 'rgba(148,197,255,.6)', fontWeight: 600 }}>{t}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DIRS.map((dir, i) => (
                  <tr key={dir} style={{ background: i % 2 ? 'rgba(255,255,255,0.025)' : 'transparent' }}>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.06)', fontWeight: 700, color: '#8ff0d4' }}>{dir}</td>
                    {TYPES.map((t) => {
                      const key = `${dir}-${t}`;
                      const totalKWh = panelEnergyData.get(key) ?? 0;
                      const pct = Math.min(100, (totalKWh / tableMaxKWh) * 100);
                      return (
                        <td key={t} style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,.06)', textAlign: 'right' }}>
                          <div><AnimatedNumber value={totalKWh} decimals={2} durationMs={400} /> kWh</div>
                          <div style={{ marginTop: 5, height: 4, borderRadius: 999, background: 'rgba(255,255,255,.07)', overflow: 'hidden' }}>
                            <div style={{
                              height: '100%', width: `${pct}%`, borderRadius: 999,
                              background: 'linear-gradient(90deg, rgba(56,189,248,.9), rgba(94,234,212,.95))',
                              transition: 'width 400ms ease',
                            }} />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          );
        })()}

        <div style={{ marginTop:10, fontSize:12, color:'#bbb' }}>
          Quelle: <code>trackerMap[*].energyTotal</code> (aufsummiert; Richtung/Typ normalisiert wie im <code>EnergyTracker</code> · Tabellenfilter: {mountFilter==='all' ? 'alle' : `${mountFilter}°`})
        </div>
        </>)}
      </div>
      )}

      {/* Kompass + North remain available in Digital-Twin user view. */}
      <div style={{
        ...hudBlockPE, position:'absolute', top:24, right:24,
        background:'rgba(2,6,23,0.6)', color:'white', padding:'10px 12px',
        borderRadius:'14px', fontFamily:'Inter, monospace', fontSize:'14px', zIndex:10,
        textAlign:'center', width:140, border:'1px solid rgba(255,255,255,.12)', backdropFilter:'blur(6px)'
      }}>
        <div style={{
          width:'70px', height:'70px', borderRadius:'50%', border:'2px solid rgba(255,255,255,.9)',
          position:'relative', margin:'0 auto', transform:`rotate(${-cameraHeading}deg)`,
          transition:'transform 0.1s linear'
        }} title={`Heading: ${cameraHeading.toFixed(0)}°`}>
          <div style={{ position:'absolute', top:'4px', left:'50%', transform:'translateX(-50%)', fontWeight:'bold', color:'red', userSelect:'none' }}>N</div>
          <div style={{ position:'absolute', right:'6px', top:'50%', transform:'translateY(-50%)' }}>E</div>
          <div style={{ position:'absolute', bottom:'4px', left:'50%', transform:'translateX(-50%)' }}>S</div>
          <div style={{ position:'absolute', left:'6px', top:'50%', transform:'translateY(-50%)' }}>W</div>
          <div style={{ position:'absolute', left:'50%', top:'50%', transform:'translate(-50%, -100%)', width:0, height:0, borderLeft:'6px solid transparent', borderRight:'6px solid transparent', borderBottom:'22px solid red' }} />
        </div>

        <button
          style={{
            marginTop:'8px', padding:'8px 10px', width:'100%',
            borderRadius:10, cursor:'pointer',
            background:'linear-gradient(135deg, rgba(56,189,248,.22), rgba(168,85,247,.22))',
            color:'#EAF2FF', border:'1px solid rgba(56,189,248,.45)'
          }}
          onClick={orientNorth}
        >⬆️ North</button>

        {(!isDigitalTwin || isAdminToken) && renderSimulationSettingsControls('compass')}
      </div>


{/* Ortssuche */}
      {(!isDigitalTwin || isAdminToken) && (
      <div style={{
        ...hudBlockPE, position:'absolute', top:24, right:188,
        width:360,
        background:'rgba(2,6,23,0.6)', color:'white', padding:'10px 12px', borderRadius:'12px',
        fontFamily:'Inter, monospace', fontSize:'14px', zIndex:10,
        border:'1px solid rgba(255,255,255,.12)', backdropFilter:'blur(6px)'
      }}>
        <form
          style={{ display:'grid', gridTemplateColumns:'auto minmax(0, 1fr) 36px', alignItems:'center', gap:8, position:'relative' }}
          onSubmit={async (e) => {
          e.preventDefault();
          try {
            const res = await fetch(`${apiUrl('/api/nominatim')}?q=${encodeURIComponent(location)}`);
            const data = await res.json() as NominatimResult[];
            if (data.length > 0) {
              const result = data[0];
              if (!applyCoordinates(result.lat, result.lon)) { alert('invalid coordinates'); return; }
              const nextLocation = result.display_name ?? location;
              skipNextSuggestionFetchRef.current = nextLocation !== location;
              setLocation(nextLocation); setSuggestions([]); setShowMessage(true);
              setTimeout(() => setShowMessage(false), 3000);
            } else { alert('city not found'); }
          } catch (err) { console.error('❌ Fehler bei der Ortssuche:', err); alert('error city search'); }
        }}>
          <label htmlFor="locationInput">📍 City:&nbsp;</label>
          <input
            id="locationInput"
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Ort eingeben..."
            style={{
              background:'rgba(15,20,35,.7)', color:'white',
              border:'1px solid rgba(255,255,255,.18)', padding:'6px 8px', borderRadius:'8px',
              width:'100%', minWidth:0
            }}
          />
          <button
            type="submit"
            style={{
              width:36, height:34, padding:0, borderRadius:10, cursor:'pointer',
              background:'linear-gradient(135deg, rgba(56,189,248,.22), rgba(168,85,247,.22))',
              color:'#E6F7FF', border:'1px solid rgba(56,189,248,.45)'
            }}
          >
            🔍
          </button>
          {suggestions.length > 0 && (
            <div style={{
              position:'absolute', top:'42px', left:'64px', right:0,
              background:'rgba(16,18,24,.95)', border:'1px solid rgba(255,255,255,.12)',
              borderRadius:'8px', maxHeight:'180px', overflowY:'auto', zIndex:100, fontSize:'13px',
              backdropFilter:'blur(6px)'
            }}>
              {suggestions.map((suggestion, i) => (
                <div
                  key={i}
                  onClick={async () => {
                    skipNextSuggestionFetchRef.current = suggestion !== location;
                    setLocation(suggestion); setSuggestions([]);
                    const res = await fetch(`${apiUrl('/api/nominatim')}?q=${encodeURIComponent(suggestion)}`);
                    const data = await res.json() as NominatimResult[];
                    if (data.length > 0) {
                      const result = data[0];
                      if (!applyCoordinates(result.lat, result.lon)) { alert('invalid coordinates'); return; }
                      setShowMessage(true); setTimeout(() => setShowMessage(false), 3000);
                    }
                  }}
                  style={{ padding:'8px 10px', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,.08)' }}
                >{suggestion}</div>
              ))}
            </div>
          )}
          {showMessage && (<div style={{ gridColumn:'1 / -1', color:'#0f0', fontSize:'12px', fontStyle:'italic' }}>✅ Ort erfolgreich gesetzt!</div>)}
          <div style={{ gridColumn:'1 / -1', color:'rgba(255,255,255,.45)', fontSize:'11px' }}>
            🌍 {locationTimezone}
          </div>
        </form>
      </div>
      )}

</div>

    {/* ============== KUNDENHAUS-LADEANZEIGE (?site=) ==============
        enterBuilderMode() räumt die Szene frei, sobald die Twin-Config da ist —
        das GLB selbst ist dann noch zweistellige Sekunden unterwegs. Ohne diese
        Anzeige sieht der Nutzer in der Zwischenzeit nur eine leere Baufläche und
        hält die Anwendung für kaputt. */}
    {!showSplash && siteModelLoad && (
      <div style={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        zIndex: 70, pointerEvents: 'none',
        background: 'rgba(2,6,23,0.88)', border: '1px solid rgba(56,189,248,.35)',
        borderRadius: 14, padding: '18px 22px', minWidth: 300, maxWidth: 'min(92vw, 380px)',
        color: '#E8EEF8', fontFamily: 'Inter, monospace', fontSize: 13,
        boxShadow: '0 12px 40px rgba(0,0,0,.55)', backdropFilter: 'blur(10px)',
      }}>
        {siteModelLoad.state === 'error' ? (
          <>
            <div style={{ fontWeight: 700, color: '#f87171', marginBottom: 6 }}>
              ⚠ Kundenhaus konnte nicht geladen werden
            </div>
            <div style={{ opacity: .75, fontSize: 12 }}>{siteModelLoad.message}</div>
          </>
        ) : (() => {
          const { received, total } = siteModelLoad;
          const mb = (n: number) => (n / 1048576).toFixed(1);
          // Ohne Content-Length (Proxy konnte ihn nicht durchreichen) gibt es
          // keinen ehrlichen Prozentwert — dann lieber ein unbestimmter Balken
          // als eine erfundene Zahl.
          const pct = total ? Math.min(100, Math.floor((received / total) * 100)) : null;
          return (
            <>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>🏠 Kundenhaus wird geladen…</div>
              <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,.12)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 999,
                  width: pct == null ? '35%' : `${pct}%`,
                  background: 'linear-gradient(90deg,#38bdf8,#a855f7)',
                  transition: 'width .2s linear',
                }} />
              </div>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 11.5, opacity: .7 }}>
                <span>{total ? `${mb(received)} / ${mb(total)} MB` : `${mb(received)} MB`}</span>
                <span>{pct == null ? '' : `${pct} %`}</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 11, opacity: .5 }}>
                Große Modelle brauchen bis zu ~90 Sekunden.
              </div>
            </>
          );
        })()}
      </div>
    )}

    {/* ===================== SCENE MODE BAR ===================== */}
    {/* Auch im Twin-Modus sichtbar: ohne diese Leiste gibt es keinen Weg in den
        Self-Build-Tab, und damit keine Möglichkeit, Hindernisse oder Tracker zu
        platzieren. Welche Modi angeboten werden, entscheidet mayUseSceneMode. */}
    {!showSplash && !projectMode && (
      <div style={{
        // Safe-Area: auf iPhones nicht unter der Notch verschwinden; auf schmalen
        // Screens passt sich die Leiste an (kleinere Tabs, notfalls scrollbar).
        position: 'absolute', top: 'calc(12px + env(safe-area-inset-top))', left: '50%', transform: 'translateX(-50%)',
        display: 'flex', gap: 6, zIndex: 60,
        maxWidth: 'calc(100vw - 16px)', overflowX: 'auto',
        background: 'rgba(2,6,23,0.75)', padding: '5px 8px', borderRadius: 14,
        border: '1px solid rgba(255,255,255,.12)', backdropFilter: 'blur(8px)',
        pointerEvents: 'auto',
      }}>
        {(isDigitalTwin
          ? (isAdminToken
              ? ([['builder', '🏗 Self Build']] as [SceneMode, string][])
              // Kunden-Twin: eigene Anlage ansehen ODER die Umgebung modellieren.
              // Muss deckungsgleich mit TWIN_SCENE_MODES bleiben.
              : ([['single', '🏠 Einzelhaus'], ['builder', '🏗 Self Build']] as [SceneMode, string][]))
          : ([
              // "Dorf" nur anbieten, wenn das öffentliche Intro-Modell geladen wurde
              ...(introModelAvailable ? [['village', '🏛 Dorf']] : []),
              ['multi', '🏘 Siedlung'], ['single', '🏠 Einzelhaus'], ['builder', '🏗 Self Build'],
            ] as [SceneMode, string][])
        ).map(([mode, label]) => (
          <button
            key={mode}
            onClick={() => activateSceneMode(mode)}
            style={{
              minWidth: 'min(112px, 23vw)', whiteSpace: 'nowrap',
              padding: '8px 14px', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700,
              background: sceneMode === mode
                ? 'linear-gradient(135deg, rgba(56,189,248,.35), rgba(168,85,247,.3))'
                : 'transparent',
              color: sceneMode === mode ? '#E6F7FF' : 'rgba(255,255,255,.55)',
              border: sceneMode === mode ? '1px solid rgba(56,189,248,.5)' : '1px solid transparent',
              transition: 'all .15s ease',
            }}
          >{label}</button>
        ))}
      </div>
    )}

    {/* ===================== DIGITAL TWIN PANEL ===================== */}
    {!showSplash && isDigitalTwin && (() => {
      const { trackers, primaryTracker } = trackerApiState as Extract<typeof trackerApiState, {status:'ready'}>;
      const tele = primaryTelemetry;
      const angle = tele ? getTiltAngle(tele) : undefined;
      const wind = tele?.wind_speed ?? 0;
      const isWindDanger = wind >= 10;
      const opMode = tele?.operating_mode ?? tele?.mode ?? '—';
      const health = tele?.health_status ?? '—';
      const healthColor = health === 'optimal' ? '#4ade80' : health === 'warning' ? '#facc15' : health === 'critical' ? '#f87171' : '#9ca3af';

      const TRACKER_MODES = [
        { id: 'sun_tracking', label: '☀️ Sun Track' },
        { id: 'auto_gps',     label: '🛰 Auto GPS' },
        { id: 'manual',       label: '🎛 Manual' },
        { id: 'park',         label: '🅿️ Park' },
      ] as const;

      return (
        <div style={{
          position: 'absolute',
          top: digitalTwinPanelPos ? digitalTwinPanelPos.y : (isAdminToken ? 260 : 24),
          left: digitalTwinPanelPos ? digitalTwinPanelPos.x : undefined,
          /* Bei offenem Editor nach links ausweichen. Das Builder-Panel sitzt bei
           * right:24 mit width:300, hat aber nur zIndex 20 gegen die 60 hier —
           * ohne Ausweichen verdeckt dieses Panel den Editor fast vollständig
           * (Admin: beide auf right:24, top 260 vs. 250). 340 = 24 + 300 + 16px
           * Luft. Eine vom Nutzer gezogene Position (digitalTwinPanelPos) bleibt
           * davon unberührt. */
          right: digitalTwinPanelPos ? undefined : (activeTab === 'builder' ? 340 : (isAdminToken ? 24 : 188)),
          zIndex: 60,
          background: 'rgba(2,6,23,0.9)', borderRadius: 18,
          border: `1px solid ${isWindDanger ? 'rgba(251,191,36,.6)' : 'rgba(56,189,248,.25)'}`,
          backdropFilter: 'blur(12px)', padding: '16px', minWidth: 260,
          pointerEvents: 'auto', color: 'white', fontSize: 13,
          boxShadow: isWindDanger ? '0 0 24px rgba(251,191,36,.2)' : '0 8px 32px rgba(0,0,0,.5)',
          transition: 'border-color .3s, box-shadow .3s',
          touchAction: 'none',
        }}>

          {/* ── Header ── */}
          <div
            onPointerDown={startDigitalTwinPanelDrag}
            title="Digital-Twin-Panel verschieben"
            style={{
              display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12,
              cursor:'grab', userSelect:'none', touchAction:'none',
            }}
          >
            <div style={{ fontWeight:800, fontSize:15, color:'#38bdf8' }}>🔗 Digital Twin</div>
            <div style={{ fontSize:11, opacity:0.5 }}>{trackers.length} Tracker</div>
          </div>

          <div style={{ fontSize:12, opacity:0.6, marginBottom:12 }}>
            {primaryTracker.name} · {primaryTracker.location_name}
          </div>

          {/* ── Simulation / Live Toggle ── */}
          <div style={{
            display:'flex', background:'rgba(255,255,255,.07)', borderRadius:10,
            padding:3, marginBottom:14, gap:3,
          }}>
            {(['simulation','live'] as const).map((m) => (
              <button key={m} onClick={() => setLiveMode(m)} style={{
                flex:1, padding:'6px 0', borderRadius:8, cursor:'pointer',
                fontSize:12, fontWeight:700, border:'none',
                background: liveMode === m
                  ? 'linear-gradient(135deg,rgba(56,189,248,.4),rgba(168,85,247,.35))'
                  : 'transparent',
                color: liveMode === m ? '#E6F7FF' : 'rgba(255,255,255,.45)',
                transition:'all .15s',
              }}>
                {m === 'simulation' ? '🔮 Simulation' : '⚡ Live Daten'}
              </button>
            ))}
          </div>

          {!isAdminToken && renderSimulationSettingsControls('panel')}

          {/* ── Telemetrie (nur im Live-Modus) ── */}
          {liveMode === 'live' && (
            <>
              {/* Wind-Warnung */}
              {isWindDanger && (
                <div style={{
                  background:'rgba(251,191,36,.15)', border:'1px solid rgba(251,191,36,.4)',
                  borderRadius:10, padding:'8px 10px', marginBottom:10, fontSize:12,
                  color:'#fbbf24', fontWeight:700, display:'flex', alignItems:'center', gap:6,
                }}>
                  🌬 Wind {wind.toFixed(1)} m/s — Starkwind!
                </div>
              )}

              {/* Sensor-Grid */}
              <div style={{
                background:'rgba(255,255,255,.05)', borderRadius:10,
                padding:'10px 12px', marginBottom:12,
                display:'grid', gridTemplateColumns:'1fr 1fr', gap:'5px 14px', fontSize:12,
              }}>
                <span style={{opacity:.6}}>Winkel</span>
                <span style={{color:'#38bdf8', fontWeight:700}}>
                  {angle != null ? `${angle.toFixed(1)}°` : '—'}
                </span>
                <span style={{opacity:.6}}>Leistung</span>
                <span style={{color:'#4ade80', fontWeight:700}}>
                  {tele?.power_w != null ? <><AnimatedNumber value={tele.power_w as number} decimals={0} durationMs={600} /> W</> : '—'}
                </span>
                <span style={{opacity:.6}}>Heute gesamt</span>
                <span style={{color:'#a78bfa', fontWeight:700}}>
                  {totalDailyKwh > 0 ? <><AnimatedNumber value={totalDailyKwh} decimals={2} durationMs={600} /> kWh</> : '—'}
                </span>
                <span style={{opacity:.6}}>Wind</span>
                <span style={{color: isWindDanger ? '#fbbf24' : 'white'}}>
                  {tele?.wind_speed != null ? `${(tele.wind_speed as number).toFixed(1)} m/s` : '—'}
                </span>
                <span style={{opacity:.6}}>Temp</span>
                <span>{tele?.temperature != null ? `${tele.temperature}°C` : '—'}</span>
                <span style={{opacity:.6}}>Modus</span>
                <span style={{color:'#a78bfa', fontWeight:600}}>{opMode}</span>
                <span style={{opacity:.6}}>Status</span>
                <span style={{color: healthColor, fontWeight:600}}>{health}</span>
              </div>

              {/* Gesamt-Leistung aller Tracker */}
              {trackers.length > 1 && (
                <div style={{
                  background:'rgba(56,189,248,.08)', borderRadius:10,
                  padding:'8px 12px', marginBottom:12, fontSize:12,
                  display:'flex', justifyContent:'space-between',
                }}>
                  <span style={{opacity:.7}}>Alle Tracker gesamt</span>
                  <span style={{color:'#38bdf8', fontWeight:700}}>{Math.round(totalLivePowerW)} W</span>
                </div>
              )}

              {/* Einzel-Tracker Modus-Buttons */}
              <div style={{fontSize:11, opacity:.6, marginBottom:6}}>Modus (primärer Tracker):</div>
              <div style={{display:'flex', flexWrap:'wrap', gap:5, marginBottom:12}}>
                {TRACKER_MODES.map(({id, label}) => (
                  <button key={id}
                    onClick={() => sendCommand(primaryTracker.device_id, id)}
                    style={{
                      padding:'5px 9px', borderRadius:8, cursor:'pointer',
                      fontSize:11, fontWeight:600, border:'none',
                      background: opMode === id
                        ? 'linear-gradient(135deg,rgba(56,189,248,.4),rgba(168,85,247,.35))'
                        : 'rgba(255,255,255,.08)',
                      color: opMode === id ? '#E6F7FF' : 'rgba(255,255,255,.65)',
                      transition:'all .15s',
                    }}
                  >{label}</button>
                ))}
              </div>

              {/* SAFE MODE – alle Tracker auf einmal */}
              <button
                onClick={() => sendBulkCommand('safe_mode')}
                style={{
                  width:'100%', padding:'10px 0', borderRadius:10, cursor:'pointer',
                  fontWeight:800, fontSize:13, border:'none',
                  background: isWindDanger
                    ? 'linear-gradient(135deg,#dc2626,#b91c1c)'
                    : 'linear-gradient(135deg,rgba(220,38,38,.7),rgba(185,28,28,.6))',
                  color:'white',
                  boxShadow: isWindDanger ? '0 0 16px rgba(220,38,38,.5)' : 'none',
                  animation: isWindDanger ? 'pulse 1.5s infinite' : 'none',
                  transition:'all .2s',
                }}
              >
                🛡 SAFE MODE — Alle {trackers.length} Tracker
              </button>

              {/* WS Status */}
              <div style={{marginTop:8, fontSize:11, opacity:.4, textAlign:'right'}}>
                {tele ? `● live · ${new Date().toLocaleTimeString()}` : '○ warten auf Daten…'}
              </div>
            </>
          )}

          {/* ── Simulation Mode Info + Energy ── */}
          {liveMode === 'simulation' && (() => {
            // Sum only builder tracker(s) from the map
            let todayKWh = 0, totalKWh = 0;
            trackerMapRef.current.forEach((p: any) => {
              if (p?.name?.startsWith('b_tracker_')) {
                todayKWh += p?.energyDay ?? 0;
                totalKWh += p?.energyTotal ?? 0;
              }
            });
            const hasBT = totalKWh > 0 || todayKWh > 0;
            return (
              <>
                <div style={{fontSize:12, opacity:.6, lineHeight:1.6, marginBottom: hasBT ? 10 : 0}}>
                  3D-Simulation zeigt die berechnete Energieproduktion basierend auf Sonnenstand und Standort.<br/>
                  <span style={{color:'#38bdf8'}}>Schalte auf Live Daten</span> um deinen echten Tracker zu sehen und zu steuern.
                </div>
                {hasBT && (
                  <div style={{
                    background:'rgba(56,189,248,.08)', borderRadius:10,
                    border:'1px solid rgba(56,189,248,.2)', padding:'10px 12px',
                    display:'grid', gridTemplateColumns:'1fr 1fr', gap:'5px 14px', fontSize:12,
                  }}>
                    <span style={{opacity:.6}}>Heute</span>
                    <span style={{color:'#4ade80', fontWeight:700}}>{todayKWh.toFixed(3)} kWh</span>
                    <span style={{opacity:.6}}>Gesamt</span>
                    <span style={{color:'#38bdf8', fontWeight:700}}>{totalKWh.toFixed(3)} kWh</span>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      );
    })()}

    {/* ===================== BUILDER PANEL ===================== */}
    {projectMode&&!showSplash&&<button aria-expanded={projectToolsOpen} onClick={()=>{setProjectToolsOpen(v=>!v);if(window.innerWidth<=800)setHudHidden(true);}} style={{position:'absolute',bottom:54,right:24,zIndex:80,padding:'10px 14px',borderRadius:8,border:'1px solid #55788a',background:'#173c45',color:'#fff'}}>Objekte {projectToolsOpen?'ausblenden':'hinzufügen / bearbeiten'}</button>}
    {!showSplash && activeTab === 'builder' && (!projectMode||projectToolsOpen) && (
      <div className="scene-builder-panel" style={{
        position: 'absolute', top: 250, right: 24,
        width: 300, zIndex: 20, pointerEvents: 'auto',
        background: 'rgba(4,8,20,0.88)', color: '#E8EEF8',
        border: '1px solid rgba(255,255,255,.14)', borderRadius: 16,
        backdropFilter: 'blur(10px)', fontFamily: 'Inter, monospace',
        boxShadow: '0 12px 40px rgba(0,0,0,.55)',
        overflowY: 'auto',
        maxHeight: 'calc(100vh - 350px)',
      }}>
        {/* Header */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid rgba(255,255,255,.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 800, fontSize: 15, letterSpacing: 0.3 }}>🏗 Scene Builder</div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>{builderPlacedCount} objekte platziert</div>
        </div>
        {/* Ground height */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, opacity: 0.7, whiteSpace: 'nowrap' }}>Bodenhöhe: {distanceLabel(builderGroundY)}</span>
          <input type="range" min={-8} max={4} step={0.5} value={builderGroundY}
            onChange={e => {
              const v = +e.target.value;
              setBuilderGroundY(v);
              (controlsRef.current as any)?.setBuilderGroundY?.(v);
            }}
            style={{ flex: 1, accentColor: '#38bdf8' }}
          />
        </div>

        {/* Platzierung: entweder "ausgewähltes Objekt bearbeiten" oder "nächste Platzierung"
            -- immer derselbe Bereich, damit Bodenhöhe/Rotation/Größe nicht mehr über das
            Panel verteilt sind. */}
        {selectedBuilderObjectId && selectedTransform ? (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.08)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#38bdf8', minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                ✏️ {selectedTransform.label}
              </div>
              <button
                onClick={() => builderSelectObject(null)}
                title="Auswahl aufheben"
                style={{ flexShrink: 0, width: 22, height: 22, borderRadius: 6, cursor: 'pointer', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.6)', fontSize: 12 }}
              >✕</button>
            </div>

            <div>
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>Rotation: {selectedTransform.rotDeg}°</div>
              <input type="range" min={0} max={360} step={5} value={selectedTransform.rotDeg}
                onChange={e => {
                  const v = +e.target.value;
                  setSelectedTransform(t => t ? { ...t, rotDeg: v } : t);
                  controlsRef.current?.builderSetSelectedRotation?.(v);
                }}
                style={{ width: '100%', accentColor: '#38bdf8' }}
              />
            </div>
            {!(projectMode && selectedTransform.kind === 'tracker') && <div>
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>{projectMode ? 'Skalierungsfaktor' : 'Größe'}: {selectedTransform.scale.toFixed(2)}</div>
              <input type="range"
                min={scaleToSlider(selectedTransform.scaleMin)} max={scaleToSlider(selectedTransform.scaleMax)}
                step={(scaleToSlider(selectedTransform.scaleMax) - scaleToSlider(selectedTransform.scaleMin)) / 300}
                value={scaleToSlider(selectedTransform.scale)}
                onChange={e => {
                  const v = sliderToScale(+e.target.value);
                  setSelectedTransform(t => t ? { ...t, scale: v } : t);
                  controlsRef.current?.builderSetSelectedScale?.(v);
                }}
                style={{ width: '100%', accentColor: '#38bdf8' }}
              />
            </div>}

            {selectedTransform.kind === 'tracker' && selectedTransform.mountingDeg != null && (
              <div>
                <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>Neigung: {selectedTransform.mountingDeg}°</div>
                <input type="range" min={0} max={45} step={1} value={selectedTransform.mountingDeg}
                  onChange={e => {
                    const v = +e.target.value;
                    setSelectedTransform(t => t ? { ...t, mountingDeg: v } : t);
                    controlsRef.current?.builderSetSelectedMounting?.(v);
                  }}
                  style={{ width: '100%', accentColor: '#38bdf8' }}
                />
              </div>
            )}

            {selectedTransform.kind === 'tracker' && selectedTransform.roofHeight != null && (
              <div>
                <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>{projectMode ? 'Trackerbasis' : 'Dachhöhe'}: {distanceLabel(selectedTransform.roofHeight)}</div>
                {projectMode&&<TrackerHeightGuide controls={controlsRef} selected/>}
                {projectMode&&<label>Aufstellhöhe über Boden (m)<input type="number" min="0" max="54" step="0.01" value={selectedTransform.roofHeight/PROJECT_UNITS_PER_METRE} onChange={e=>{const h=Math.max(0,Math.min(540,Number(e.target.value)*PROJECT_UNITS_PER_METRE));setSelectedTransform(t=>t?{...t,roofHeight:h}:t);controlsRef.current?.builderSetSelectedHeight?.(h);}}/></label>}
                <input type="range" min={0} max={1000} step={1}
                  value={Math.round(Math.sqrt(Math.max(0, selectedTransform.roofHeight) / 540) * 1000)}
                  onChange={e => {
                    const h = Math.round(540 * Math.pow(+e.target.value / 1000, 2));
                    setSelectedTransform(t => t ? { ...t, roofHeight: h } : t);
                    controlsRef.current?.builderSetSelectedHeight?.(h);
                  }}
                  style={{ width: '100%', accentColor: '#a855f7' }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  {[-5, -1, 1, 5].map(d => (
                    <button key={d} onClick={() => {
                      const h = Math.min(540, Math.max(0, (selectedTransform.roofHeight ?? 0) + d));
                      setSelectedTransform(t => t ? { ...t, roofHeight: h } : t);
                      controlsRef.current?.builderSetSelectedHeight?.(h);
                    }} style={{
                      flex: 1, padding: '3px 0', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                      background: 'rgba(255,255,255,.06)', color: '#aaa', border: '1px solid rgba(255,255,255,.1)',
                    }}>{d > 0 ? `+${projectMode?distanceLabel(d):d}` : projectMode?distanceLabel(d):d}</button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={builderMoveSelected}
                style={{
                  flex: 1, padding: '7px 8px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: builderMovingSelected ? 'rgba(74,222,128,.2)' : 'rgba(56,189,248,.16)',
                  color: builderMovingSelected ? '#4ade80' : '#38bdf8',
                  border: builderMovingSelected ? '1px solid rgba(74,222,128,.4)' : '1px solid rgba(56,189,248,.35)',
                }}
              >{builderMovingSelected ? '📍 Klicken zum Fixieren…' : '📍 Verschieben'}</button>
              <button
                onClick={builderDuplicateSelectedObj}
                title="Strg+D"
                style={{
                  padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: 'rgba(168,85,247,.16)', color: '#d8b4fe', border: '1px solid rgba(168,85,247,.35)',
                }}
              >⧉</button>
              <button
                onClick={() => builderDeleteObject(selectedBuilderObjectId)}
                style={{
                  padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: 'rgba(244,63,94,.16)', color: '#fecdd3', border: '1px solid rgba(244,63,94,.35)',
                }}
              >🗑</button>
            </div>
            <div style={{ fontSize: 10, opacity: 0.5 }}>Tipp: Rechtsklick wählt aus · Strg+D dupliziert.</div>
          </div>
        ) : (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,.08)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 11, opacity: 0.6, fontWeight: 700 }}>Nächste Platzierung</div>
            <div>
              <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>Rotation: {builderRotation}°</div>
              <input type="range" min={0} max={360} step={5} value={builderRotation}
                onChange={e => { const v = +e.target.value; setBuilderRotation(v); controlsRef.current?.builderSetRotation?.(v); }}
                style={{ width: '100%', accentColor: '#38bdf8' }}
              />
            </div>
            {builderSubTab === 'house' && (
              <div>
                <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>Größe: {builderScale.toFixed(2)}x</div>
                <input type="range" min={scaleToSlider(0.1)} max={scaleToSlider(4)} step={(scaleToSlider(4) - scaleToSlider(0.1)) / 300} value={scaleToSlider(builderScale)}
                  onChange={e => { const v = sliderToScale(+e.target.value); setBuilderScale(v); controlsRef.current?.builderSetScaleMultiplier?.(v); }}
                  style={{ width: '100%', accentColor: '#38bdf8' }}
                />
                {builderScale !== 1 && (
                  <button
                    onClick={() => { setBuilderScale(1); controlsRef.current?.builderSetScaleMultiplier?.(1); }}
                    style={{
                      marginTop: 4, padding: '3px 8px', fontSize: 11, borderRadius: 6, cursor: 'pointer',
                      background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.12)', color: 'rgba(255,255,255,.6)',
                    }}
                  >↺ zurücksetzen</button>
                )}
              </div>
            )}
            <div style={{ fontSize: 10, opacity: 0.5 }}>Tipp: Rechtsklick auf ein platziertes Objekt bearbeitet es.</div>
          </div>
        )}

        {/* Sub-tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
          {([['house', '🏠 Haus'], ['tracker', '☀️ Tracker'], ['obstacle', '🌳 Hindernis']] as const).map(([st, label]) => (
            <button
              key={st}
              onClick={() => { builderCancel(); setBuilderSubTab(st); }}
              style={{
                flex: 1, padding: '8px 4px', fontSize: 11, cursor: 'pointer', fontWeight: 600,
                background: builderSubTab === st ? 'rgba(56,189,248,.18)' : 'transparent',
                color: builderSubTab === st ? '#38bdf8' : 'rgba(255,255,255,.5)',
                border: 'none', borderBottom: builderSubTab === st ? '2px solid #38bdf8' : '2px solid transparent',
              }}
            >{label}</button>
          ))}
        </div>

        <div style={{ padding: '14px 16px' }}>

          {/* HOUSE SUB-TAB */}
          {builderSubTab === 'house' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 2 }}>Hausmodell wählen:</div>
              {BUILDER_CATEGORY_ORDER.map((cat) => {
                const items = BUILDER_HOUSE_MODELS
                  .map((m, i) => ({ m, i }))
                  .filter(({ m }) => m.category === cat);
                if (!items.length) return null;
                const isOpen = builderOpenCategory === cat;
                return (
                  <div key={cat} style={{ border: '1px solid rgba(255,255,255,.1)', borderRadius: 8, overflow: 'hidden' }}>
                    <button
                      onClick={() => setBuilderOpenCategory(isOpen ? null : cat)}
                      style={{
                        width: '100%', padding: '8px 10px', textAlign: 'left', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                        background: isOpen ? 'rgba(56,189,248,.14)' : 'rgba(255,255,255,.04)',
                        color: isOpen ? '#38bdf8' : 'rgba(255,255,255,.8)',
                        border: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}
                    >
                      <span>{cat}</span>
                      <span style={{ opacity: 0.55, fontSize: 11 }}>{items.length} {isOpen ? '▲' : '▼'}</span>
                    </button>
                    {isOpen && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: 6 }}>
                        {items.map(({ m, i }) => (
                          <button
                            key={i}
                            onClick={() => {
                              setSelHouseIdx(i);
                              const ctrl = controlsRef.current;
                              if (!ctrl) return;
                              ctrl.builderCancelPlacing?.();
                              ctrl.builderSetRotation?.(builderRotation);
                              ctrl.builderSetScaleMultiplier?.(builderScale);
                              ctrl.builderStartPlacing?.({
                                kind: 'house', label: m.label, modelFile: m.file,
                                scale: m.scale, fitSize: m.fitSize, ghostSize: m.ghostSize,
                                preserveMaterials: m.preserveMaterials, centerOnPlacement: m.centerOnPlacement,
                              });
                              setBuilderPlacing(true);
                            }}
                            style={{
                              padding: '7px 10px', borderRadius: 7, cursor: 'pointer', textAlign: 'left', fontSize: 12.5,
                              background: selHouseIdx === i ? 'rgba(56,189,248,.22)' : 'rgba(255,255,255,.04)',
                              border: selHouseIdx === i ? '1px solid rgba(56,189,248,.5)' : '1px solid rgba(255,255,255,.08)',
                              color: '#fff',
                            }}
                          >{m.label}</button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* TRACKER SUB-TAB */}
          {builderSubTab === 'tracker' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Tracker-Typ:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(['fixed', 'single', 'limited', 'unlimited'] as const).map(t => (
                  <button key={t} onClick={() => setSelTrackerType(t)} style={{
                    padding: '6px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    background: selTrackerType === t ? 'rgba(56,189,248,.25)' : 'rgba(255,255,255,.06)',
                    color: selTrackerType === t ? '#38bdf8' : '#aaa',
                    border: selTrackerType === t ? '1px solid rgba(56,189,248,.5)' : '1px solid rgba(255,255,255,.1)',
                  }}>{t}</button>
                ))}
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Neigung (neuer Tracker): {trackerMountDeg}°</div>
                <input type="range" min={0} max={45} step={5} value={trackerMountDeg}
                  onChange={e => setTrackerMountDeg(+e.target.value)}
                  style={{ width: '100%', accentColor: '#38bdf8' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Ausrichtung:</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['süd', 'nord', 'ost', 'west'] as const).map(c => (
                    <button key={c} onClick={() => setTrackerCompass(c)} style={{
                      flex: 1, padding: '6px 4px', borderRadius: 7, cursor: 'pointer', fontSize: 12,
                      background: trackerCompass === c ? 'rgba(168,85,247,.25)' : 'rgba(255,255,255,.06)',
                      color: trackerCompass === c ? '#c084fc' : '#aaa',
                      border: trackerCompass === c ? '1px solid rgba(168,85,247,.5)' : '1px solid rgba(255,255,255,.1)',
                    }}>{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>{projectMode ? 'Trackerbasis (neuer Tracker)' : 'Dachhöhe (neuer Tracker)'}: {distanceLabel(trackerRoofHeight)}</div>
                {projectMode&&<TrackerHeightGuide controls={controlsRef}/>}
                {projectMode&&<label>Aufstellhöhe über Boden (m)<input type="number" min="0" max="54" step="0.01" value={trackerRoofHeight/PROJECT_UNITS_PER_METRE} onChange={e=>setTrackerRoofHeight(Math.max(0,Math.min(540,Number(e.target.value)*PROJECT_UNITS_PER_METRE)))}/></label>}
                {/* Quadratisches Mapping: im häufig genutzten Bereich (0–150) feinfühlig,
                    oben grob — ein linearer 0–540-Slider war viel zu empfindlich. */}
                <input type="range" min={0} max={1000} step={1}
                  value={Math.round(Math.sqrt(Math.max(0, trackerRoofHeight) / 540) * 1000)}
                  onChange={e => setTrackerRoofHeight(Math.round(540 * Math.pow(+e.target.value / 1000, 2)))}
                  style={{ width: '100%', accentColor: '#a855f7' }}
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                  {[-5, -1, 1, 5].map(d => (
                    <button key={d} onClick={() => setTrackerRoofHeight(h => Math.min(540, Math.max(0, h + d)))} style={{
                      flex: 1, padding: '3px 0', borderRadius: 6, cursor: 'pointer', fontSize: 11,
                      background: 'rgba(255,255,255,.06)', color: '#aaa', border: '1px solid rgba(255,255,255,.1)',
                    }}>{d > 0 ? `+${projectMode?distanceLabel(d):d}` : projectMode?distanceLabel(d):d}</button>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 11, opacity: 0.55, lineHeight: 1.4 }}>
                ℹ️ <b>Ausrichtung</b> = Grundrichtung; der <b>Rotations-Slider</b> oben dreht
                stufenlos weiter (für schräg stehende Häuser). Das Tracking folgt der
                gedrehten Richtung physikalisch korrekt.
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                  Module: {trackerPanelCount} Stk. — ≈ {(TRACKER_STANDARD_KW * (trackerPanelCount / 4) * (PANEL_COLORS[trackerPanelColorIdx]?.eff ?? 1)).toFixed(2)} kWp
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {TRACKER_PANEL_COUNTS.map(n => (
                    <button key={n} onClick={() => setTrackerPanelCount(n)} style={{
                      flex: 1, padding: '6px 0', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: trackerPanelCount === n ? 'rgba(250,204,21,.22)' : 'rgba(255,255,255,.06)',
                      color: trackerPanelCount === n ? '#facc15' : '#aaa',
                      border: trackerPanelCount === n ? '1px solid rgba(250,204,21,.5)' : '1px solid rgba(255,255,255,.1)',
                    }}>{n}</button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  {([['portrait', '⬛ Hochkant'], ['landscape', '▬ Quer']] as const).map(([v, label]) => (
                    <button key={v} onClick={() => setTrackerPanelOrientation(v)} style={{
                      flex: 1, padding: '6px 0', borderRadius: 7, cursor: 'pointer', fontSize: 11, fontWeight: 600,
                      background: trackerPanelOrientation === v ? 'rgba(250,204,21,.22)' : 'rgba(255,255,255,.06)',
                      color: trackerPanelOrientation === v ? '#facc15' : '#aaa',
                      border: trackerPanelOrientation === v ? '1px solid rgba(250,204,21,.5)' : '1px solid rgba(255,255,255,.1)',
                    }}>{label}</button>
                  ))}
                </div>
                <div style={{ fontSize: 11, opacity: 0.55, marginTop: 3 }}>
                  Raster: {trackerGridLabel(trackerPanelCount, trackerPanelOrientation)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Modulfarbe:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {PANEL_COLORS.map((c, i) => (
                    <button key={c.label} onClick={() => setTrackerPanelColorIdx(i)}
                      title={`${c.label} — Wirkungsgrad ${(c.eff * 100).toFixed(0)}%`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 7,
                        cursor: 'pointer', fontSize: 11, fontWeight: 600,
                        background: trackerPanelColorIdx === i ? 'rgba(56,189,248,.18)' : 'rgba(255,255,255,.06)',
                        color: trackerPanelColorIdx === i ? '#e2e8f0' : '#aaa',
                        border: trackerPanelColorIdx === i ? '1px solid rgba(56,189,248,.5)' : '1px solid rgba(255,255,255,.1)',
                      }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: c.hex, border: '1px solid rgba(255,255,255,.25)' }} />
                      {c.label}{c.eff < 1 ? ` (${(c.eff * 100).toFixed(0)}%)` : ''}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Gestellfarbe:</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {FRAME_COLORS.map((c, i) => (
                    <button key={c.label} onClick={() => setTrackerFrameColorIdx(i)} style={{
                      display: 'flex', alignItems: 'center', gap: 5, padding: '5px 8px', borderRadius: 7,
                      cursor: 'pointer', fontSize: 11, fontWeight: 600,
                      background: trackerFrameColorIdx === i ? 'rgba(168,85,247,.18)' : 'rgba(255,255,255,.06)',
                      color: trackerFrameColorIdx === i ? '#e2e8f0' : '#aaa',
                      border: trackerFrameColorIdx === i ? '1px solid rgba(168,85,247,.5)' : '1px solid rgba(255,255,255,.1)',
                    }}>
                      <span style={{ width: 12, height: 12, borderRadius: 3, background: c.hex ?? 'linear-gradient(135deg,#c0c4c9,#8f959c)', border: '1px solid rgba(255,255,255,.25)' }} />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* OBSTACLE SUB-TAB */}
          {builderSubTab === 'obstacle' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.7 }}>Hindernis-Typ:</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {([['tree', '🌳 Baum'], ['wall', '🧱 Wand']] as const).map(([t, label]) => (
                  <button key={t} onClick={() => setSelObstacle(t)} style={{
                    flex: 1, padding: '8px 4px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    background: selObstacle === t ? 'rgba(56,189,248,.22)' : 'rgba(255,255,255,.06)',
                    color: selObstacle === t ? '#38bdf8' : '#aaa',
                    border: selObstacle === t ? '1px solid rgba(56,189,248,.45)' : '1px solid rgba(255,255,255,.1)',
                  }}>{label}</button>
                ))}
              </div>
              {selObstacle === 'tree' ? (
                <>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Baumhöhe: {distanceLabel(treeHeight)}</div>
                    <input type="range" min={5} max={60} step={1} value={treeHeight}
                      onChange={e => setTreeHeight(+e.target.value)}
                      style={{ width: '100%', accentColor: '#228B22' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Kronenradius: {distanceLabel(treeCrownRadius)}</div>
                    <input type="range" min={2} max={20} step={1} value={treeCrownRadius}
                      onChange={e => setTreeCrownRadius(+e.target.value)}
                      style={{ width: '100%', accentColor: '#228B22' }}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Breite: {distanceLabel(obstacleW)}</div>
                    <input type="range" min={2} max={450} step={1} value={obstacleW}
                      onChange={e => setObstacleW(+e.target.value)}
                      style={{ width: '100%', accentColor: '#38bdf8' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Höhe: {distanceLabel(obstacleH)}</div>
                    <input type="range" min={2} max={300} step={1} value={obstacleH}
                      onChange={e => setObstacleH(+e.target.value)}
                      style={{ width: '100%', accentColor: '#38bdf8' }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Tiefe: {distanceLabel(obstacleD)}</div>
                    <input type="range" min={1} max={50} step={1} value={obstacleD}
                      onChange={e => setObstacleD(+e.target.value)}
                      style={{ width: '100%', accentColor: '#38bdf8' }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!builderPlacing ? (
              <button
                onClick={builderBeginPlace}
                style={{
                  padding: '10px 0', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14,
                  background: 'linear-gradient(135deg, rgba(56,189,248,.3), rgba(168,85,247,.28))',
                  color: '#E6F7FF', border: '1px solid rgba(56,189,248,.5)',
                  boxShadow: '0 6px 20px rgba(56,189,248,.2)',
                }}
              >
                {builderSubTab === 'house' ? '🏠 Haus platzieren' : builderSubTab === 'tracker' ? '☀️ Tracker platzieren' : '🌳 Hindernis platzieren'}
              </button>
            ) : (
              <button
                onClick={builderCancel}
                style={{
                  padding: '10px 0', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14,
                  background: 'rgba(244,63,94,.2)', color: '#ffeef2', border: '1px solid rgba(244,63,94,.45)',
                }}
              >✕ Abbrechen</button>
            )}
            {builderPlacedCount > 0 && (
              <button
                onClick={builderClearAll}
                style={{
                  padding: '8px 0', borderRadius: 10, cursor: 'pointer', fontSize: 13,
                  background: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.6)',
                  border: '1px solid rgba(255,255,255,.14)',
                }}
              >🗑 Alles löschen</button>
            )}
          </div>

          {builderObjects.length > 0 && (
            <div style={{
              marginTop: 12,
              paddingTop: 12,
              borderTop: '1px solid rgba(255,255,255,.1)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 12, opacity: 0.72, fontWeight: 700 }}>Objekte</div>
                {selectedBuilderObjectId && (
                  <button
                    onClick={() => builderDeleteObject(selectedBuilderObjectId)}
                    style={{
                      padding: '5px 8px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 11,
                      background: 'rgba(244,63,94,.18)',
                      color: '#ffe4e6',
                      border: '1px solid rgba(244,63,94,.38)',
                    }}
                  >Auswahl löschen</button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 420, overflowY: 'auto', paddingRight: 2 }}>
                {builderObjects.map((obj, i) => {
                  const selected = selectedBuilderObjectId === obj.id;
                  return (
                    <div
                      key={obj.id}
                      onClick={() => builderSelectObject(obj.id)}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        alignItems: 'center',
                        gap: 8,
                        padding: '7px 8px',
                        borderRadius: 9,
                        cursor: 'pointer',
                        background: selected ? 'rgba(56,189,248,.18)' : 'rgba(255,255,255,.045)',
                        border: selected ? '1px solid rgba(56,189,248,.45)' : '1px solid rgba(255,255,255,.08)',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {i + 1}. {obj.label}
                        </div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.45)', marginTop: 2 }}>
                          {obj.kind === 'house' ? 'Haus' : obj.kind === 'tracker' ? 'Tracker' : 'Hindernis'}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); builderDeleteObject(obj.id); }}
                        title="Dieses Objekt löschen"
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: 8,
                          cursor: 'pointer',
                          background: 'rgba(244,63,94,.14)',
                          color: '#fecdd3',
                          border: '1px solid rgba(244,63,94,.3)',
                        }}
                      >✕</button>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)' }}>
                Objekt anklicken oder in der 3D-Szene auswählen.
              </div>
            </div>
          )}

          {builderPlacing && (
            <div style={{ marginTop: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(56,189,248,.1)', border: '1px solid rgba(56,189,248,.25)', fontSize: 12, color: '#7dd3fc' }}>
              Klick in die 3D-Szene zum Platzieren. Mehrfach klickbar.
            </div>
          )}

          {isDigitalTwin && builderObjects.length > 0 && (
            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {isAdminToken && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', fontWeight: 700 }}>
                    USER ID (für wen speichern?)
                  </label>
                  <input
                    type="number"
                    min={1}
                    placeholder="z.B. 2"
                    value={adminTargetUserId}
                    onChange={e => setAdminTargetUserId(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '7px 10px',
                      borderRadius: 8,
                      background: 'rgba(255,255,255,.07)',
                      border: '1px solid rgba(255,255,255,.18)',
                      color: '#fff',
                      fontSize: 13,
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              )}
              <button
                onClick={() => saveHouseConfig(isAdminToken ? adminTargetUserId || undefined : undefined)}
                disabled={houseConfigStatus === 'saving' || (isAdminToken && !adminTargetUserId)}
                style={{
                  width: '100%',
                  padding: '10px 0',
                  borderRadius: 10,
                  cursor: (houseConfigStatus === 'saving' || (isAdminToken && !adminTargetUserId)) ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                  opacity: isAdminToken && !adminTargetUserId ? 0.45 : 1,
                  background: houseConfigStatus === 'saved'
                    ? 'rgba(34,197,94,.22)'
                    : houseConfigStatus === 'error'
                    ? 'rgba(244,63,94,.22)'
                    : 'rgba(56,189,248,.18)',
                  color: houseConfigStatus === 'saved'
                    ? '#86efac'
                    : houseConfigStatus === 'error'
                    ? '#fecdd3'
                    : '#7dd3fc',
                  border: `1px solid ${houseConfigStatus === 'saved' ? 'rgba(34,197,94,.4)' : houseConfigStatus === 'error' ? 'rgba(244,63,94,.4)' : 'rgba(56,189,248,.35)'}`,
                }}
              >
                {houseConfigStatus === 'saving' ? '⏳ Speichern…'
                  : houseConfigStatus === 'saved' ? '✅ Gespeichert'
                  : houseConfigStatus === 'error' ? '❌ Fehler beim Speichern'
                  : isAdminToken ? `💾 Für User ${adminTargetUserId || '?'} speichern` : '💾 Haus-Config speichern'}
              </button>
            </div>
          )}

          {!isDigitalTwin && authed && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,.08)' }}>
              <button
                onClick={() => setMyModelsOpen((o) => !o)}
                style={{
                  width: '100%', padding: '10px 0', borderRadius: 10, cursor: 'pointer',
                  fontSize: 13, fontWeight: 700,
                  background: 'rgba(168,85,247,.16)', color: '#e9d5ff',
                  border: '1px solid rgba(168,85,247,.35)',
                }}
              >
                📦 Meine Modelle {myModelsOpen ? '▲' : '▼'}
              </button>

              {myModelsOpen && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button
                    onClick={saveCurrentAsMyModel}
                    disabled={builderObjects.length === 0 || myModelsStatus === 'saving'}
                    style={{
                      width: '100%', padding: '9px 0', borderRadius: 10,
                      cursor: (builderObjects.length === 0 || myModelsStatus === 'saving') ? 'not-allowed' : 'pointer',
                      opacity: builderObjects.length === 0 ? 0.45 : 1,
                      fontSize: 13, fontWeight: 700,
                      background: myModelsStatus === 'saved' ? 'rgba(34,197,94,.22)'
                        : myModelsStatus === 'error' ? 'rgba(244,63,94,.22)' : 'rgba(56,189,248,.18)',
                      color: myModelsStatus === 'saved' ? '#86efac'
                        : myModelsStatus === 'error' ? '#fecdd3' : '#7dd3fc',
                      border: '1px solid rgba(56,189,248,.35)',
                    }}
                  >
                    {myModelsStatus === 'saving' ? '⏳ Speichern…'
                      : myModelsStatus === 'saved' ? '✅ Gespeichert'
                      : myModelsStatus === 'error' ? '❌ Fehler beim Speichern'
                      : '💾 Aktuelles Modell speichern'}
                  </button>

                  {/* Szene als Datei rein/raus — trägt eine fertige Szene von
                      einer Umgebung in die andere (siehe importSceneFile). */}
                  <div
                    onDragOver={(e) => { e.preventDefault(); setImportDragOver(true); }}
                    onDragLeave={() => setImportDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setImportDragOver(false);
                      importSceneFile(e.dataTransfer?.files?.[0]);
                    }}
                    onClick={() => importInputRef.current?.click()}
                    style={{
                      padding: '12px 10px', borderRadius: 10, cursor: 'pointer', textAlign: 'center',
                      fontSize: 12.5, lineHeight: 1.45,
                      color: importDragOver ? '#e9d5ff' : 'rgba(255,255,255,.55)',
                      background: importDragOver ? 'rgba(168,85,247,.18)' : 'rgba(255,255,255,.04)',
                      border: `1px dashed ${importDragOver ? 'rgba(168,85,247,.6)' : 'rgba(255,255,255,.18)'}`,
                      transition: 'background .12s, border-color .12s, color .12s',
                    }}
                  >
                    ⬆️ Szene-JSON hierher ziehen<br />
                    <span style={{ fontSize: 11.5, opacity: .7 }}>oder klicken zum Auswählen</span>
                  </div>
                  <input
                    ref={importInputRef}
                    type="file"
                    accept="application/json,.json"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      importSceneFile(e.target.files?.[0]);
                      // Zurücksetzen, damit dieselbe Datei erneut gewählt
                      // werden kann (onChange feuert sonst nicht nochmal).
                      e.target.value = '';
                    }}
                  />

                  <button
                    onClick={exportCurrentSceneFile}
                    disabled={builderObjects.length === 0}
                    style={{
                      width: '100%', padding: '8px 0', borderRadius: 10,
                      cursor: builderObjects.length === 0 ? 'not-allowed' : 'pointer',
                      opacity: builderObjects.length === 0 ? 0.45 : 1,
                      fontSize: 12.5, fontWeight: 600,
                      background: 'rgba(255,255,255,.06)', color: 'rgba(255,255,255,.75)',
                      border: '1px solid rgba(255,255,255,.16)',
                    }}
                  >
                    ⬇️ Szene als Datei exportieren
                  </button>

                  {myModelsError && (
                    <div style={{ fontSize: 12, color: '#fecdd3', padding: '6px 8px', borderRadius: 8, background: 'rgba(244,63,94,.1)', border: '1px solid rgba(244,63,94,.25)' }}>
                      {myModelsError}
                    </div>
                  )}

                  {myModels.length === 0 ? (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)' }}>
                      Noch keine gespeicherten Modelle.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                      {myModels.map((m) => (
                        <div key={m.id} style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                          padding: '7px 9px', borderRadius: 8,
                          background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)',
                        }}>
                          <span style={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.name}
                          </span>
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            <button
                              onClick={() => loadMyModel(m.id)}
                              style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11.5, cursor: 'pointer', background: 'rgba(56,189,248,.18)', color: '#7dd3fc', border: '1px solid rgba(56,189,248,.35)' }}
                            >Laden</button>
                            <button
                              onClick={() => deleteMyModel(m.id)}
                              style={{ padding: '4px 8px', borderRadius: 6, fontSize: 11.5, cursor: 'pointer', background: 'rgba(244,63,94,.14)', color: '#fecdd3', border: '1px solid rgba(244,63,94,.3)' }}
                            >Löschen</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Intro / Info Modal (bereits futuristisch) */}
    {showRangeSimDone && rangeSimResult && (
      <div
        style={{
          position:'absolute', inset:0, zIndex:10000,
          display:'grid', placeItems:'center',
          background:'rgba(2,8,23,0.55)',
          backdropFilter:'blur(14px) saturate(130%)',
          WebkitBackdropFilter:'blur(14px) saturate(130%)',
          pointerEvents:'auto',
        }}
        onClick={() => setShowRangeSimDone(false)}
      >
        <div
          onClick={e => e.stopPropagation()}
          style={{
            width:'min(92vw,480px)', padding:2,
            borderRadius:20,
            background:'linear-gradient(135deg,rgba(56,189,248,.9),rgba(168,85,247,.9))',
            boxShadow:'0 24px 72px rgba(0,0,0,.6)',
          }}
        >
          <div style={{
            background:'rgba(14,17,26,0.96)', borderRadius:18,
            padding:'36px 40px', textAlign:'center',
            fontFamily:'Inter,system-ui,sans-serif', color:'#E8EEF8',
          }}>
            <div style={{ fontSize:48, marginBottom:12 }}>✅</div>
            <h2 style={{ margin:'0 0 8px', fontSize:24, fontWeight:800 }}>Simulation abgeschlossen</h2>
            <p style={{ margin:'0 0 24px', color:'rgba(255,255,255,.55)', fontSize:14 }}>
              Zeitraum: {rangeSimResult.days} Tag{rangeSimResult.days !== 1 ? 'e' : ''}
            </p>
            <div style={{
              display:'flex', gap:16, justifyContent:'center', marginBottom:28,
            }}>
              <div style={{
                background:'rgba(56,189,248,.12)', border:'1px solid rgba(56,189,248,.3)',
                borderRadius:14, padding:'16px 24px', flex:1,
              }}>
                <div style={{ fontSize:11, color:'rgba(255,255,255,.45)', marginBottom:4 }}>Gesamt aller Panels</div>
                <div style={{ fontSize:28, fontWeight:800, color:'#38bdf8' }}>{rangeSimResult.totalKWh.toFixed(2)}</div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,.5)' }}>kWh</div>
              </div>
              <div style={{
                background:'rgba(168,85,247,.12)', border:'1px solid rgba(168,85,247,.3)',
                borderRadius:14, padding:'16px 24px', flex:1,
              }}>
                <div style={{ fontSize:11, color:'rgba(255,255,255,.45)', marginBottom:4 }}>Ø pro Tag</div>
                <div style={{ fontSize:28, fontWeight:800, color:'#a855f7' }}>
                  {(rangeSimResult.totalKWh / rangeSimResult.days).toFixed(2)}
                </div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,.5)' }}>kWh/Tag</div>
              </div>
            </div>
            <button
              onClick={() => setShowRangeSimDone(false)}
              style={{
                padding:'12px 36px', borderRadius:12, cursor:'pointer', fontSize:15, fontWeight:700,
                background:'linear-gradient(135deg,rgba(56,189,248,.22),rgba(168,85,247,.22))',
                color:'#E6F7FF', border:'1px solid rgba(56,189,248,.45)',
              }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )}

    {showIntroInfo && (
      <div
        style={{
          position:'absolute',
          inset:0,
          zIndex:10000,
          display:'grid',
          placeItems:'center',
          background:'rgba(2,8,23,0.45)',
          backdropFilter:'blur(12px) saturate(120%)',
          WebkitBackdropFilter:'blur(12px) saturate(120%)',
          pointerEvents:'auto'
        }}
        onClick={() => setShowIntroInfo(false)}
      >
        <div
          onClick={(e)=>e.stopPropagation()}
          style={{
            maxWidth:1400,
            width:'min(96vw,1400px)',
            padding:1,
            borderRadius:18,
            background:'linear-gradient(135deg, rgba(56,189,248,.9), rgba(168,85,247,.9))',
            boxShadow:'0 28px 80px rgba(0,0,0,.55)'
          }}
        >
          <div
            style={{
              background:'rgba(16,18,24,0.9)',
              border:'1px solid rgba(255,255,255,0.08)',
              borderRadius:17,
              padding:28,
              /* Eigenes Scrolling. Vorher konnte man bei einem niedrigen Fenster
                 die SEITE scrollen, um an den Schliessen-Button zu kommen; seit
                 html/#root overflow:hidden tragen (damit ein Tracker-Klick die
                 Szene nicht mehr nach oben schiebt) geht das nicht mehr, und der
                 Dialog waere sonst unbedienbar geworden. */
              maxHeight:'88vh',
              overflowY:'auto',
              color:'#E8EEF8',
              fontFamily:'Inter, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif'
            }}
          >
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:14,marginBottom:10}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{
                  width:12,height:12,borderRadius:'50%',
                  background:'linear-gradient(135deg,#38bdf8,#a855f7)',
                  boxShadow:'0 0 14px rgba(56,189,248,.9)'
                }} />
                <div style={{fontWeight:800,letterSpacing:.2,fontSize:28}}>
                  Digital Twin — Solart Tracker
                </div>
                <span style={{
                  marginLeft:8,fontSize:13,padding:'3px 8px',borderRadius:999,
                  background:'rgba(56,189,248,.12)',border:'1px solid rgba(56,189,248,.35)',color:'#A5E9FF'
                }}>R&amp;D</span>
              </div>

              <button
                onClick={()=>setShowIntroInfo(false)}
                aria-label="Close"
                style={{
                  border:'1px solid rgba(255,255,255,.14)',
                  background:'rgba(255,255,255,.05)',
                  color:'#fff',borderRadius:10,cursor:'pointer',
                  padding:'8px 12px',transition:'all .15s ease', fontSize:16
                }}
                onMouseOver={(e)=>{ e.currentTarget.style.background='rgba(255,255,255,.09)'; }}
                onMouseOut ={(e)=>{ e.currentTarget.style.background='rgba(255,255,255,.05)'; }}
                title="Close"
              >✕</button>
            </div>

            <div style={{opacity:.98,lineHeight:1.75,fontSize:17}}>
              <p style={{margin:'10px 0 4px 0'}}>
                This digital twin is an actively developed technical tool for exploring solar-tracker behavior across locations,
                dates, and operating assumptions. It supports research, planning, and product improvement by making solar movement,
                tracking behavior, and energy estimates easier to inspect. Feedback from real use cases is very welcome, especially
                when it includes the location, date/time, setup, and what behavior you expected to see.
              </p>
            </div>

            <div style={{
              height:1, margin:'6px 0 22px 0',
              background:'linear-gradient(90deg, rgba(56,189,248,.0), rgba(56,189,248,.6), rgba(168,85,247,.6), rgba(168,85,247,0))',
              opacity:.6
            }} />

            <div style={{
              display:'grid',
              gridTemplateColumns:'repeat(auto-fit, minmax(380px, 1fr))',
              gap:22
            }}>
              <section>
                <div style={{fontWeight:800, fontSize:18, margin:'0 0 10px'}}>What you can do</div>
                <ul style={{margin:'0 0 6px 18px',padding:0, lineHeight:1.65, fontSize:15.5}}>
                  <li>Explore <b>geographic scenarios</b> by setting a city/coordinates and observing sun path &amp; expected yield.</li>
                  <li>Step through <b>time</b> (minutes → months), or run a fast, deterministic <b>range simulation</b> for totals.</li>
                  <li>Compare <b>tracker types</b> (fixed, single, dual limited/unlimited) and <b>orientations</b> (N/E/S/W) at different tilts.</li>
                  <li>Switch between <b>Ideal</b> and <b>Realistic</b> energy models to inspect geometric vs. loss-inclusive outputs.</li>
                  <li>Inspect <b>panel details</b> and a <b>summary table</b> aggregating energy by direction and tracker type.</li>
                </ul>
              </section>

              <section>
                <div style={{fontWeight:800, fontSize:18, margin:'0 0 10px'}}>How to use</div>
                <ul style={{margin:'0 0 6px 18px',padding:0, lineHeight:1.65, fontSize:15.5}}>
                  <li><b>Navigation:</b> drag to orbit, wheel/pinch to zoom; <i>North</i> realigns the camera.</li>
                  <li><b>Time control:</b> ⏯️ Start/Stop runs or pauses; ⏭️ Forward click steps one interval; hold for fast-forward.</li>
                  <li><b>Date &amp; range:</b> pick a start date and (optional) end date. Use ⚡ <i>Range Sim</i> to compute totals quickly.</li>
                  <li><b>Location:</b> use the city field (with autocomplete) to set latitude/longitude.</li>
                  <li><b>Energy model:</b> <i>Ideal</i> = geometry only; <i>Realistic</i> applies losses (IAM, temperature, soiling, PR, etc.).</li>
                  <li><b>Panels &amp; metrics:</b> click panels for info; use the tilt filter (0°/30°/35°/40°/45°/All); the table aggregates energy by direction/type.</li>
                  <li><b>Reset:</b> clears accumulated energy and resets the simulation date—useful before fresh comparisons.</li>
                </ul>
              </section>
            </div>

            <div style={{display:'flex',justifyContent:'flex-end',gap:12,marginTop:22}}>
              <button
                onClick={()=>setShowIntroInfo(false)}
                style={{
                  border:'1px solid rgba(56,189,248,.45)',
                  background:'linear-gradient(135deg, rgba(56,189,248,.18), rgba(168,85,247,.18))',
                  color:'#E6F7FF',
                  borderRadius:12,
                  cursor:'pointer',
                  padding:'10px 16px',
                  fontWeight:700,
                  boxShadow:'0 8px 22px rgba(56,189,248,.25)',
                  transition:'transform .08s ease, box-shadow .15s ease', fontSize:15.5
                }}
                onMouseDown={(e)=>{ e.currentTarget.style.transform='translateY(1px)'; }}
                onMouseUp={(e)=>{ e.currentTarget.style.transform='translateY(0)'; }}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
{/* Quick Controls (sichtbar über dem Canvas) */}
{false && !showSplash && activeTab === 'sim' && (
<div style={{  position:'absolute',
                  top: 70,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 50,
                  display:'flex',
                  gap: 8,
                  pointerEvents:'auto'
                }}>


  <button
    onClick={() => controlsRef.current?.toggleHouseMode?.()}
    title="Siedlung ↔ Einzelhaus"
    style={{
      padding:'10px 14px', borderRadius:12, cursor:'pointer',
      background:'linear-gradient(135deg, rgba(99,102,241,.22), rgba(168,85,247,.22))',
      color:'#EAF2FF', border:'1px solid rgba(168,85,247,.45)'
    }}
  >
    🏘️ / 🏠 Switch
  </button>
</div>
)}








{/* Floating Buttons (immer klickbar, unabhängig vom HUD) */}
<div style={{ position: 'absolute', bottom: 'calc(12px + env(safe-area-inset-bottom))', right: 'calc(12px + env(safe-area-inset-right))', display: 'flex', gap: 8, zIndex: 999, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 'calc(100vw - 24px)', fontFamily: 'ui-monospace, "SF Mono", "Fira Code", "JetBrains Mono", Menlo, Consolas, monospace' }}>
  <button
    style={{
      padding: '9px 14px',
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 12.5,
      letterSpacing: .3,
      background: 'rgba(6,14,20,.82)',
      backdropFilter: 'blur(8px)',
      color: '#8ff0d4',
      border: '1px solid rgba(94,234,212,.35)',
      boxShadow: '0 4px 16px rgba(0,0,0,.35)',
    }}
    onClick={() => {setHudHidden((h) => !h);if(projectMode&&window.innerWidth<=800)setProjectToolsOpen(false);}}
  >
    [ {hudHidden ? '🎛️ ui zeigen' : '🧹 ui ausblenden'} ]
  </button>

  <button
    style={{
      padding: '9px 14px',
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 12.5,
      letterSpacing: .3,
      background: 'rgba(6,14,20,.82)',
      backdropFilter: 'blur(8px)',
      color: '#38bdf8',
      border: '1px solid rgba(56,189,248,.35)',
      boxShadow: '0 4px 16px rgba(0,0,0,.35)',
    }}
    onClick={toggleFullscreen}
    title="Browser-Vollbild umschalten"
  >
    [ ⛶ vollbild ]
  </button>

  {/* --- DEV LOGS Button ------------------------------------------------ */}
  {authRole === 'admin' && (
  <button
    style={{
      padding: '9px 14px',
      borderRadius: 8,
      cursor: 'pointer',
      fontSize: 12.5,
      letterSpacing: .3,
      background: 'rgba(6,14,20,.82)',
      backdropFilter: 'blur(8px)',
      color: '#facc15',
      border: '1px solid rgba(250,204,21,.35)',
      boxShadow: '0 4px 16px rgba(0,0,0,.35)',
    }}
     onClick={async () => {
        await captureIpAndLog('dev-logs'); // ⬅️ IP jetzt!
        setDevLogOpen(true);
      }}
    title="Entwickler-Logs anzeigen"
  >
    [ 🧪 dev-logs ]
  </button>
  )}
  {/* ------------------------------------------------------------------- */}

  {/* --- NEW BUTTON: Alle Logs holen -------------------------------- */}
{authRole === 'admin' && (
  <button

  style={{
          padding: '9px 14px',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 12.5,
          letterSpacing: .3,
          background: 'rgba(6,14,20,.82)',
          backdropFilter: 'blur(8px)',
          color: '#facc15',
          border: '1px solid rgba(250,204,21,.35)',
          boxShadow: '0 4px 16px rgba(0,0,0,.35)',
        }}

onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();     // <— verhindert, dass der Click zum Backdrop durchläuft
    handleFetchAllLogs();
  }}

  >
    [ 🗒️ alle logs holen ]
  </button>
)}

</div>
{/* --- DEV LOGS Modal --------------------------------------------------- */}
            {devLogOpen && (
              <div
                style={{
                  position:'absolute', inset:0, zIndex:10050,
                  display:'grid', placeItems:'center',
                  background:'rgba(2,8,23,0.55)', backdropFilter:'blur(10px)'
                }}
                onClick={() => setDevLogOpen(false)}
              >
                <div
                  onClick={(e)=>e.stopPropagation()}
                  style={{
                    width:'min(920px, 96vw)',
                    maxHeight:'78vh',
                    overflow:'hidden',
                    display:'flex', flexDirection:'column',
                    gap:10,
                    padding:14, borderRadius:14,
                    background:'rgba(10,12,18,.95)',
                    border:'1px solid rgba(255,255,255,.12)',
                    color:'#EAF2FF', boxShadow:'0 18px 60px rgba(0,0,0,.55)',
                    fontFamily:'Inter, ui-monospace, Menlo, monospace'
                  }}
                >
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8}}>
                    <div style={{fontWeight:800, letterSpacing:.2}}>🧪 Developer Logs</div>
                    <div style={{display:'flex', gap:8}}>
                      <button
                        onClick={()=>{
                          const txt = devLogs.join('\n');
                          navigator.clipboard?.writeText(txt).then(()=>pushLog('Dev logs copied'));
                        }}
                        style={{ padding:'8px 10px', borderRadius:10, cursor:'pointer',
                          background:'transparent', color:'#cfe',
                          border:'1px solid rgba(255,255,255,.18)'}}
                      >
                        Copy
                      </button>
                      <button
                        onClick={() => { setDevLogs([]); pushLog('Dev logs cleared'); }}
                        style={{ padding:'8px 10px', borderRadius:10, cursor:'pointer',
                          background:'transparent', color:'#fec',
                          border:'1px solid rgba(255,255,255,.18)'}}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => setDevLogOpen(false)}
                        style={{ padding:'8px 10px', borderRadius:10, cursor:'pointer',
                          background:'linear-gradient(135deg, rgba(56,189,248,.22), rgba(168,85,247,.22))',
                          color:'#EAF2FF', border:'1px solid rgba(56,189,248,.45)'}}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      border:'1px solid rgba(255,255,255,.1)',
                      borderRadius:10,
                      padding:10,
                      background:'rgba(255,255,255,.03)',
                      overflow:'auto',
                      maxHeight:'60vh',
                      fontSize:12.5,
                      lineHeight:1.45,
                      whiteSpace:'pre-wrap'
                    }}
                  >
                    {devLogs.length === 0 ? (
                      <div style={{opacity:.7}}>Keine Logs bisher…</div>
                    ) : (
                      <ul style={{margin:0, padding:'0 0 0 16px'}}>
                        {devLogs.map((l, i)=>(
                          <li key={i} style={{margin:'2px 0'}}>{l}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* --------------------------------------------------------------------- */}
{adminOpen && (
  <div
    style={{
      position:'absolute', inset:0, zIndex:10050, display:'grid', placeItems:'center',
      background:'rgba(2,8,23,0.55)', backdropFilter:'blur(8px)'
    }}
    onClick={(e)=>{ if (e.target === e.currentTarget) { setSelectedLog(null); setAdminOpen(false); } }}
  >
    <div
      onClick={(e)=>e.stopPropagation()}
      style={{
        width:'min(1100px, 96vw)', maxHeight:'80vh',
        background:'#0b0f17', color:'#eaf2ff',
        border:'1px solid rgba(255,255,255,.12)', borderRadius:12,
        padding:14, fontFamily:'ui-monospace, Menlo, monospace', overflow:'hidden'
      }}
      role="dialog" aria-modal="true"
    >
      {/* Header */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
        <div style={{display:'flex', gap:12, alignItems:'center'}}>
          <strong style={{fontWeight:800, fontSize:16, letterSpacing:.2}}>Admin-Bereich</strong>
          <div style={{display:'flex', gap:4}}>
            <button
              onClick={()=>setAdminTab('logs')}
              style={{padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                background: adminTab==='logs' ? 'rgba(56,189,248,.2)' : 'transparent',
                color: adminTab==='logs' ? '#7dd3fc' : 'rgba(255,255,255,.55)',
                border:'1px solid ' + (adminTab==='logs' ? 'rgba(56,189,248,.4)' : 'rgba(255,255,255,.15)')}}
            >🗒️ Logs</button>
            <button
              onClick={()=>setAdminTab('users')}
              style={{padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                background: adminTab==='users' ? 'rgba(168,85,247,.22)' : 'transparent',
                color: adminTab==='users' ? '#e9d5ff' : 'rgba(255,255,255,.55)',
                border:'1px solid ' + (adminTab==='users' ? 'rgba(168,85,247,.4)' : 'rgba(255,255,255,.15)')}}
            >👤 Benutzer</button>
            <button
              onClick={()=>setAdminTab('logins')}
              style={{padding:'5px 12px', borderRadius:8, fontSize:12, fontWeight:700, cursor:'pointer',
                background: adminTab==='logins' ? 'rgba(250,204,21,.18)' : 'transparent',
                color: adminTab==='logins' ? '#fde68a' : 'rgba(255,255,255,.55)',
                border:'1px solid ' + (adminTab==='logins' ? 'rgba(250,204,21,.4)' : 'rgba(255,255,255,.15)')}}
            >🔑 Logins</button>
          </div>
        </div>
        <div style={{display:'flex', gap:8, alignItems:'center'}}>
          <div style={{fontSize:12, opacity:.8, minWidth:80, textAlign:'right'}}>{copyMsg}</div>
          <button
            onClick={()=>{ setSelectedLog(null); setAdminOpen(false); }}
            style={{padding:'6px 10px', borderRadius:10, cursor:'pointer', background:'transparent',
                    color:'#cfe', border:'1px solid rgba(255,255,255,.18)'}}
          >Close</button>
        </div>
      </div>

      {adminTab === 'users' ? (
        <div style={{maxHeight:'66vh', overflow:'auto'}}>
          <form onSubmit={createAdminUser} style={{display:'flex', gap:8, flexWrap:'wrap', alignItems:'end', marginBottom:12}}>
            <div>
              <div style={{fontSize:11, opacity:.7, marginBottom:4}}>Username</div>
              <input value={newUserName} onChange={e=>setNewUserName(e.target.value)}
                style={{padding:'7px 10px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:13}} />
            </div>
            <div>
              <div style={{fontSize:11, opacity:.7, marginBottom:4}}>Initial-Passwort</div>
              <input type="password" value={newUserPass} onChange={e=>setNewUserPass(e.target.value)}
                style={{padding:'7px 10px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:13}} />
            </div>
            <div>
              <div style={{fontSize:11, opacity:.7, marginBottom:4}}>Rolle</div>
              <select value={newUserRole} onChange={e=>setNewUserRole(e.target.value as 'user'|'admin')}
                style={{padding:'7px 10px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:13}}>
                <option value="user">user</option>
                <option value="admin">admin</option>
              </select>
            </div>
            <button type="submit" style={{padding:'8px 14px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700,
              background:'rgba(56,189,248,.2)', color:'#7dd3fc', border:'1px solid rgba(56,189,248,.4)'}}
            >+ Anlegen</button>
          </form>

          {adminUsersError && (
            <div style={{fontSize:12, color:'#fecdd3', padding:'6px 8px', borderRadius:8, background:'rgba(244,63,94,.1)', border:'1px solid rgba(244,63,94,.25)', marginBottom:10}}>
              {adminUsersError}
            </div>
          )}

          <table style={{width:'100%', borderCollapse:'collapse', fontSize:13}}>
            <thead>
              <tr style={{background:'rgba(255,255,255,.03)'}}>
                {['Username','Rolle','Status','Reset nötig','Letzter Login','Aktionen'].map(h=>(
                  <th key={h} style={{textAlign:'left', borderBottom:'1px solid #fff2', padding:'8px 10px', whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {adminUsers.map(u => (
                <tr key={u.id}>
                  <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a'}}>{u.username}</td>
                  <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a'}}>{u.role}</td>
                  <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a'}}>
                    <span style={{color: u.status === 'active' ? '#86efac' : '#fecdd3'}}>{u.status}</span>
                  </td>
                  <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a'}}>{u.mustResetPassword ? 'ja' : '—'}</td>
                  <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a', whiteSpace:'nowrap'}}>
                    {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('de-DE') : <span style={{opacity:.5}}>nie</span>}
                  </td>
                  <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a'}}>
                    <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                      <button onClick={()=>patchAdminUser(u.id, { role: u.role === 'admin' ? 'user' : 'admin' })}
                        style={{padding:'4px 8px', borderRadius:6, fontSize:11.5, cursor:'pointer', background:'rgba(168,85,247,.16)', color:'#e9d5ff', border:'1px solid rgba(168,85,247,.35)'}}
                      >{u.role === 'admin' ? 'zu User' : 'zu Admin'}</button>
                      <button onClick={()=>patchAdminUser(u.id, { status: u.status === 'active' ? 'blocked' : 'active' })}
                        style={{padding:'4px 8px', borderRadius:6, fontSize:11.5, cursor:'pointer',
                          background: u.status==='active' ? 'rgba(244,63,94,.14)' : 'rgba(34,197,94,.16)',
                          color: u.status==='active' ? '#fecdd3' : '#86efac',
                          border: '1px solid ' + (u.status==='active' ? 'rgba(244,63,94,.3)' : 'rgba(34,197,94,.35)')}}
                      >{u.status === 'active' ? 'Blockieren' : 'Entsperren'}</button>
                      <button onClick={()=>resetAdminUserPassword(u.id, u.username)}
                        style={{padding:'4px 8px', borderRadius:6, fontSize:11.5, cursor:'pointer', background:'rgba(56,189,248,.16)', color:'#7dd3fc', border:'1px solid rgba(56,189,248,.35)'}}
                      >PW zurücksetzen</button>
                      <button onClick={()=>showVisitsForUser(u.username)}
                        title="Zu den Logs wechseln, gefiltert auf diesen Benutzer"
                        style={{padding:'4px 8px', borderRadius:6, fontSize:11.5, cursor:'pointer', background:'rgba(250,204,21,.14)', color:'#fde68a', border:'1px solid rgba(250,204,21,.32)'}}
                      >Visits anzeigen</button>
                      <button onClick={()=>showLoginAttemptsForUser(u.username)}
                        title="Zu den Login-Versuchen wechseln, gefiltert auf diesen Benutzer"
                        style={{padding:'4px 8px', borderRadius:6, fontSize:11.5, cursor:'pointer', background:'rgba(249,115,22,.14)', color:'#fdba74', border:'1px solid rgba(249,115,22,.32)'}}
                      >Logins anzeigen</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : adminTab === 'logins' ? (
        <div style={{maxHeight:'66vh', overflow:'auto'}}>
          {/* Filter: Login-Versuche nach Benutzer / IP / Erfolg eingrenzen */}
          <form
            onSubmit={(e)=>{ e.preventDefault(); fetchLoginAttemptsFiltered(); }}
            style={{display:'flex', gap:8, flexWrap:'wrap', alignItems:'end', marginBottom:10}}
          >
            <div>
              <div style={{fontSize:11, opacity:.7, marginBottom:4}}>Benutzer</div>
              <input value={loginFilterUser} onChange={e=>setLoginFilterUser(e.target.value)}
                placeholder="z.B. admin"
                style={{padding:'6px 9px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:12.5, width:130}} />
            </div>
            <div>
              <div style={{fontSize:11, opacity:.7, marginBottom:4}}>IP</div>
              <input value={loginFilterIp} onChange={e=>setLoginFilterIp(e.target.value)}
                placeholder="z.B. 62.40."
                style={{padding:'6px 9px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:12.5, width:130}} />
            </div>
            <div>
              <div style={{fontSize:11, opacity:.7, marginBottom:4}}>Erfolg</div>
              <select value={loginFilterSuccess} onChange={e=>setLoginFilterSuccess(e.target.value as ''|'true'|'false')}
                style={{padding:'6px 9px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:12.5}}>
                <option value="">Alle</option>
                <option value="true">Nur erfolgreich</option>
                <option value="false">Nur fehlgeschlagen</option>
              </select>
            </div>
            <button type="submit" style={{padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12.5, fontWeight:700,
              background:'rgba(250,204,21,.18)', color:'#fde68a', border:'1px solid rgba(250,204,21,.4)'}}
            >Filtern</button>
            <button type="button"
              onClick={()=>{ setLoginFilterUser(''); setLoginFilterIp(''); setLoginFilterSuccess(''); fetchLoginAttemptsFiltered({ username:'', ip:'', success:'' }); }}
              style={{padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12.5,
                background:'transparent', color:'rgba(255,255,255,.6)', border:'1px solid rgba(255,255,255,.18)'}}
            >Zurücksetzen</button>
            <div style={{fontSize:11.5, opacity:.6, alignSelf:'center'}}>{loginAttempts.length} Einträge</div>
          </form>

          {loginAttempts.length === 0 ? (
            <div style={{opacity:.7, padding:12}}>Keine Einträge.</div>
          ) : (
            <table style={{width:'100%', borderCollapse:'collapse', fontSize:13, fontVariantNumeric:'tabular-nums'}}>
              <thead>
                <tr style={{background:'rgba(255,255,255,.03)'}}>
                  {['ts','user','erfolg','grund','ip'].map(h=>(
                    <th key={h} style={{textAlign:'left', borderBottom:'1px solid #fff2', padding:'8px 10px', whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loginAttempts.map((a:any)=>(
                  <tr key={a.id}>
                    <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a', whiteSpace:'nowrap'}}>{a.ts}</td>
                    <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a', whiteSpace:'nowrap'}}>{a.username}</td>
                    <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a', whiteSpace:'nowrap'}}>
                      <span style={{
                        fontSize:11, padding:'2px 7px', borderRadius:5,
                        background: a.success ? 'rgba(34,197,94,.16)' : 'rgba(244,63,94,.14)',
                        color: a.success ? '#86efac' : '#fecdd3',
                        border: '1px solid ' + (a.success ? 'rgba(34,197,94,.35)' : 'rgba(244,63,94,.3)')
                      }}>{a.success ? 'erfolgreich' : 'fehlgeschlagen'}</span>
                    </td>
                    <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a', whiteSpace:'nowrap'}}>{a.reason}</td>
                    <td style={{padding:'8px 10px', borderBottom:'1px solid #ffffff1a', whiteSpace:'nowrap'}}>{a.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
      <>
      {/* Optional: Serverdata */}
      {serverData && (
        <details style={{marginBottom:10}}>
          <summary>Serverdata</summary>
          <pre>{serverData}</pre>
        </details>
      )}

      {/* Filter: Logs nach Benutzer / IP / Pfad eingrenzen */}
      <form
        onSubmit={(e)=>{ e.preventDefault(); fetchAdminLogsFiltered(); }}
        style={{display:'flex', gap:8, flexWrap:'wrap', alignItems:'end', marginBottom:10}}
      >
        <div>
          <div style={{fontSize:11, opacity:.7, marginBottom:4}}>Benutzer</div>
          <input value={logFilterUser} onChange={e=>setLogFilterUser(e.target.value)}
            placeholder="z.B. admin"
            style={{padding:'6px 9px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:12.5, width:130}} />
        </div>
        <div>
          <div style={{fontSize:11, opacity:.7, marginBottom:4}}>IP</div>
          <input value={logFilterIp} onChange={e=>setLogFilterIp(e.target.value)}
            placeholder="z.B. 62.40."
            style={{padding:'6px 9px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:12.5, width:130}} />
        </div>
        <div>
          <div style={{fontSize:11, opacity:.7, marginBottom:4}}>Pfad</div>
          <input value={logFilterPath} onChange={e=>setLogFilterPath(e.target.value)}
            placeholder="z.B. login"
            style={{padding:'6px 9px', borderRadius:8, background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.18)', color:'#fff', fontSize:12.5, width:130}} />
        </div>
        <button type="submit" style={{padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12.5, fontWeight:700,
          background:'rgba(56,189,248,.2)', color:'#7dd3fc', border:'1px solid rgba(56,189,248,.4)'}}
        >Filtern</button>
        <button type="button"
          onClick={()=>{ setLogFilterUser(''); setLogFilterIp(''); setLogFilterPath(''); fetchAdminLogsFiltered({ username:'', ip:'', path:'' }); }}
          style={{padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12.5,
            background:'transparent', color:'rgba(255,255,255,.6)', border:'1px solid rgba(255,255,255,.18)'}}
        >Zurücksetzen</button>
        <div style={{fontSize:11.5, opacity:.6, alignSelf:'center'}}>{adminLogs.length} Einträge</div>
      </form>

      {/* Split-Layout: links Tabelle, rechts Detailpanel */}
      <div style={{
        display:'grid', gridTemplateColumns: selectedLog ? '1fr 420px' : '1fr',
        gap:12, alignItems:'start', overflow:'hidden'
      }}>
        {/* Tabelle */}
        <div style={{overflow:'auto', maxHeight:'66vh', border:'1px solid #ffffff1f', borderRadius:10}}>
          {adminLogs.length === 0 ? (
            <div style={{opacity:.7, padding:12}}>Keine Einträge.</div>
          ) : (
            <table style={{
              width:'100%', borderCollapse:'collapse', tableLayout:'fixed',
              fontSize:13, fontVariantNumeric:'tabular-nums'
            }}>
              <thead>
                <tr style={{background:'rgba(255,255,255,.03)'}}>
                  {['ts','user','ip','path','ua (gekürzt)'].map((h)=>(
                    <th key={h} style={{textAlign:'left', borderBottom:'1px solid #fff2',
                      padding:'10px 12px', whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {adminLogs.map((r:any)=>{
                  const ipLooksInternal = !!parseMeta(r.meta)?.ip_server_was_internal;
                  return (
                  <tr
                    key={r.id}
                    onClick={()=>setSelectedLog(r)}
                    title="Details anzeigen"
                    style={{
                      cursor:'pointer',
                      background: selectedLog?.id === r.id ? 'rgba(56,189,248,.10)' : 'transparent'
                    }}
                  >
                    <td style={{borderBottom:'1px solid #ffffff1a', padding:'9px 12px', whiteSpace:'nowrap'}}>{r.ts}</td>
                    <td style={{borderBottom:'1px solid #ffffff1a', padding:'9px 12px', whiteSpace:'nowrap'}}>{r.username || '—'}</td>
                    <td style={{borderBottom:'1px solid #ffffff1a', padding:'9px 12px', whiteSpace:'nowrap'}}>
                      {r.ip}
                      {ipLooksInternal && (
                        <span title="Serverseitig konnte keine öffentliche IP ermittelt werden; ggf. auf vom Client gemeldete IP zurückgefallen."
                              style={{marginLeft:6, fontSize:10, padding:'1px 5px', borderRadius:4, background:'rgba(251,191,36,.15)', color:'#fbbf24', border:'1px solid rgba(251,191,36,.35)'}}>
                          ⚠ unsicher
                        </span>
                      )}
                    </td>
                    <td style={{borderBottom:'1px solid #ffffff1a', padding:'9px 12px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{r.path || ''}</td>
                    <td style={{borderBottom:'1px solid #ffffff1a', padding:'9px 12px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                      {short(r.ua, 120)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail-Panel */}
        {selectedLog && (
          <div style={{
            position:'relative',
            border:'1px solid rgba(255,255,255,.12)', borderRadius:10,
            background:'rgba(255,255,255,.03)', padding:12, maxHeight:'66vh', overflow:'auto'
          }}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6}}>
              <div style={{fontWeight:800}}>Details</div>
              <button
                onClick={()=>setSelectedLog(null)}
                style={{padding:'6px 10px', borderRadius:8, cursor:'pointer',
                        background:'transparent', color:'#cfe', border:'1px solid rgba(255,255,255,.18)'}}
              >Schließen</button>
            </div>

            {/* Kern-Felder */}
            <div style={{fontSize:12.5, lineHeight:1.6}}>
              <div><b>ts:</b> {selectedLog.ts}</div>
              {selectedLog.username && <div><b>user:</b> {selectedLog.username}</div>}
              <div style={{display:'flex', gap:6, alignItems:'center'}}>
                <div><b>ip:</b> {selectedLog.ip}</div>
                <button onClick={()=>copy(String(selectedLog.ip))}
                        style={{border:'1px solid #fff2', background:'transparent', color:'#cfe', borderRadius:6, padding:'2px 6px', fontSize:11}}>copy</button>
                {parseMeta(selectedLog.meta)?.ip_server_was_internal && (
                  <span style={{fontSize:10, padding:'1px 5px', borderRadius:4, background:'rgba(251,191,36,.15)', color:'#fbbf24', border:'1px solid rgba(251,191,36,.35)'}}>
                    serverseitig nur intern ermittelbar
                  </span>
                )}
              </div>
              {selectedLog.referer && <div><b>referer:</b> {selectedLog.referer}</div>}
              <div><b>path:</b> {selectedLog.path || '/'}</div>
              <div style={{marginTop:6}}>
                <div style={{display:'flex', gap:6, alignItems:'center'}}>
                  <b>user-agent</b>
                  <button onClick={()=>copy(String(selectedLog.ua))}
                          style={{border:'1px solid #fff2', background:'transparent', color:'#cfe', borderRadius:6, padding:'2px 6px', fontSize:11}}>copy</button>
                </div>
                <pre style={{whiteSpace:'pre-wrap', fontSize:12, margin:'4px 0 0 0'}}>{selectedLog.ua}</pre>
              </div>

              {/* Meta (inkl. clientIp) */}
              <div style={{marginTop:10}}>
                <div style={{display:'flex', gap:6, alignItems:'center'}}>
                  <b>meta</b>
                  <button
                    onClick={()=>copy(typeof selectedLog.meta === 'string' ? selectedLog.meta : JSON.stringify(selectedLog.meta ?? {}, null, 2))}
                    style={{border:'1px solid #fff2', background:'transparent', color:'#cfe', borderRadius:6, padding:'2px 6px', fontSize:11}}
                  >copy JSON</button>
                </div>
                {(() => {
                  const m = parseMeta(selectedLog.meta);
                  const clientIp  = m?.clientIp;

                  return (
                    <div style={{marginTop:6}}>
                      {clientIp && (
                        <div style={{display:'flex', gap:6, alignItems:'center', marginBottom:6}}>
                          <div><b>clientIp (Browser):</b> {clientIp}</div>
                          <button onClick={()=>copy(clientIp)}
                                  style={{border:'1px solid #fff2', background:'transparent', color:'#cfe', borderRadius:6, padding:'2px 6px', fontSize:11}}>copy</button>
                        </div>
                      )}
                      <pre style={{
                        background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.08)',
                        borderRadius:8, padding:8, fontSize:12, whiteSpace:'pre-wrap'
                      }}>{m ? JSON.stringify(m, null, 2) : '—'}</pre>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  </div>
)}


   </div>
   </div>
   );      // end return

   }       // end component

// Zählt einen numerischen Wert weich zum neuen Zielwert hoch/runter, statt hart zu springen.
function AnimatedNumber({ value, decimals = 2, durationMs = 500 }: { value: number; decimals?: number; durationMs?: number }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  useEffect(() => {
    const from = fromRef.current;
    const to = value;
    if (from === to) return;
    let rafId: number;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (to - from) * eased);
      if (t < 1) {
        rafId = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [value, durationMs]);
  return <>{display.toFixed(decimals)}</>;
}
